import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import { BusinessProcess } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { AutoBidNotOrderedMarket } from '@ebalance/proto/bffuser/v1/auto_bid_notice_model_pb'
import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import {
  type BiddingApprovalMode,
  BiddingApprovalState,
  BiddingLifecycleAction,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type {
  BiddingSpotApprovalRequest,
  BiddingSpotBid,
  BiddingSpotBidInput,
} from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { getBiddingSpotScreen } from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import {
  BiddingSpotScreenSourceStatus,
  CreateBiddingSpotApprovalRequestRequestSchema,
  GetBiddingSpotScreenRequestSchema,
  UpdateBiddingSpotApprovalRequestRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import { ProcurementNeedMarket } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import { DateSchema } from '@ebalance/proto/google/type/date_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Alert,
  Box,
  Flex,
  HStack,
  PrimaryButton,
  TabsContent as RawTabsContent,
  TabsList as RawTabsList,
  TabsTrigger as RawTabsTrigger,
  SecondaryButton,
  TabsRoot,
  TertiaryButton,
  Tooltip,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { AutoBidNotOrderedBanner } from '~/components/bidding/common/AutoBidNotOrderedBanner'
import { BidCsvImportDoneAlert } from '~/components/bidding/common/BidCsvImportDoneAlert'
import {
  useLoadedIssuerOptions,
  useRegisteredIssuerSelection,
} from '~/components/bidding/common/IssuerSelector'
import { RejectDialog } from '~/components/bidding/common/RejectDialog'
import { SubmitConfirmModal } from '~/components/bidding/common/SubmitConfirmModal'
import { TradableAreasBannerBody } from '~/components/bidding/common/TradableAreasBanner'
import { UnimportedBidBanner } from '~/components/bidding/common/UnimportedBidBanner'
import { UnknownIssuerBidBanner } from '~/components/bidding/common/UnknownIssuerBidBanner'
import {
  type ApprovalModeGroup,
  groupAreasByApprovalMode,
} from '~/components/bidding/common/approvalModeGroups'
import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import { saveLastBidAreas } from '~/components/bidding/common/lastBidArea'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactAlert } from '~/components/common/CompactAlert'
import {
  JepxMemberBidWarning,
  useJepxMemberBidWarning,
} from '~/components/common/JepxMemberBidWarning'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { useLayoutAutoBidNotOrderedNotices } from '~/components/layouts/MainLayout/SideMenuDotsContext'
import { iconSize } from '~/constants/iconSize'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import {
  invalidateBlockApprovalRequestQueries,
  invalidateSpotApprovalRequestQueries,
} from '~/hooks/bidding/queryKeys'
import { useBidImportStatus } from '~/hooks/bidding/useBidImportStatus'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useListBiddingBlockApprovalRequests } from '~/hooks/bidding/useListBiddingBlockApprovalRequests'
import {
  convertListSpotResponse,
  useListBiddingSpotApprovalRequests,
} from '~/hooks/bidding/useListBiddingSpotApprovalRequests'
import { useListTradableAreas } from '~/hooks/bidding/useListTradableAreas'
import { useProcurementNeedChartData } from '~/hooks/bidding/useProcurementNeedChartData'
import { useTransitionBiddingBlockApprovalState } from '~/hooks/bidding/useTransitionBiddingBlockApprovalState'
import { useTransitionBiddingSpotApprovalState } from '~/hooks/bidding/useTransitionBiddingSpotApprovalState'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useLocationStateNotice } from '~/hooks/useLocationStateNotice'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { useGetWorkflowCase } from '~/hooks/workflowCase/useGetWorkflowCase'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { JEPX_AREA_CODES } from '~/models/area'
import {
  type BidCsvImportNotice,
  earliestBidCsvImportDate,
  readBidCsvImportAppliedNotice,
} from '~/models/bidCsvImportReturn'
import { bidIssuerTypeOf } from '~/models/bidIssuerType'
import { now as serverNow } from '~/models/clock'
import { formatJstMonthDayHm } from '~/models/dateTime'
import { readSettlementDetailLandingDate } from '~/models/settlementDetailLanding'
import { useGetApprovalFlowScreen } from '~/pages/AdminApprovalFlow/hooks/useGetApprovalFlowScreen'
import { ApprovalStatusBanner } from '~/pages/SpotBidding/components/ApprovalStatusBanner'
import { AreaFilterBar } from '~/pages/SpotBidding/components/AreaFilterBar'
import { BlockApprovalStatusBanner } from '~/pages/SpotBidding/components/BlockApprovalStatusBanner'
import { BlockBidCreateModal } from '~/pages/SpotBidding/components/BlockBidCreateModal'
import { BlockBulkApproval } from '~/pages/SpotBidding/components/BlockBulkApproval'
import { BlockDeleteConfirmModal } from '~/pages/SpotBidding/components/BlockDeleteConfirmModal'
import { BulkCreateModal } from '~/pages/SpotBidding/components/BulkCreateModal'
import { BulkWithdrawConfirmModal } from '~/pages/SpotBidding/components/BulkWithdrawConfirmModal'
import { BulkWithdrawResultBanner } from '~/pages/SpotBidding/components/BulkWithdrawResultBanner'
import { BusinessAreaEmptyState } from '~/pages/SpotBidding/components/BusinessAreaEmptyState'
import { ClearAllBidsConfirmModal } from '~/pages/SpotBidding/components/ClearAllBidsConfirmModal'
import {
  CrossDatePendingApprovalBody,
  crossDatePendingKeys,
} from '~/pages/SpotBidding/components/CrossDatePendingApprovalBanner'
import { DeleteBidConfirmModal } from '~/pages/SpotBidding/components/DeleteBidConfirmModal'
import { DraftSubmitSplitNote } from '~/pages/SpotBidding/components/DraftSubmitSplitNote'
import {
  IndividualBidModal,
  type IndividualBidModalSource,
  type IndividualBidSubmission,
} from '~/pages/SpotBidding/components/IndividualBidModal'
import { PageIssuerSelector } from '~/pages/SpotBidding/components/PageIssuerSelector'
import { SpotBulkApproval } from '~/pages/SpotBidding/components/SpotBulkApproval'
import { SpotPageHeader } from '~/pages/SpotBidding/components/SpotPageHeader'
import { SpotPositionChartPanel } from '~/pages/SpotBidding/components/SpotPositionChartPanel'
import { SpotStatusHub } from '~/pages/SpotBidding/components/SpotStatusHub'
import { WithdrawBidConfirmModal } from '~/pages/SpotBidding/components/WithdrawBidConfirmModal'
import { BlockBidTab } from '~/pages/SpotBidding/components/tabs/BlockBidTab'
import { ExecutionResultTab } from '~/pages/SpotBidding/components/tabs/ExecutionResultTab'
import { NormalBidTab } from '~/pages/SpotBidding/components/tabs/NormalBidTab'
import { OperationHistoryTab } from '~/pages/SpotBidding/components/tabs/OperationHistoryTab'
import {
  DEFAULT_SPOT_TAB,
  SETTLEMENT_DETAIL_LANDING_TAB,
  SPOT_TABS,
} from '~/pages/SpotBidding/constants/tabs'
import type { SpotTabKey } from '~/pages/SpotBidding/constants/tabs'
import { useBlockApprovalDecision } from '~/pages/SpotBidding/hooks/useBlockApprovalDecision'
import { useBulkWithdrawControl } from '~/pages/SpotBidding/hooks/useBulkWithdrawControl'
import { useCreateBiddingBlockApprovalRequest } from '~/pages/SpotBidding/hooks/useCreateBiddingBlockApprovalRequest'
import { useCreateBiddingSpotApprovalRequest } from '~/pages/SpotBidding/hooks/useCreateBiddingSpotApprovalRequest'
import { useDeleteBiddingBlockApprovalRequest } from '~/pages/SpotBidding/hooks/useDeleteBiddingBlockApprovalRequest'
import { useDeleteBiddingSpotApprovalRequest } from '~/pages/SpotBidding/hooks/useDeleteBiddingSpotApprovalRequest'
import { useGetBiddingSpotScreen } from '~/pages/SpotBidding/hooks/useGetBiddingSpotScreen'
import { useSendResultNotice } from '~/pages/SpotBidding/hooks/useSendResultNotice'
import { useSpotApprovalDecision } from '~/pages/SpotBidding/hooks/useSpotApprovalDecision'
import { useSpotDeadlineCountdown } from '~/pages/SpotBidding/hooks/useSpotDeadlineCountdown'
import { useSpotWithdrawResultNotices } from '~/pages/SpotBidding/hooks/useSpotWithdrawResultNotices'
import {
  useBlockSubmissionDuplicateCheck,
  useSpotSubmissionDuplicateCheck,
} from '~/pages/SpotBidding/hooks/useSubmissionDuplicateCheck'
import { useTransitionBiddingSpotDrafts } from '~/pages/SpotBidding/hooks/useTransitionBiddingSpotDrafts'
import { useUpdateBiddingBlockApprovalRequest } from '~/pages/SpotBidding/hooks/useUpdateBiddingBlockApprovalRequest'
import { useUpdateBiddingSpotApprovalRequest } from '~/pages/SpotBidding/hooks/useUpdateBiddingSpotApprovalRequest'
import { useWithdrawBiddingBlockBidGroup } from '~/pages/SpotBidding/hooks/useWithdrawBiddingBlockBidGroup'
import { useWithdrawBiddingSpotBid } from '~/pages/SpotBidding/hooks/useWithdrawBiddingSpotBid'
import {
  blockDetailStatesOf,
  findStaleApprovalDetailIds,
  spotDetailStatesOf,
  withListSendStatus,
} from '~/pages/SpotBidding/models/approvalDetailSync'
import {
  ApprovalFlowUnresolvedError,
  isDirectSubmitMode,
  resolveAreaApprovalMode,
  resolveCreateApprovalMode,
} from '~/pages/SpotBidding/models/approvalModeResolve'
import {
  type ApprovedActionVerb,
  type ApprovedSendKind,
  resolveApprovedActionVerb,
  resolveApprovedSendKind,
  selectApprovedRequests,
} from '~/pages/SpotBidding/models/approvedSendKind'
import {
  type BlockBidTabListFilters,
  type NormalBidTabListFilters,
  SPOT_BID_LIST_FILTERS_STORAGE_KEY,
  type SpotBidListFilters,
  spotBidListFiltersCodec,
  widenBidListFiltersForCsvImport,
} from '~/pages/SpotBidding/models/bidListFilters'
import {
  type BlockBidRow,
  blockRowBidIssuerType,
} from '~/pages/SpotBidding/models/blockBidRow'
import {
  formatBlockBidGroupDeleteSummary,
  formatBlockBidGroupWithdrawSummary,
  translateBlockWithdrawError,
} from '~/pages/SpotBidding/models/blockBidWithdrawAction'
import {
  type BlockBidSubmission,
  BlockDetailUnresolvedError,
  formToCreateBlockInput,
  formToUpdateBlockInput,
  translateBlockWriteError,
} from '~/pages/SpotBidding/models/blockBidWrite'
import {
  isNonExpiredDraftBlockGroup,
  selectNonExpiredDraftBlockGroups,
} from '~/pages/SpotBidding/models/blockDraftGroup'
import { countBlockGroupLegs } from '~/pages/SpotBidding/models/blockGroupLegs'
import type {
  BulkAreaOption,
  BulkCreateParams,
  BulkGeneratedBid,
  ExistingBidVolume,
} from '~/pages/SpotBidding/models/bulkCreate'
import { selectBulkAddExistingVolumes } from '~/pages/SpotBidding/models/bulkCreate'
import {
  countClearedBids,
  planClearAll,
  resolveBlockClearAllGroups,
} from '~/pages/SpotBidding/models/clearAllPlan'
import { convertBlockBidRows } from '~/pages/SpotBidding/models/convertBlockBidsFromApprovalRequests'
import { convertSpotBidsFromApprovalRequest } from '~/pages/SpotBidding/models/convertSpotBidsFromApprovalRequest'
import {
  type DraftSubmitKind,
  type DraftSubmitPlan,
  draftSubmitKind,
  draftSubmitSendTiming,
  draftSubmitSplit,
  draftSubmitTargets,
  isDraftSubmitPlanCurrent,
  planDraftSubmit,
  resolveUnchangedDrafts,
} from '~/pages/SpotBidding/models/draftSubmitPlan'
import { computeFailedDataLabels } from '~/pages/SpotBidding/models/failedDataLabels'
import { buildIssuerPositionSlots } from '~/pages/SpotBidding/models/issuerPosition'
import {
  calcSpotPhase,
  formatSpotDeadlineDisplay,
  isSpotBeforeDeadline,
  nearestOpenSpotTargetDate,
  spotAcceptanceOpenAt,
  spotDeadlineAt,
} from '~/pages/SpotBidding/models/marketPhase'
import {
  areaEmptyStateInScope,
  areaFilterAvailability,
  isAlwaysListedDraft,
  missingMastersInScope,
} from '~/pages/SpotBidding/models/pageIssuerScope'
import {
  blockSendResultSubjects,
  spotSendResultSubjects,
} from '~/pages/SpotBidding/models/sendResultNotice'
import type { SharedBidListFilter } from '~/pages/SpotBidding/models/sharedBidListFilter'
import {
  loadSpotAreaFilter,
  loadSpotSidebarOpen,
  saveSpotAreaFilter,
  saveSpotSidebarOpen,
} from '~/pages/SpotBidding/models/sidebarStorage'
import {
  type SpotViolationAudience,
  buildBidLimitsUnavailableSection,
  buildBlockVolumesUnavailableSections,
  buildSpotAreaBreakdown,
  buildSpotInversionSection,
  buildSpotViolationSections,
  selectRequestBids,
  selectRevalidationBids,
  selectRevalidationBlockVolumes,
  summarizeSpotBidRows,
} from '~/pages/SpotBidding/models/spotApprovalDecision'
import {
  type SpotBidRow,
  isNonExpiredDraftSpotBid,
} from '~/pages/SpotBidding/models/spotBidRow'
import { isInSpotInversionPool } from '~/pages/SpotBidding/models/spotBidValidation'
import {
  formatSpotBidDeleteSummary,
  formatSpotBidWithdrawSummary,
} from '~/pages/SpotBidding/models/spotBidWithdrawAction'
import {
  BULK_ORIGIN_BY_VOLUME_MODE,
  type SpotBidCreateSubmission,
  SpotDetailUnresolvedError,
  SpotFocusedRequestUnavailableError,
  bulkGeneratedToBidInput,
  formToBidInput,
  spotBidToInput,
  spotCreateFormToBidInput,
  translateSpotDraftSubmitError,
  translateSpotWriteError,
} from '~/pages/SpotBidding/models/spotBidWrite'
import { canCreateSpotBid } from '~/pages/SpotBidding/models/spotMenuActionState'
import {
  spotUnknownIssuerBidCount,
  spotUnknownIssuerDeliveryContractCodes,
} from '~/pages/SpotBidding/models/unknownIssuerBid'

import { NeedsReviewBanner } from './components/NeedsReviewBanner'

/**
 * chakra v3 の `TabsList*Props` は本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の
 * d.ts 解決問題) で `HTMLChakraProps` が collapse し `children` / style prop / `value` が
 * 欠落する。 v3 本来の prop シグネチャ (`HTMLChakraProps<element>`) へ cast して復元する。
 * ランタイムは ecloud のまま。
 */
const TabsList = RawTabsList as React.FC<HTMLChakraProps<'div'>>
const TabsTrigger = RawTabsTrigger as React.FC<
  HTMLChakraProps<'button'> & { value: SpotTabKey }
>
const TabsContent = RawTabsContent as React.FC<
  HTMLChakraProps<'div'> & { value: string }
>

/** 今日 0:00 (= 当日プリセット)。 */
const todayMidnight = (): Date => {
  const now = serverNow()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/** 明日 0:00 (= 翌日プリセット / 初期値)。 */
const tomorrowMidnight = (): Date => {
  const now = serverNow()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
}

/**
 * 入札一覧を 1 つの申請セットに絞り込む契機。 一覧見出しに出す注記文言が契機で変わる:
 *   - reopen: 差戻し後の再編集 (AC-056、 2026-07-28 ユーザー判断)
 *   - approvalReview: 承認前に承認者が対象の札を確認する (AC-BSP-094、
 *     2026-08-26 Des 判断「承認者が札一覧を確認できることはマスト要件」)
 */
type FocusedRequestOrigin = 'reopen' | 'approvalReview'

/**
 * 契機 → 一覧見出しの注記 (i18n key)。 `Record` で全契機の網羅を tsc に強制し、 決定を
 * ここ 1 か所に置く (= NormalBidTab 側に既定値の silent fallback を持たせない。 3 つ目の
 * 契機が増えた時に黙って「差戻し分のみ表示中」 と出ることを防ぐ)。
 */
const FOCUSED_REQUEST_NOTE_KEY: Record<FocusedRequestOrigin, string> = {
  reopen: 'page:biddingSpot.list.reopenedFilterNote',
  approvalReview: 'page:biddingSpot.list.approvalReviewFilterNote',
}

type ModalKind =
  | 'none'
  | 'individual-create' // 個別作成 (= IndividualBidModal で 1 札入力 → 当日 request に Create/Update)
  | 'bulk-create'
  | 'clear-all' // 入札全削除 確認 (DRAFT 一括削除、 機能詳細7)
  | 'request-confirm' // 承認申請確認 modal (= 表示中の下書き → 承認申請。 承認モードが混ざる時を含む)
  | 'direct-submit-confirm' // 直接提出確認 modal (= 表示中の下書き → SUBMITTED、 承認なしモード、 AC-BSP-015)
  | 'block-create' // ブロック作成 modal
  | 'block-clear-all' // ブロック全削除 確認 (DRAFT group 一括削除、 issue #2091)
  | 'block-submit-confirm' // ブロック承認申請確認 modal (表示中の下書きの group → REQUEST)
  | 'block-direct-submit-confirm' // ブロック直接提出確認 modal (表示中の下書きの group → SUBMIT、 承認なしモード、 AC-BSP-015)

const NO_SUBMIT_BIDS: SpotBidRow[] = []

const DRAFT_VIOLATION_AUDIENCE = {
  request: 'request',
  direct: 'submit',
  mixed: 'mixed',
} as const satisfies Record<DraftSubmitKind, SpotViolationAudience>

/** ブロック入札の承認申請・提出で、 確認 modal を開いた時点に確定した group (AC-BSP-007)。 */
type BlockSubmitTarget = {
  id: string
  updatedAt: Timestamp | undefined
  rows: readonly BlockBidRow[]
}

const NO_BLOCK_SUBMIT_TARGETS: readonly BlockSubmitTarget[] = []

/** この画面が受け取る入札 CSV の種別 (AC-SPOT-024)。 時間前の結果は受け取らない */
const readSpotCsvImportNotice = (
  state: unknown,
): BidCsvImportNotice | undefined =>
  readBidCsvImportAppliedNotice(state, ['spotNormal', 'spotBlock'])

/** 清算画面の「詳細」から開いた時の着地の日 (AC-JST-009)。 時間前向けの state は受け取らない */
const readSpotSettlementLandingDate = (state: unknown): Date | undefined =>
  readSettlementDetailLandingDate(state, 'spot')

export const SpotBiddingPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const memberBidDisabledReason = useJepxMemberBidWarning()
  const queryClient = useQueryClient()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  // 自動入札が発注しなかったコマの記録 (AC-BSP-120)。 layout が 1 か所で取得して配る。
  const { notices: autoBidNotices } = useLayoutAutoBidNotOrderedNotices()

  // workflow-back-bar-readonly AC-04/AC-15: caseId は URL クエリパラメータ
  // `?workflowId=` から取得し、read-only 制御 (完了済み
  // ケースで承認申請・入札削除・ブロック入札作成/削除・全クリアを無効化) に使う。
  // fail-closed の理由 (取得失敗でも画面は描画し続ける / ケース状態が確定しない間は
  // 読み取り専用に倒す) は useCurrentWorkflowCase の JSDoc 参照。
  const {
    caseId,
    isReadonly,
    isDateLocked,
    lockedTargetDate,
    isTargetDateReady,
  } = useCurrentWorkflowCase()
  const { readonlyReason } = useWorkflowLockReasons()
  // 読み取り専用で非活性にした主操作ボタンの押せない理由 (AC-WFW-014)。
  // 取得中・失敗中は readonlyReason が undefined で、 ツールチップを出さない。
  // isWfCaseError はエラー文言表示専用 (useCurrentWorkflowCase は isError を公開
  // しない)。同一 cache key を共有するため fetch 自体は重複しない。
  const { isError: isWfCaseError } = useGetWorkflowCase(caseId, {
    throwOnError: false,
  })

  // 入札 CSV を取り込んで移ってきた時の完了の案内 (AC-SPOT-024)。 取り込んだ札を確かめられるよう、
  // 種別のタブ・取り込んだ受渡日のうち最も早い日で開く (下の初期値)。 エリアは下の effect で足す。
  const [csvImportNotice, dismissCsvImportNotice] = useLocationStateNotice(
    readSpotCsvImportNotice,
  )
  // 清算画面の「詳細」から移ってきた時は、 清算書の受渡日・約定結果を見るタブで開く
  // (AC-BSP-043 の例外)。
  const [settlementLandingDate] = useLocationStateNotice(
    readSpotSettlementLandingDate,
  )
  const [tab, setTab] = useState<SpotTabKey>(() =>
    settlementLandingDate !== undefined
      ? SETTLEMENT_DETAIL_LANDING_TAB
      : csvImportNotice === undefined
        ? DEFAULT_SPOT_TAB
        : csvImportNotice.kind === 'spotBlock'
          ? 'blockBid'
          : 'normalBid',
  )
  // 約定結果タブ・オペレーション履歴タブは共通ツールバーのエリアで絞らず、 入札後ポジション
  // チャート (選択中のエリアで描く札作成側の部品) も出さない (AC-BSP-131 / AC-BSP-037 / AC-BSP-038。
  // design #10970)。
  const isResultTab = tab === 'executionResult' || tab === 'operationHistory'
  // 受渡日のユーザー操作値 (= datepicker / 当日・翌日プリセットが set する)。
  // 初期は翌日 (= JEPX スポットは前日に翌日受渡分を入札するのが既定動線)。
  // caseId 付きで開いている間はケース対象日が確定するまでこの値をそのまま表示
  // (fail-closed。 AC-WFW-006a Decision 1) するが、 日付操作 UI は disabled のため
  // 実質操作不可。 確定後は `lockedTargetDate` を優先し、 本 state を bypass する
  // (Decision 3: 永続 state を汚さない)。
  const [manualTargetDate, setManualTargetDate] = useState<Date>(() => {
    // 取り込んだ受渡日のうち最も早い日 (次に入札締切を迎える日。 取り込んだ札の無い日に
    // 着地しない)。 ワークフローで日付が固定されている間は変えない。
    const earliest = isDateLocked
      ? undefined
      : csvImportNotice === undefined
        ? undefined
        : earliestBidCsvImportDate(csvImportNotice)
    const settlementLanding = isDateLocked ? undefined : settlementLandingDate
    return earliest ?? settlementLanding ?? tomorrowMidnight()
  })
  const targetDate = lockedTargetDate ?? manualTargetDate
  // エリアフィルタ (AC-BSP-019、 複数選択) の生の選択状態。 sentinel なし
  // (0 件選択もそのまま保持する)。 チャート (AC-BSP-016a) はこの生の集合が
  // ちょうど 1 件の時だけ描画する ([[singleSelectedArea]] 参照。 design
  // `getSpotChartArea` が `__spotSelectedAreasRaw` を見るのと同じ規約)。
  // 初期選択は design `spot.html` `#spot-bid-area-ms` の `selected: ['東京']`
  // 準拠 (issue #4102 PdM 判断 2026-08-03: P0 リリース時点でチャートが必ず単一
  // エリアで描画できる状態から始める)。
  // 前回の選択があればそれを復元する (AC-SC-031、 #10122)。
  const [rawSelectedAreas, setRawSelectedAreas] = useState<Set<AreaCode>>(
    () => loadSpotAreaFilter() ?? new Set([AreaCode.TOKYO]),
  )
  // ユーザーがこの表示中にエリアフィルタを操作したか。 未操作の初期値 (東京、 または
  // 復元した前回の選択) が取引不可だった時だけ取引可能な先頭エリアへ寄せる (下の
  // useEffect) ために使う。 操作後に空選択へなるのは design 規約どおりなので寄せない。
  const hasUserChangedAreasRef = useRef(false)
  const handleAreaFilterChange = useCallback((next: Set<AreaCode>): void => {
    hasUserChangedAreasRef.current = true
    setRawSelectedAreas(next)
    saveSpotAreaFilter(next)
  }, [])
  // 画面上部ツールバーで選択中の発注主体区分 (spec AC-BSP-005d、 design `spot.html`
  // `#spot-page-issuer-selector`)。 いまどの区分で札を作るかを常に画面に出す (#7075)。
  // 既定 (優先順の最上位) は下の `useRegisteredIssuerSelection` が選択肢の到着時に入れる。
  // 札を作るモーダル (個別・一括・ブロック) はこの区分で開き、 モーダル内で札ごとに
  // 変えられる (モーダル → ツールバーへは戻さない)。 入札一覧に出す札と、 エリアフィルタで
  // 選べるエリアもこの区分で決まる (AC-BSP-019。 [[filterAreaAvailabilityOf]])。
  const [pageIssuerType, setPageIssuerType] = useState<LicenseType>(
    LicenseType.UNSPECIFIED,
  )
  const { options: pageIssuerOptions, selected: pageIssuerSelected } =
    useRegisteredIssuerSelection({
      // AC-BSP-135 ※: 区分は登録の有無だけで選び、 エリアの可否は受渡日で求める。
      deliveryDate: targetDate,
      value: pageIssuerType,
      onChange: setPageIssuerType,
      selectsDefault: true,
    })
  /**
   * 入札一覧の左フィルターサイドバーを開いているか (#7449)。
   *
   * **通常入札 / ブロック入札で共有する。** プロトタイプ
   * (`design/bidding/spot.html`) も `body.spot-sidebar-on` 1 つを両タブで
   * 共有しており、片方で閉じてタブを切り替えたら開いて見える、を避ける。
   * 端末単位で localStorage に持つ (既定は開いている = 従来の挙動)。
   */
  const [isSidebarOpen, setIsSidebarOpen] =
    useState<boolean>(loadSpotSidebarOpen)
  const handleToggleSidebar = useCallback((): void => {
    setIsSidebarOpen((prev) => {
      const next = !prev
      saveSpotSidebarOpen(next)
      return next
    })
  }, [])
  // 入札一覧の絞り込み。 AC-SC-031 に従い保持し、 他の画面への移動・再読み込みのあとに復元する (#12745)。
  // 売/買・ステータス (AC-BSP-019) は通常入札 / ブロック入札で共有し、 タブを切り替えても引き継ぐ。
  // タブ固有の絞り込み (通常入札タブの売買逆転のみ・入札制限超過のみ、 ブロック入札タブの種別・提出状態)
  // も同じ値で持つが、 そのタブにだけ渡す (他のタブの一覧と「クリア」 には効かない)。
  const { filters: listFilters, updateFilters: updateListFilters } =
    usePersistedFilters<SpotBidListFilters>({
      storageKey: SPOT_BID_LIST_FILTERS_STORAGE_KEY,
      codec: spotBidListFiltersCodec,
    })
  const listFilter = useMemo<SharedBidListFilter>(
    () => ({ sides: listFilters.sides, statuses: listFilters.statuses }),
    [listFilters.sides, listFilters.statuses],
  )
  // タブからは変えた項目だけを受け取り、 直前の値に重ねる (「クリア」 は共有の値とタブ固有の値を続けて
  // 戻すので、 描画時点の値から組み立てると先に戻した項目を上書きする)。
  const setListFilter = (next: SharedBidListFilter): void => {
    updateListFilters((prev) => ({
      ...prev,
      sides: next.sides,
      statuses: next.statuses,
    }))
  }
  const patchNormalTabListFilters = (
    patch: Partial<NormalBidTabListFilters>,
  ): void => {
    updateListFilters((prev) => ({ ...prev, ...patch }))
  }
  const patchBlockTabListFilters = (
    patch: Partial<BlockBidTabListFilters>,
  ): void => {
    updateListFilters((prev) => ({ ...prev, ...patch }))
  }
  // 入札 CSV を取り込んで移ってきた時、 入札一覧の絞り込みを取り込んだ札を含むように広げ、
  // 広げた状態を保存する (AC-SPOT-024。 今の選択に足し、 選択を狭めない)。 広げるのは画面を開いた時の
  // 1 回だけで、 その後に絞り直した選択は広げ直さない (案内は閉じるまで残るので、 案内の有無では判定しない)。
  // 再読み込み・開き直しでは案内を受け取らない (useLocationStateNotice が履歴の state を消す)。
  const csvImportListFilterAppliedRef = useRef(false)
  useEffect(() => {
    if (
      csvImportNotice === undefined ||
      csvImportListFilterAppliedRef.current
    ) {
      return
    }
    csvImportListFilterAppliedRef.current = true
    // タブ固有の絞り込みを戻すのは仮採用 (spec の列挙に無い項目。 PdM 確認予定。 bidListFilters.ts 参照)。
    const widened = widenBidListFiltersForCsvImport(
      listFilters,
      csvImportNotice.sides,
    )
    if (widened !== undefined) updateListFilters(() => widened)
  }, [csvImportNotice, listFilters, updateListFilters])

  const [modalKind, setModalKind] = useState<ModalKind>('none')
  // 承認申請・提出の対象 (通常入札) = 主ボタンを押した時点で一覧に表示中の下書きの札 (AC-BSP-007)。
  // 実行時に取り直すと、 開いた後にフィルタを変えた時に確認 modal で見せた札と違う札を申請する。
  // `plan` は開いた時点で確定した申請セットごとの target と承認モード。 確認 modal の種類・件数・
  // 内訳も、 確定後に送る targets も、 実行時の照合 (AC-BSP-124) もここから導く。
  const [submitScope, setSubmitScope] = useState<
    | { plan: DraftSubmitPlan; bids: SpotBidRow[]; hiddenCount: number }
    | undefined
  >(undefined)
  const submitTargets = submitScope?.bids ?? NO_SUBMIT_BIDS
  const draftKind =
    submitScope === undefined ? 'request' : draftSubmitKind(submitScope.plan)
  const draftSendTiming = draftSubmitSendTiming(draftKind)
  // 混在 (承認申請と提出が混ざる) の時だけ内訳を持つ。
  const draftSplit =
    submitScope !== undefined && draftKind === 'mixed'
      ? draftSubmitSplit(submitScope.plan)
      : undefined
  // 承認申請・提出の対象 (ブロック入札) = 主ボタンを押した時点で一覧に表示中の下書きの group
  // (AC-BSP-007)。 件数・承認フローの行・実行時の照合 (AC-BSP-124) はこの group の集合 1 つから
  // 導く。 group は編集しても ID が変わらないので、 開いた後の他のユーザーの編集は
  // 開いた時点の更新日時で見分ける。 `rows` は group の全 leg。
  const [blockSubmitScope, setBlockSubmitScope] = useState<
    readonly BlockSubmitTarget[]
  >(NO_BLOCK_SUBMIT_TARGETS)
  // 主ボタンを押した時点で、 絞り込みで表示していない下書きの group の数 (確認 modal の件数の下に
  // 1 行で出す)。
  const [blockSubmitHiddenCount, setBlockSubmitHiddenCount] = useState(0)
  // 入札全削除の対象 = 全削除を押した時点で一覧に表示中の下書き (機能詳細 7)。 確認 modal の
  // 件数と削除が同じ札を指すよう、 開いた時点で確定して持つ。
  const [clearAllTargets, setClearAllTargets] = useState<readonly SpotBidRow[]>(
    [],
  )
  // 対象外になった下書きの数 (通常入札: 絞り込みで表示していない札 / ブロック入札: 絞り込みで表示して
  // いない group)。 確認 modal の件数の下に 1 行で出す。 ブロック入札は一覧が LINK / LOOP の組を
  // 分けずに表示するので、 片方の leg だけが対象外になる group は無い。
  const [clearAllExcludedCount, setClearAllExcludedCount] = useState(0)
  const [blockClearAllTargets, setBlockClearAllTargets] = useState<
    readonly BlockBidRow[]
  >([])
  const [blockClearAllHiddenCount, setBlockClearAllHiddenCount] = useState(0)
  const blockClearAllGroupIds = useMemo(
    () => new Set(blockClearAllTargets.map((r) => r.groupId)),
    [blockClearAllTargets],
  )
  // 確認 modal を開いた時点の、 対象 group の更新日時 (group ID → 更新日時)。 ブロックの group は
  // 編集しても ID と下書きの状態が変わらないので、 開いた後の他のユーザーの編集は更新日時で見分ける。
  // modal に出した内容の元の明細 (screen) が持つ版を取る。 polling する一覧の版を取ると、 明細より先に
  // 一覧だけが更新された時に、 modal に出していない内容の版をロックの基準にしてしまう。
  const [blockClearAllUpdatedAt, setBlockClearAllUpdatedAt] = useState<
    ReadonlyMap<string, Timestamp | undefined>
  >(new Map())

  // 市場フェーズ (= 入札締切ガードの源泉)。 現在時刻は 1 分ごとに更新する
  // (`useNowByMinute`)。 mount 時の `new Date()` で固定すると、 画面を開いたまま
  // 入札締切 (受渡日前日 10:00 JST) を跨いだときに phase が 'open' のまま残り、
  // 締切後も札を作成・編集できてしまう (issue #5349)。 design `spot.html` も
  // 締切バナーを 5 分間隔で再評価しており、 締切跨ぎの即時反映が仕様側の前提。
  const now = useNowByMinute()
  const hasUnimportedBids = useBidImportStatus(targetDate, false)

  const phase = useMemo(() => calcSpotPhase(now, targetDate), [now, targetDate])
  // 入札締切前か (= 作成・編集 UI を活性にしてよいか。 AC-BSP-064)。
  // 各タブの isBeforeDeadline と同じ判定。
  const isBeforeDeadline = isSpotBeforeDeadline(phase)
  // 入札受付開始前 (予約入札、 AC-BSP-092) に提出すると予約になり、 受付開始時に送信される。
  // 提出・承認申請の確認モーダルの「JEPX への送信」 と撤回の行をこの時刻で書き分ける
  // (AC-BSP-076。 書式は受付開始の表示と同じ M/D HH:MM)。
  const confirmReservedOpenAt =
    phase === 'reserved'
      ? formatJstMonthDayHm(spotAcceptanceOpenAt(targetDate))
      : undefined
  // 承認申請確認 modal の表示中だけ、 入札締切までの残り時間を秒 tick で追う
  // (AC-BSP-012)。 page 全体の `now` は分粒度なので、 モーダルを開いたまま次の分境界
  // まで秒表示が止まって見える (page-header の `SpotPhaseBadge` と同じ理由。 共有
  // hook `useSpotDeadlineCountdown` 参照)。
  const draftDeadlineRemainingSeconds = useSpotDeadlineCountdown(
    targetDate,
    modalKind === 'request-confirm',
  )

  // 承認フロー設定 (= BFF ApprovalFlowService.GetApprovalFlowScreen)。
  // 新規申請セット (spot / block) の作成時に、 対象エリアの承認モード (要承認 /
  // 承認なし / 自動) を解決するために読む (AC-BSP-015)。 `throwOnError: false` で
  // 本画面の主目的 (入札作成・提出) を error boundary に巻き込まない (= 他の read
  // query と同じ fail-safe 方針)。
  const {
    data: approvalFlowScreenData,
    isLoading: isApprovalFlowScreenLoading,
    isError: isApprovalFlowScreenError,
  } = useGetApprovalFlowScreen({ throwOnError: false })
  // 取得中・取得失敗の間は resolveCreateApprovalMode を呼べない (2026-09-16
  // ユーザー決定 1-B)。 以前は「安全側として要承認」 にフォールバックしていたが、
  // それは読めていない設定を推測で確定させることになり、 backend 照合 (別 PR、
  // 決定 1-A) が拒否しうる組合せを黙って作ってしまう。 取得できるまで入札作成
  // (個別 / 一括 / ブロック) と DRAFT の提出 (承認申請 / 直接提出) ボタンを
  // 非活性にし、 万一書込が走っても persistDraftBids / handleBlockCreate /
  // handleBlockUpdate 側の guard が ApprovalFlowUnresolvedError で中断する。
  const isApprovalFlowUnavailable =
    isApprovalFlowScreenLoading ||
    isApprovalFlowScreenError ||
    approvalFlowScreenData === undefined

  // 入札一覧を 1 つの申請セットの札だけに絞り込む state (= 複数の申請セットが並存すると
  // 「どの札の話なのか」 がバナーだけでは判別できないため、 対象札そのものを見せる)。
  // 契機 (FocusedRequestOrigin) で一覧見出しの注記文言が変わるので origin を併せて持つ。
  // id と注記が別 state だとズレうるので 1 つのオブジェクトで持つ。
  const [focusedRequest, setFocusedRequest] = useState<
    { id: string; origin: FocusedRequestOrigin } | undefined
  >(undefined)
  const focusedRequestId = focusedRequest?.id

  // 業務エリア設定 (個人設定) で許可されたエリア (AC-BSP-019)。 許可されていないエリアは、
  // エリアフィルタでも札を作るモーダル (個別作成・一括作成・ブロック作成) でも選択肢に残して
  // 選べなくする (AC-BSP-005b。 `conventions.md`「業務エリアフィルタの原則」 の既定〈除外〉に
  // 対する本画面の明示)。 登録可否軸は別軸で、 エリアフィルタでは未登録を選択肢に残して選択
  // 不可にし ([[filterAreaAvailabilityOf]])、 札を作るモーダルでは選択肢に出さない。
  //
  // 絞り込み自体は共有 hook `~/hooks/useBusinessAreaFilter` (issue #7535) に寄せる。
  // 同じ原則を page ごとに実装すると未取得時の扱いが画面間で乖離するため
  // (#7503。 `BalanceDemand` / `BalanceGeneration` と同じ配線)。 同 hook は個人設定を
  // `throwOnError: false` で購読するので、 表示フィルタ 1 本の取得失敗で入札画面ごと
  // 落とさず、 絞り込まない fallback (= 全 9 エリア) で描画を続ける。 その間は業務エリア外の
  // エリアも選べるので、 取得失敗は下の取得失敗バナーで知らせる (`isBusinessAreasError`)。
  const { selectableAreaCodes: areaCodes, isBusinessAreasError } =
    useBusinessAreaFilter(JEPX_AREA_CODES)

  // 取引可能エリア (= BFF TradableAreaService.ListTradableAreas)。 表示中の受渡日の
  // 広域マスタで求める (各モーダルの発注主体区分の選択肢と同じ断面。 #8842)。
  // 取得失敗 / 未取得 (data === undefined) のとき全エリア有効 / バナー非表示とする。
  const { data: tradableAreasData, isError: isTradableAreasError } =
    useListTradableAreas(targetDate)
  const tradableAreas = tradableAreasData?.tradableAreas ?? []
  // 組織全体の取引不可エリア。 札を作るモーダルのエリア選択肢はこれを使う (モーダルは
  // 自分の発注主体セレクタで区分が変わるため、 ツールバーの区分では絞らない)。
  const disabledAreas = useMemo(() => {
    if (tradableAreasData === undefined) return new Set<AreaCode>()
    const tradableSet = new Set(tradableAreas)
    return new Set(JEPX_AREA_CODES.filter((code) => !tradableSet.has(code)))
  }, [tradableAreas, tradableAreasData])
  // 閲覧用の登録集合は発注できる集合とは別。 会員情報が無くコードを導けない発電も
  // 登録済みなら一覧・チャートを表示する (AC-BSP-019 / AC-IJM-012・013)。
  // AC-BSP-105 の既存空状態判定も同じ登録集合を参照する。
  const registeredJepxAreas = useMemo(() => {
    if (tradableAreasData === undefined) return JEPX_AREA_CODES
    const registered = new Set(
      tradableAreasData.issuers.flatMap((issuer) =>
        issuer.registeredAreas.map((r) => r.area),
      ),
    )
    return JEPX_AREA_CODES.filter((code) => registered.has(code))
  }, [tradableAreasData])
  const filterOrgDisabledAreas = useMemo(
    () =>
      new Set(
        JEPX_AREA_CODES.filter((code) => !registeredJepxAreas.includes(code)),
      ),
    [registeredJepxAreas],
  )
  // 入札一覧に出す札とエリアフィルタで選べるエリアが従う発注主体区分 (AC-BSP-019 /
  // AC-BSP-005b)。 ツールバーの区分が選択肢に届くまでと、 申請セットに絞り込み中
  // (AC-BSP-094 / AC-056) は未選択のまま組織全体にする: 確認したい申請セットが選んだ区分と
  // 違う区分の札・エリアにあっても、 一覧とエリアフィルタで辿り着けるようにするため。
  // 絞り込みを解除すると区分に戻り、 選べなくなったエリアは下の補正 effect が選択から外す。
  const scopeIssuer =
    focusedRequestId === undefined ? pageIssuerSelected : undefined
  const issuerScopeType = scopeIssuer?.issuerType ?? LicenseType.UNSPECIFIED
  // 区分の登録は日付をまたいで既知でも、 当日のエリア可否は取得するまで不明。
  // query の fail-soft 契約に従いエリアだけを絞らず、 一覧の既知の区分スコープは保持する。
  const filterAreaAvailabilityOf = useCallback(
    (code: AreaCode) =>
      areaFilterAvailability(
        tradableAreasData === undefined ? undefined : scopeIssuer,
        filterOrgDisabledAreas,
        areaCodes,
        code,
      ),
    [tradableAreasData, scopeIssuer, filterOrgDisabledAreas, areaCodes],
  )
  const filterMissingMasterOf = useCallback(
    (area: AreaCode) => {
      if (scopeIssuer === undefined || isTradableAreasError) return undefined
      return tradableAreasData?.registrationIssues.find(
        (issue) =>
          issue.issuerType === scopeIssuer.issuerType && issue.area === area,
      )?.missingMaster
    },
    [tradableAreasData, scopeIssuer, isTradableAreasError],
  )
  const filterDisabledAreas = useMemo(
    () =>
      new Set(
        JEPX_AREA_CODES.filter(
          (code) => filterAreaAvailabilityOf(code) !== 'selectable',
        ),
      ),
    [filterAreaAvailabilityOf],
  )

  // エリアフィルタで選べるエリア (= 業務エリア ∩ ツールバーの発注主体区分で選べるエリア。
  // 申請セットに絞り込み中は区分によらず組織全体の登録済みエリア)。 一覧と、 下の CSV 取込時の
  // エリア拡張が使う。 発注用の候補・検証は各モーダルが独立して持つ。
  const selectableAreas = useMemo(
    () => areaCodes.filter((code) => !filterDisabledAreas.has(code)),
    [areaCodes, filterDisabledAreas],
  )

  // 入札 CSV を取り込んで移ってきた時、 エリアの絞り込みを取り込んだ札を含むように広げる
  // (AC-SPOT-024。 今の選択に足し、 選択を狭めない。 空選択 = 全エリアはそのまま)。 足すのは
  // 選べるエリア (業務エリア ∩ ツールバーの発注主体区分で選べるエリア = selectableAreas) に
  // 限るので、 取引可能エリアの応答を待ってから 1 回だけ行う。
  // 保存は利用者の変更と同じ経路 (handleAreaFilterChange)。
  const csvImportAreasAppliedRef = useRef(false)
  useEffect(() => {
    if (csvImportNotice === undefined || csvImportAreasAppliedRef.current) {
      return
    }
    if (tradableAreasData === undefined && !isTradableAreasError) return
    csvImportAreasAppliedRef.current = true
    if (rawSelectedAreas.size === 0) return
    const selectable = new Set(selectableAreas)
    const missing = csvImportNotice.areas.filter(
      (code) => !rawSelectedAreas.has(code) && selectable.has(code),
    )
    if (missing.length === 0) return
    handleAreaFilterChange(
      new Set(
        [...rawSelectedAreas, ...missing].filter(
          (code) => !filterDisabledAreas.has(code),
        ),
      ),
    )
  }, [
    csvImportNotice,
    filterDisabledAreas,
    handleAreaFilterChange,
    isTradableAreasError,
    rawSelectedAreas,
    selectableAreas,
    tradableAreasData,
  ])

  // AC-BSP-105 の空状態の判定の入力 (計算は [[areaEmptyStateInScope]])。 範囲はツールバーで
  // 選んだ発注主体区分 ([[scopeIssuer]])。 区分が決まらない間 (申請セットに絞り込み中・区分を
  // 選べない) は組織全体 (全区分の和集合)。 `tradableAreasData` 未確定の間は 0 件と誤確定しない
  // (Des 判断 #7502 は未確定を空集合とみなさないことを求めている)。
  const areaEmptyStateInputs = {
    areaCodes,
    jepxAreaCodes: JEPX_AREA_CODES,
    orgIssuers: tradableAreasData?.issuers,
    registeredIssuerCount: tradableAreasData?.registeredIssuerTypes.length,
  }

  const {
    orderableAreas: orderableJepxAreas,
    isBroadAreaMasterEmpty,
    showsAreaEmptyPanel: candidateShowsAreaEmptyPanel,
    hasNoAreaCandidate,
    hidesAreaContent,
  } = areaEmptyStateInScope({
    ...areaEmptyStateInputs,
    pageIssuer: scopeIssuer,
  })

  // 発注主体区分で絞らない組織全体の判定。 申請セットに絞り込むと判定はこれになる
  // ([[canFocusRequest]])。 区分を表示しない約定結果・オペレーション履歴タブの案内にも使う。
  const orgWideAreaEmptyState = areaEmptyStateInScope({
    ...areaEmptyStateInputs,
    pageIssuer: undefined,
  })

  // チェック中エリアが後から選べなくなったら (= TradableAreas の応答が遅れて届いた等で
  // filterDisabledAreas が広がる、 またはツールバーの発注主体区分を切り替えて新しい区分で
  // 選べなくなった。 AC-BSP-005b) / 業務エリア外へ変わったら選択から落とす。 ユーザー操作の
  // 後で 0 件になった場合は寄せない: 空選択は下流 ([[selectedAreas]]) で選べる全エリアに
  // 正規化される (design `spot.html` `window.__spotSelectedAreas` と同じ規約)。
  // 未操作の初期値 (東京、 または復元した前回の選択) が選べず / 業務エリア外で 0 件に
  // なった時だけ、 選べる先頭エリアへ寄せる (spec AC-BSP-019 注記「P0 リリース時点でチャートが必ず単一エリアで
  // 描画できる状態から始める」)。
  useEffect(() => {
    setRawSelectedAreas((prev) => {
      if (prev.size === 0) return prev
      const kept = new Set(
        [...prev].filter(
          (code) => areaCodes.includes(code) && !filterDisabledAreas.has(code),
        ),
      )
      if (kept.size === prev.size) return prev
      if (kept.size === 0 && !hasUserChangedAreasRef.current) {
        const firstSelectable = areaCodes.find(
          (code) => !filterDisabledAreas.has(code),
        )
        if (firstSelectable !== undefined) return new Set([firstSelectable])
      }
      return kept
    })
  }, [areaCodes, filterDisabledAreas])

  // 実効エリア (= 一覧 / KPI が消費する集合)。 空選択
  // (= 何もチェックしていない) は選択可能な全エリアに正規化する (design `spot.html`
  // `window.__spotSelectedAreas` と同じ規約。 選択可能な全エリアは業務エリア ∩
  // ツールバーの発注主体区分で選べるエリア = [[selectableAreas]])。 チャートは正規化前の [[rawSelectedAreas]]
  // を見るため、 ここでの正規化はチャートの単一/複数判定に影響しない。
  const selectedAreas = useMemo(
    () =>
      rawSelectedAreas.size === 0 ? new Set(selectableAreas) : rawSelectedAreas,
    [rawSelectedAreas, selectableAreas],
  )
  // チャート (AC-BSP-016a) はこのフィルタで単一エリアを選択している時だけ描画する。
  // 0 件選択を「取引可能な全エリアだから実質単一」 のように救済しない (design
  // `getSpotChartArea` も raw 選択数だけを見る)。
  const singleSelectedArea = useMemo(
    () => (rawSelectedAreas.size === 1 ? [...rawSelectedAreas][0] : undefined),
    [rawSelectedAreas],
  )

  // 受渡日の全承認案件を解決する (= 追加入札 AC-079 により同一受渡日に複数の
  // 承認案件〈申請セット〉が並存しうる、 AC-082)。 list で id 一覧を取得し、
  // id ごとに並列 Get して full aggregate (spot_bids 込み) を得る。
  // 取得の対象の受渡日 (送信結果の通知が受渡日ごとに観測を始めるための印)。
  const targetDateScope = `${targetDate.getFullYear()}-${targetDate.getMonth() + 1}-${targetDate.getDate()}`
  const targetDateFilter = useMemo(
    () =>
      create(DateSchema, {
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1,
        day: targetDate.getDate(),
      }),
    [targetDate],
  )
  const {
    data,
    dataUpdatedAt: spotListUpdatedAt,
    isFetching: isSpotListFetching,
    isLoading,
    isError: isSpotListError,
  } = useListBiddingSpotApprovalRequests(targetDateFilter, {
    enabled: isTargetDateReady,
  })
  const {
    data: screenData,
    dataUpdatedAt: screenUpdatedAt,
    isFetching: isScreenFetching,
    isError: isScreenError,
  } = useGetBiddingSpotScreen(targetDateFilter, { enabled: isTargetDateReady })
  const isSpotScreenSourceFailed =
    screenData !== undefined &&
    screenData.spotStatus !== BiddingSpotScreenSourceStatus.OK
  const isBlockScreenSourceFailed =
    screenData !== undefined &&
    screenData.blockStatus !== BiddingSpotScreenSourceStatus.OK

  /**
   * 全受渡日ぶんの承認申請 (#5538)。 受渡日フィルタを渡さないと全日付が返る
   * (`target_date_filter` は optional) ので、 新しい RPC は要らない。
   * 「他の受渡日に承認待ちがある」 バナー専用。
   *
   * ⚠ **取得失敗を握りつぶさないこと。** hook 側の fail-safe
   * (`throwOnError: false`) により画面は落ちないが、 このクエリだけが失敗すると
   * 結果が空配列になり、 **「他の受渡日に承認待ちは無い」 と読める画面**になる
   * (選択中の受渡日ぶんの一覧は成功しているので `isSpotListError` も false)。
   * 承認待ちの見落としは AC-BSP-095 が防ごうとしているものそのものなので、
   * 失敗は下の `failedDataLabels` に合流させて明示する。
   */
  const { data: allApprovalRequestsData, isError: isCrossDateApprovalsError } =
    useListBiddingSpotApprovalRequests()
  const allApprovalRequests = useMemo(
    () =>
      allApprovalRequestsData !== undefined
        ? convertListSpotResponse(allApprovalRequestsData)
        : [],
    [allApprovalRequestsData],
  )
  const spotApprovalRequests = useMemo(
    () => screenData?.spotApprovalRequests ?? [],
    [screenData],
  )
  // 一括取下 (AC-BSP-116) の対象から、 作ったワークフローを理由に外す札・ブロック入札のグループの
  // workflowId (AC-BSP-058)。 外すかの判定は backend が一括取下と同じ規則で持ち (通常入札は確定済、
  // ブロック入札は確定済か記録したワークフローのケースが見つからない)、 画面は workflowId がその集合に
  // 含まれるかを引くだけ。 規則が札の種類で違うので、 集合は混ぜない。
  const spotBulkWithdrawExcludedWorkflowIds = useMemo(
    () => new Set(screenData?.completedWorkflowIds ?? []),
    [screenData],
  )
  const blockBulkWithdrawExcludedWorkflowIds = useMemo(
    () => new Set(screenData?.blockBulkWithdrawExcludedWorkflowIds ?? []),
    [screenData],
  )
  // AC-BSP-096 (通常入札): 承認者の承認・差戻しを手動リロードなしで反映する。
  //
  // 承認状態の追跡は polling している一覧 (`useListBiddingSpotApprovalRequests`)
  // が担い、 **明細 (spot_bids) は定期再取得しない** (spec は編集中データの
  // 巻き込みを避けるため自動更新の対象を承認ステータスに限定している)。 ただし
  // 承認状態バナー (AC-BSP-067) が出す承認日時・差戻し理由・提出日時は一覧 item
  // (`BiddingSpotApprovalRequestListItem`) に無く明細にしか無いため、 一覧が
  // 承認状態または id 集合の変化を拾ったときに screen を 1 回だけ取り直す。
  // 承認待ちのまま一覧の更新日時が明細より新しいとき (途中の段の承認、
  // `models/approvalDetailSync.ts` の `isPendingUpdatedSince`) も取り直す。
  // = 「明細の定期再取得」 ではなく 「承認ステータス変化イベントに対する再取得」。
  //
  // id 配列ではなく join 済み文字列を effect の依存にして差分を安定化する。
  // 差分が残る間は list の polling ごとに screen を再取得する (承認依頼 id に
  // "," は現れない)。
  const staleSpotDetailIdsKey = useMemo(
    () =>
      findStaleApprovalDetailIds(
        data?.items ?? [],
        spotDetailStatesOf(spotApprovalRequests),
      ).join(','),
    [data, spotApprovalRequests],
  )
  // 承認状態バナーの見出しは JEPX への送信状態でも決まる (AC-BSP-067)。 送信状態の変化では
  // 明細を取り直さず、 polling している一覧が持つ送信状態をバナーに渡す明細へ重ねる。
  const spotBannerRequests = useMemo(
    () => withListSendStatus(spotApprovalRequests, data?.items ?? []),
    [data, spotApprovalRequests],
  )
  // screen が未解決、または list / screen の差分が残る間は、既存 DRAFT の
  // 有無を確定できない (= 未解決分に DRAFT が含まれるかもしれない)。 このまま新規
  // Create すると既存 DRAFT を無視した重複申請セットを起票してしまい、 受渡日
  // UNIQUE 撤廃後は DB でも弾かれないため、 persistDraftBids の Create 判定で参照して中断する。
  const hasUnresolvedSpotDetail =
    screenData === undefined || staleSpotDetailIdsKey !== ''
  // 一覧 (list) 自体が失敗した場合も同じく DRAFT の不在を確定できない。 このとき
  // `data?.items` は空になるため、 上の hasUnresolvedSpotDetail
  // は false のまま = 「DRAFT なし」と誤って確定してしまう。 一覧の失敗を明示的に
  // 未解決として合流させ、 Create 判定を fail-closed に保つ。
  const isSpotStateUnresolved =
    hasUnresolvedSpotDetail ||
    isSpotListError ||
    isScreenError ||
    isSpotScreenSourceFailed
  // 札一覧が「欠落したまま描画されている」状態か。 部分描画は継続する方針だが
  // (failedDataLabels バナーで通知)、 約定 KPI / エリア集計は全札の合計を確定値と
  // して出すため、 欠落した合計を正値として読ませない目的で約定結果タブに渡す。
  //
  // 失敗だけでなく **loading 中も** 欠落扱いにする (= `isSpotStateUnresolved` と同条件)。
  // screen 解決までの間は札が 0 件のまま描画されるため、 失敗のみを見ると
  // 「約定率 —・約定総量 0.0 MW」 を確定値として出してしまい、 解決後に数字が入れ替わる。
  const isSpotBidsIncomplete = isSpotStateUnresolved
  const bids = useMemo(
    () => convertSpotBidsFromApprovalRequest(spotApprovalRequests),
    [spotApprovalRequests],
  )
  // 個別作成・編集モーダルの売買逆転の警告が突き合わせる札。 一覧のバナーと同じ検知対象の
  // 状態の札だけ (AC-BSP-009)。
  const inversionPoolBids = useMemo(
    () => bids.filter((b) => isInSpotInversionPool(b, !isBeforeDeadline)),
    [bids, isBeforeDeadline],
  )
  const spotSendResultSubjectList = useMemo(
    () =>
      data === undefined
        ? undefined
        : spotSendResultSubjects(data.items, spotApprovalRequests),
    [data, spotApprovalRequests],
  )
  useSendResultNotice(
    spotSendResultSubjectList,
    spotListUpdatedAt,
    'perArea',
    targetDateScope,
  )

  // 絞り込み対象そのものが消えた / 役目を終えた時の解除。 setState の updater 経由で
  // 判定するのは、 非同期処理中に focusedRequest が別の値へ変わっていた場合に
  // 巻き込みで解除しないため。
  const clearFocusIfMatches = (id: string): void => {
    setFocusedRequest((prev) => (prev?.id === id ? undefined : prev))
  }

  // 参照を固定して子へ渡す (setState 系だが inline arrow にすると描画ごとに変わる)。
  const handleClearFocusedApprovalRequest = useCallback((): void => {
    setFocusedRequest(undefined)
  }, [])

  // 絞り込みは受渡日を跨いで持ち越さない (= 別日の申請セットとは id が一致せず、
  // 一覧が 0 件のまま「フィルタをリセット」でしか抜けられない袋小路になる)。
  // 受渡日変更で解除する。
  useEffect(() => {
    setFocusedRequest(undefined)
  }, [targetDate])

  // 承認状態別の request 解決 (= メインボタン出し分け・追加入札の書込先解決の中心)。
  //
  // issue #3511 Phase 2 / PdM 判断 (2026-08-18): 「複数 DRAFT 申請セットの並存はユーザーに
  // 意識させない。 受渡日単位で常に 1 つの下書きの塊として振る舞う」。 そのため一括系の操作
  // (一括作成・CSV 取込・下書き復元) は先頭 1 件ではなく draftRequests 全体を対象にする。
  // 承認申請・提出と入札全削除だけは、 draftRequests のうち一覧に表示中の札に限る
  // (AC-BSP-007 の handleSubmitDrafts / 機能詳細 7 の handleClearAllBids)。 単一 DRAFT を指す必要があるのは
  // 「新しい札の追加先」 (PdM 論点1: 最新の DRAFT) だけで、 それが draftRequest。
  //
  // AC-079: 追加した札は DRAFT request があればそこへ append (Update)、
  //         無ければ Create で新規申請セット (DRAFT) を起票する。
  // AC-082c: メインボタンの承認申請カウントは一覧に表示中の下書きの札数 (AC-BSP-007)。
  // AC-056: 既存 DRAFT と REJECTED が並存する受渡日で後者を REOPEN すると DRAFT が
  //         複数になる。 絞り込み中は表示と書込先を一致させないと、 画面に出ていない
  //         別 DRAFT へ札追加・CSV 取込・承認申請が走るため、 絞り込み対象に限定する
  //         (= 絞り込み中だけは「受渡日単位」 ではなくその 1 セットが下書きの塊)。
  const draftRequests = useMemo(() => {
    const drafts = spotApprovalRequests.filter(
      (r) => r.approvalState === BiddingApprovalState.DRAFT,
    )
    if (focusedRequestId === undefined) return drafts
    // 絞り込み中に対象 DRAFT が見つからないケースは 2 つあり、 どちらも「画面に
    // 出ている下書きは無い」 が正しい答えなので全 DRAFT へは戻さない (= 戻すと
    // 承認申請・全削除が画面に出ていない申請セットを対象にしてしまう):
    //   - 承認前の札確認 (AC-BSP-094) で PENDING に絞っている (= そもそも DRAFT でない)
    //   - 絞り込み対象の DRAFT が消えた (= 解除は clearFocusIfMatches が行うが、
    //     その反映までの 1 render を素通しさせない)
    return drafts.filter((r) => r.id === focusedRequestId)
  }, [spotApprovalRequests, focusedRequestId])
  // 札の追加先 = 最新の DRAFT (PdM 論点1)。 list は created_at DESC 順 (backend の
  // ListBiddingSpotApprovalRequests) なので先頭が最新。
  const draftRequest: BiddingSpotApprovalRequest | undefined = draftRequests[0]
  // 絞り込み中なのに対象 DRAFT が 1 件も解決できない状態。 これは「DRAFT が無い」 では
  // なく「同日の DRAFT を画面に出していない」 なので、 書込系がこれを「DRAFT 不在」 と
  // 解釈して Create 分岐へ落ちると重複申請セットを起票してしまう (下の persistDraftBids)。
  // 明細未解決 (isSpotStateUnresolved) と同じ理由で書込を止める。
  //
  // origin では判定しない: 承認前の札確認 (approvalReview) は対象が PENDING なので常に
  // ここに該当するが、 差戻し後再編集 (reopen) でも背景再取得で対象 DRAFT が消えれば
  // 同じ状態になる (解除する clearFocusIfMatches はローカル操作経路でしか発火しない)。
  // 「絞り込んでいるのに対象 DRAFT が無い」 という事実そのものを条件にする。
  const isFocusedDraftUnavailable =
    focusedRequestId !== undefined && draftRequests.length === 0
  const draftRequestIds = useMemo(
    () => new Set(draftRequests.map((r) => r.id)),
    [draftRequests],
  )
  const pendingRequests = spotApprovalRequests.filter(
    (r) => r.approvalState === BiddingApprovalState.PENDING,
  )
  // 数える承認待ち (まとめて承認の入口・「承認申請中」 ボタン)。 入札締切を過ぎた受渡日の
  // 承認待ちは AC-BSP-099 で失効になるので数えない (AC-BSP-095)。 承認状態バナーが締切後に
  // 承認待ちを出さない (AC-BSP-067) のと同じ線で、 申請の承認状態が戻るのを待たない。
  // 承認・差戻し・申請取消の対象は上の `pendingRequests` から引き、 締切は確定時に別途判定する。
  const countedPendingRequests = isBeforeDeadline ? pendingRequests : []

  // 受渡日内の全 DRAFT 申請セットの札 (issue #3511 Phase 2)。 締切で失効にした札は除く (AC-BSP-099)。
  // 承認申請・提出確認 modal の集計の母集合は submitTargets (AC-BSP-007)。 提出確認 modal 用の集計は APPROVED request 群の札。
  const draftBids = useMemo(
    () =>
      bids.filter(
        (b) =>
          draftRequestIds.has(b.approvalRequestId) &&
          isNonExpiredDraftSpotBid(b),
      ),
    [bids, draftRequestIds],
  )
  const draftSubmitSummary = useMemo(
    () => summarizeSpotBidRows(submitTargets),
    [submitTargets],
  )

  // 承認申請をブロックする 3 種の違反 (design `bsc-violation-alert` / `bsc-stairs-alert`)。
  //
  // - AC-BSP-050 JEPX 制度上限違反: 価格 0.01〜999 円/kWh、数量 0.1〜9999.9 MW の
  //   制度値域。テナント設定に依存せず全組織で効く。AC-BSP-052 が「テナント設定上限違反
  //   とは独立した違反種別として区別して表示する」ことを求めるため、下の入札制限違反とは
  //   別セクションで出す (= 同じ札が両方に該当したら 2 セクションに出る)。
  // - AC-008 入札制限違反 (= テナント設定): 判定は一覧アラート (NormalBidTab) と同じ
  //   settings「入札制限」実設定値ベース (= useBidLimits、connect-query cache 共有で
  //   追加 fetch なし)。一覧アラートがエリアで絞った札を数えるのに対し、申請が数えるのは
  //   押した時点で表示中の下書きの札 (AC-BSP-007。 表示外の札の違反では止めない)。
  // - AC-021 JEPX 階段数超過: 同一コマ × エリア × 売買方向の札数が上限を超えるグループ。
  const { limits: normalBidLimits, isError: isBidLimitsError } = useBidLimits(
    BidSegment.SPOT_NORMAL,
  )
  // 承認申請確認 modal のエリア別内訳 (AC-BSP-007。 承認確認 modal の
  // spotDecision.areaBreakdown と同じ組み立て方、 情報粒度を揃える)。
  const draftAreaBreakdown = useMemo(
    () => buildSpotAreaBreakdown(submitTargets),
    [submitTargets],
  )
  // 承認申請確認 modal の売買逆転チェック結果 (AC-BSP-007)。 承認確認 modal
  // (useSpotApprovalDecision.warningSections) と同じく、 受渡日の有効札全体
  // (selectRevalidationBids) を母集合にして対象外の組を出さないようにする。
  const draftRevalidationBids = useMemo(
    () => selectRevalidationBids(bids),
    [bids],
  )
  const draftWarningSections = useMemo(
    () => [buildSpotInversionSection(submitTargets, draftRevalidationBids, t)],
    [submitTargets, draftRevalidationBids, t],
  )

  // ----- 書込 (個別作成 / 編集 / 削除) -----
  // 当日 request が無ければ Create、 有れば既存札を input に戻して全置換 Update。
  const { mutateAsync: createAsync, isPending: isCreating } =
    useCreateBiddingSpotApprovalRequest()
  const { mutateAsync: updateAsync, isPending: isUpdating } =
    useUpdateBiddingSpotApprovalRequest()
  // 取下 (AC-BSP-057)。 他の書き込みと loading 状態を分けるため別 instance にする。
  const { mutateAsync: withdrawAsync, isPending: isWithdrawing } =
    useWithdrawBiddingSpotBid()
  const { mutateAsync: deleteApprovalAsync, isPending: isDeleting } =
    useDeleteBiddingSpotApprovalRequest()
  // 行削除の再入判定 (issue #3511)。 isUpdating / isDeleting は次の render でしか
  // 立たないため、 同一 tick の連打は state では防げない。
  const deletingRef = useRef(false)
  // 取下の再入判定 (AC-BSP-057)。 deletingRef と同型: isWithdrawing (isPending) は
  // 次の render でしか立たないため、 同一 tick の連打確認クリックは state だけでは
  // 防げず、 同じ楽観ロック値の取消 RPC が複数飛ぶ。
  const withdrawingRef = useRef(false)
  // ----- 承認ライフサイクル遷移 (REQUEST / SUBMIT) -----
  const { mutateAsync: transitionAsync, isPending: isTransitioning } =
    useTransitionBiddingSpotApprovalState()
  // 表示中の下書きの承認申請・直接提出 (AC-BSP-007)。 各 target の action は backend が導く。
  const {
    mutateAsync: transitionDraftsAsync,
    isPending: isTransitioningDrafts,
  } = useTransitionBiddingSpotDrafts()
  const [writeError, setWriteError] = useState<string | undefined>(undefined)
  // AC-055: 申請取消の確認 modal が対象とする PENDING request の id (= undefined で
  // 非表示)。 entity ではなく id で持ち、 確定時に pendingRequests から引き直す (= modal を
  // 開いたまま invalidate/refetch が走っても stale な expectedUpdatedAt を握らない。 承認 /
  // 差戻し (useSpotApprovalDecision) とブロック側 cancelingBlockGroupId と同じ不変条件)。
  const [cancelingRequestId, setCancelingRequestId] = useState<
    string | undefined
  >(undefined)
  // 申請取消 確認 modal 用の集計は取消対象 request の札のみ (AC-055 / AC-082)。
  const cancelingSummary = useMemo(
    () => summarizeSpotBidRows(selectRequestBids(bids, cancelingRequestId)),
    [bids, cancelingRequestId],
  )
  // 入札一覧 accordion (= 絞り込み後のスクロール先)。
  const bidListRef = useRef<HTMLDivElement>(null)
  // 絞り込み直後の入札一覧へのスクロールは、通常入札タブの描画完了後に行う one-shot
  // フラグ (契機は AC-056 の再編集と AC-BSP-094 の承認前確認)。setTab 直後の同期
  // scrollIntoView は未マウント / 非表示の一覧に対して走って不発になるため、tab が
  // normalBid になった後の effect で 1 回だけ実行する。#6449 で承認状態バナーを通常
  // 入札タブ内へ移して以降、押下元は常に normalBid だが、呼び出し側の
  // setTab('normalBid') に依存せず「描画後に 1 回」を保証する構造はそのまま残す
  // (= 一覧絞り込み (focusedRequest) が反映されてからスクロールする、の意味もあるため)。
  const [pendingBidListScroll, setPendingBidListScroll] = useState(false)
  useEffect(() => {
    if (!pendingBidListScroll || tab !== 'normalBid') return
    bidListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setPendingBidListScroll(false)
  }, [pendingBidListScroll, tab])
  // 個別作成モーダルの開き方 (新規作成 / 編集 / 複製 / 再入札は排他)。 1 つの判別共用体に
  // まとめ、 「編集対象と複製元が両方ある」 状態を作れないようにする。 再入札
  // (AC-BSP-081) の元になる取消済みの札も `mode: 'rebid'` で持ち、 編集モードにはしない
  // (= 新規 BidId の新しい札として起票する)。
  const [individualModalSource, setIndividualModalSource] =
    useState<IndividualBidModalSource>({ mode: 'create' })
  const editingBid =
    individualModalSource.mode === 'edit'
      ? individualModalSource.bid
      : undefined
  const openIndividualBidModal = (source: IndividualBidModalSource): void => {
    setIndividualModalSource(source)
    setModalKind('individual-create')
  }
  // 失効した札の「複製」(AC-BSP-011「翌営業日向けに再利用できる」)。 失効札の受渡日は
  // 必ず入札締切後なので、 表示中の受渡日のまま新規作成すると AC-BSP-013 / AC-BSP-064
  // に当たり backend も締切超過で拒否する。 そのため複製先は締切前で最も近い受渡日
  // ([[nearestOpenSpotTargetDate]]) に切り替え、 その日付の通常入札タブで開く。
  // workflowId 付きで受渡日が固定されている間は日付を動かせないので、 固定日が
  // 締切前の時だけその日へ複製でき、 締切後なら導線自体を出さない。 読み取り専用
  // (完了済ケース等) でも出さない。 複製は新規作成なので、 他の作成導線 (個別作成 /
  // コマクリック。 NormalBidTab の canCreateBid) と同じ述語 [[canCreateSpotBid]] で
  // 決める: 承認フロー設定が取得中・取得失敗の間 (保存時に ApprovalFlowUnresolvedError で
  // 止まるだけになる) と、 承認前の札確認などの絞り込みで追加先の下書きが画面に無い間
  // (保存時に SpotFocusedRequestUnavailableError で止まるだけになる) も出さない。
  const canDuplicateBid = canCreateSpotBid({
    // 複製先の受渡日の締切前か。 固定されていなければ締切前で最も近い日へ切り替える。
    isBeforeDeadline: !isDateLocked || (isTargetDateReady && isBeforeDeadline),
    isReadonly,
    approvalFlowUnavailable: isApprovalFlowUnavailable,
    isFocusedDraftUnavailable,
  })
  const handleDuplicateBid = (row: SpotBidRow): void => {
    const duplicateTargetDate = isDateLocked
      ? targetDate
      : nearestOpenSpotTargetDate(serverNow())
    if (!isDateLocked) setManualTargetDate(duplicateTargetDate)
    setTab('normalBid')
    openIndividualBidModal({
      mode: 'duplicate',
      source: row,
      targetDate: duplicateTargetDate,
    })
  }
  const [deletingBid, setDeletingBid] = useState<SpotBidRow | undefined>(
    undefined,
  )
  // 取下 (AC-BSP-057) の対象札。 undefined = 確認モーダルを閉じている。
  const [withdrawingBid, setWithdrawingBid] = useState<SpotBidRow | undefined>(
    undefined,
  )

  // ----- ブロック入札 -----
  // 受渡日 filter でブロック承認依頼一覧を取得し、 DRAFT 件数と提出対象グループを導出する。
  // 取得失敗はブロックタブ内 Alert で伝える (= hook 側 throwOnError:false と対)。
  // caseId 付きで開いている間、 ケース対象日が確定するまでは対象日依存のこの一覧も
  // 取得を開始しない (AC-WFW-006a Decision 1、 通常入札の一覧/screen query と同じ gating)。
  const {
    data: blockListData,
    dataUpdatedAt: blockListUpdatedAt,
    isFetching: isBlockListFetching,
    isError: isBlockListError,
  } = useListBiddingBlockApprovalRequests(targetDateFilter, {
    enabled: isTargetDateReady,
  })
  // 一覧 item は約定軸を持たないので、 入札締切まで提出せず失効にした group (承認ステータスは
  // DRAFT に戻る、 AC-BSP-099) は明細 (screen) と突き合わせて外す。
  const draftBlockGroups = useMemo(
    () =>
      selectNonExpiredDraftBlockGroups(
        blockListData?.items ?? [],
        screenData?.blockApprovalRequests ?? [],
      ),
    [blockListData, screenData],
  )
  const draftBlockCount = draftBlockGroups.length
  // PENDING (= 承認申請中) の集計。
  // メインアクションボタン (renderBlockActionButton) の状態別出し分けに使う。
  const pendingBlockGroups = useMemo(
    () =>
      (blockListData?.items ?? []).filter(
        (item) => item.approvalState === BiddingApprovalState.PENDING,
      ),
    [blockListData],
  )
  // 入札締切を過ぎた受渡日の承認待ちは数えない (通常入札の `countedPendingRequests` と同じ。
  // AC-BSP-095 / AC-BSP-099)。
  const pendingBlockCount = isBeforeDeadline ? pendingBlockGroups.length : 0
  // 承認済 (承認を経て提出まで進んだ group を含む) の送信の進み具合。 主ボタンの出し分けに使う
  // (renderApprovedSendButton)。
  const blockApprovedSendKind = resolveApprovedSendKind(
    blockListData?.items ?? [],
  )

  const blockApprovalRequests = useMemo(
    () => screenData?.blockApprovalRequests ?? [],
    [screenData],
  )
  // AC-BSP-096 (ブロック入札): 通常入札と同じ配線・同じ理由。 一覧 polling が
  // 承認状態または id 集合の変化を拾ったとき (承認待ちのまま一覧の更新日時が明細より
  // 新しいときを含む、 `isPendingUpdatedSince`) に screen を 1 回取り直し、
  // BlockApprovalStatusBanner (差戻し理由・差戻し日時は明細にしか無い) を追従させる。
  // 取下処理中の明細を持つ提出済のグループは、 取下の結果 (取消・取下失敗) で一覧の更新日時が
  // 進んだときにも取り直す (AC-BSP-057、 `blockDetailStatesOf`)。
  const staleBlockDetailIdsKey = useMemo(
    () =>
      findStaleApprovalDetailIds(
        blockListData?.items ?? [],
        blockDetailStatesOf(blockApprovalRequests),
      ).join(','),
    [blockListData, blockApprovalRequests],
  )
  useEffect(() => {
    if (staleSpotDetailIdsKey === '' && staleBlockDetailIdsKey === '') return
    // 通常・ブロックは同じ screen を使う。遅い取得を次の一覧 polling で中断せず、
    // 差分が残る場合は次の polling で取り直す (AC-BSP-110a)。別日の cache は無効化しない。
    void queryClient.invalidateQueries(
      {
        queryKey: createConnectQueryKey({
          schema: getBiddingSpotScreen,
          input: create(GetBiddingSpotScreenRequestSchema, {
            targetDate: targetDateFilter,
          }),
          cardinality: 'finite',
        }),
      },
      { cancelRefetch: false },
    )
  }, [
    staleSpotDetailIdsKey,
    staleBlockDetailIdsKey,
    spotListUpdatedAt,
    blockListUpdatedAt,
    targetDateFilter,
    queryClient,
  ])
  // 送信状態の変化はバナーへ渡す明細に一覧の値を重ねて反映する (通常入札と同じ理由)。
  const blockBannerRequests = useMemo(
    () => withListSendStatus(blockApprovalRequests, blockListData?.items ?? []),
    [blockListData, blockApprovalRequests],
  )
  // screen が未解決、または list / screen の差分が残る間は、 その group の
  // 内容 (leg / 価格 / 数量) を利用者が確認できていない (= blockApprovalRequests
  // に含まれない)。 一方 draftBlockGroups は一覧 (list) 由来
  // なので、 一覧が成功していれば未解決の group もそのまま含まれてしまう。
  // 中身を確認できない group に承認申請・JEPX 提出の状態遷移を実行させると事故に
  // なる (= 通常入札の hasUnresolvedSpotDetail と同じ理由。 throwOnError:false 化が
  // 新規に到達可能にした退行)。
  const hasUnresolvedBlockDetail =
    screenData === undefined || staleBlockDetailIdsKey !== ''
  // 一覧 (list) 自体が失敗した場合も同じく明細の全量を確定できない。 一覧の失敗を
  // 明示的に未解決として合流させ、 承認申請・JEPX 提出の判定を fail-closed に保つ。
  const isBlockStateUnresolved =
    hasUnresolvedBlockDetail ||
    isBlockListError ||
    isScreenError ||
    isBlockScreenSourceFailed

  // 上限数量(コマ計) (AC-BSP-080) の合算母集合: 申請対象の DRAFT 札 (submitTargets) に
  // 加え、 受渡日内の既存の有効札 (承認申請中・承認済・入札中、 selectRevalidationBids)
  // と、 同じ受渡日の有効なブロック入札の当該コマ数量 (selectRevalidationBlockVolumes)
  // を含めた受渡日通算の総エクスポージャで判定する (AC-BSP-080 の合算母集合定義)。
  // backend の承認申請・直接提出・承認のゲート (spot_bid.go SumActiveBlockBidVolume) も
  // 同じ母集合で判定する。
  const blockVolumeTotalRows = useMemo(
    () =>
      selectRevalidationBlockVolumes(screenData?.blockApprovalRequests ?? []),
    [screenData],
  )
  // 受渡日内の既存の有効札 (通常入札の有効札 + 有効なブロック入札の当該コマ数量) の母集合。
  // 申請対象の DRAFT 札を含まない。 通常入札・ブロック入札の承認確認 modal の再検証
  // (AC-BSP-097) と一括作成のインライン判定の既存分に使う (承認対象の申請セット・group 自身は
  // PENDING なのでこの中に入る)。
  const activeVolumeTotalRows = useMemo(
    () => [...selectRevalidationBids(bids), ...blockVolumeTotalRows],
    [bids, blockVolumeTotalRows],
  )
  const draftVolumeTotalRows = useMemo(
    () => [...submitTargets, ...activeVolumeTotalRows],
    [submitTargets, activeVolumeTotalRows],
  )
  // 承認申請確認と直接提出確認と混在の確認は同じ母集合・同じ判定で、 上限数量(コマ計) の明細と
  // 本文だけ「提出できません」 「申請・提出できません」 に替わる (AC-BSP-063)。 開いた時点の
  // 計画の種類から 1 回だけ組む。
  const draftViolationAudience = DRAFT_VIOLATION_AUDIENCE[draftKind]
  const draftViolationSections = useMemo(
    () => [
      ...buildSpotViolationSections(submitTargets, normalBidLimits, t, {
        audience: draftViolationAudience,
        volumeTotalRows: draftVolumeTotalRows,
      }),
      // 入札制限が取得できていない間は、上の AC-008 判定が「制限未設定 = 違反なし」に
      // 見えるだけで検証が成立していない (= 空 lookup)。 画面は描画し続けるが、
      // 検証できないまま申請へ進ませない (fail-closed): 節を 1 つ立てて申請確認 modal に
      // 理由を出し、hasDraftViolation 経由で「申請する」を非活性にする。
      // backend も同じ設定を読んで DRAFT 離脱時に検証する (ensureWithinBidLimits) ため、
      // ここで通しても結局は弾かれる = 通す実益が無く、理由の分かりにくいエラーになる。
      //
      // 承認確認 modal (AC-BSP-097) にも宛先違いの同型の節を積む
      // (useSpotApprovalDecision)。
      ...(isBidLimitsError ? [buildBidLimitsUnavailableSection(t)] : []),
      // ブロック入札を確定できていない間は、 上限数量(コマ計) の合算からブロック分が
      // 抜けて「違反なし」 に見えるだけになる。 入札制限の取得失敗と同じく fail-closed。
      ...(isBlockStateUnresolved
        ? buildBlockVolumesUnavailableSections(
            submitTargets,
            normalBidLimits,
            t,
            draftViolationAudience,
          )
        : []),
    ],
    [
      submitTargets,
      draftViolationAudience,
      draftVolumeTotalRows,
      isBidLimitsError,
      isBlockStateUnresolved,
      normalBidLimits,
      t,
    ],
  )
  const hasDraftViolation = draftViolationSections.some(
    (s) => s.details.length > 0,
  )

  // BlockBidRow[] に変換 (= エリアフィルタは BlockBidTab 内で適用)。
  const blockBidRows = useMemo(
    () => convertBlockBidRows(blockApprovalRequests),
    [blockApprovalRequests],
  )
  // AC-BSP-019: 区分を選び直す・作り直す下書きがある間は、 空状態へ差し替えない。
  const loadedIssuerOptions = useLoadedIssuerOptions(targetDate)
  const hasRepairDraft =
    bids.some((bid) =>
      isAlwaysListedDraft(
        {
          isDraft: bid.approvalState === BiddingApprovalState.DRAFT,
          issuerType: bid.issuerType,
          areas: [bid.areaCode],
        },
        loadedIssuerOptions,
      ),
    ) ||
    blockBidRows.some((row) =>
      isAlwaysListedDraft(
        {
          isDraft: row.approvalState === BiddingApprovalState.DRAFT,
          issuerType: blockRowBidIssuerType(row),
          areas: [row.areaCode],
        },
        loadedIssuerOptions,
      ),
    )
  // AC-BSP-105: 空状態の間は入札一覧とチャートを差し替える。 差し替えを止めるのは AC-BSP-019 の
  // 下書きだけ。 区分が空欄の取り込んだ札 (AC-BSP-111b) も隠す。 AC-BSP-111b を優先して一覧を出すかは
  // spec に例外が無く、 PdM に確認中 (#11326) のため、 決まるまで既存の差し替えを変えない。
  const showsAreaEmptyPanel = candidateShowsAreaEmptyPanel && !hasRepairDraft
  const hidesBidLists = hidesAreaContent && !hasRepairDraft
  // 申請セットに絞り込むと判定は組織全体になる。 そこでも一覧が出ない間は「対象の札を確認」を出さず、
  // 再編集の後も絞り込まない (押しても一覧が出ず、 解除する操作も出ないため)。 一覧を出すかと同じ条件で、
  // AC-BSP-019 の下書きで一覧が出ている間は絞り込める。
  const canFocusRequest =
    !orgWideAreaEmptyState.hidesAreaContent || hasRepairDraft
  // 再編集で下書きに戻す札が AC-BSP-019 の下書きになるなら、 戻した後は一覧が出るので絞り込める。
  const reopensIntoListedDraft = (
    request: BiddingSpotApprovalRequest,
  ): boolean =>
    request.spotBids.some((bid) =>
      isAlwaysListedDraft(
        {
          isDraft: true,
          issuerType: bidIssuerTypeOf(bid.issuerType),
          areas: [bid.areaCode],
        },
        loadedIssuerOptions,
      ),
    )

  // 取下の結果 (取下失敗の確定 AC-BSP-057b、 一括取下で受け付けた札の成立・取下失敗 AC-BSP-116) を
  // 開いている画面で観測したらトーストで知らせる。 通常入札の札とブロック入札の Leg は同じ screen の値。
  const withdrawResultNotices = useSpotWithdrawResultNotices({
    bids,
    blockBidRows,
    observedAtMs: screenUpdatedAt,
    isObservable: screenData !== undefined,
    isBeforeDeadline,
  })
  const matchesReviewIssuer = (issuerType: string | undefined): boolean =>
    issuerType === 'unknown' ||
    (pageIssuerType !== LicenseType.UNSPECIFIED &&
      issuerType === bidIssuerTypeOf(pageIssuerType))
  const needsReviewCount =
    bids.filter((bid) => bid.needsReview && matchesReviewIssuer(bid.issuerType))
      .length +
    new Set(
      blockBidRows
        .filter(
          (row) =>
            row.needsReview &&
            matchesReviewIssuer(
              row.issuerType !== undefined
                ? bidIssuerTypeOf(row.issuerType)
                : undefined,
            ),
        )
        .map((row) => row.groupId),
    ).size
  const blockSendResultSubjectList = useMemo(
    () =>
      blockListData === undefined
        ? undefined
        : blockSendResultSubjects(blockListData.items, blockApprovalRequests),
    [blockListData, blockApprovalRequests],
  )
  useSendResultNotice(
    blockSendResultSubjectList,
    blockListUpdatedAt,
    'perGroup',
    targetDateScope,
  )
  const unknownIssuerDeliveryContractCodes = useMemo(
    () => spotUnknownIssuerDeliveryContractCodes(bids, blockBidRows),
    [bids, blockBidRows],
  )

  // PENDING (= 承認申請中) の group で、 かつ明細 Get が解決済み (= blockApprovalRequests
  // に含まれる) のもの。 AC-BSP-055 の申請取消 (確認 modal の対象解決・札件数表示) 用。
  // 一覧 (list) 由来の pendingBlockGroups と違い、 expectedUpdatedAt を Get 済み full
  // entity から正しく解決できる group だけに絞る (= 明細 Get が未解決の group は取消
  // 対象にしない、 fail-closed)。
  const pendingBlockApprovalRequests = useMemo(
    () =>
      blockApprovalRequests.filter(
        (g) => g.approvalState === BiddingApprovalState.PENDING,
      ),
    [blockApprovalRequests],
  )
  // まとめて承認に並べるのは締切前の承認待ちだけ (承認・差戻し・申請取消の対象解決は上の
  // `pendingBlockApprovalRequests` で、 締切は確定時に別途判定する)。
  const countedPendingBlockApprovalRequests = isBeforeDeadline
    ? pendingBlockApprovalRequests
    : []

  // DRAFT group のうち明細 Get 済み (= blockApprovalRequests に含まれる) のもの。
  // 承認モード (AC-BSP-015) は list item (draftBlockGroups) に無く full entity
  // にしかないため、 メインボタンの出し分け用にここで解決する。 全 DRAFT group
  // が Get 済みとは限らない (= isBlockStateUnresolved) ので、 renderBlockActionButton
  // 側でその場合は安全側 (承認申請フロー) にフォールバックする。
  const draftBlockApprovalRequests = useMemo(
    () => blockApprovalRequests.filter(isNonExpiredDraftBlockGroup),
    [blockApprovalRequests],
  )

  // 提出確認・承認申請確認モーダルの確認項目「承認フロー」 (#9586)。 対象の札のエリアを、
  // 承認フロー設定の現在の値で要承認 / 承認なし / 自動にまとめる (design
  // `js/approval-modes.js` の `_approvalModeText` と同じく、 申請セットに保存した値でなく
  // エリアごとの設定を出す。 保存値は「自動」 を「承認なし」 に畳むため)。 設定を読めて
  // いない間は行を出さない (推測で書かない、 2026-09-16 ユーザー決定 1-B と同じ理由)。
  const spotApprovalModesOf = useCallback(
    (
      areaCodes: readonly AreaCode[],
    ): readonly ApprovalModeGroup[] | undefined =>
      approvalFlowScreenData === undefined
        ? undefined
        : groupAreasByApprovalMode(areaCodes, (area) =>
            resolveAreaApprovalMode(
              approvalFlowScreenData.processes,
              BusinessProcess.SPOT_BID,
              area,
            ),
          ),
    [approvalFlowScreenData],
  )
  const draftApprovalModes = useMemo(
    () => spotApprovalModesOf(submitTargets.map((b) => b.areaCode)),
    [spotApprovalModesOf, submitTargets],
  )
  // 一部の札だけを申請・提出する時の承認モードの解決 (planDraftSubmit に渡す)。 設定を
  // 読めていない間は undefined (推測で解決しない。 2026-09-16 ユーザー決定 1-B)。
  const resolveSpotDraftMode = useMemo(
    () =>
      approvalFlowScreenData === undefined
        ? undefined
        : (areaCodes: readonly AreaCode[]): BiddingApprovalMode =>
            resolveCreateApprovalMode({
              processes: approvalFlowScreenData.processes,
              businessProcess: BusinessProcess.SPOT_BID,
              areaCodes,
            }),
    [approvalFlowScreenData],
  )
  // ブロックは LINK / LOOP の相手側 (secondary) のエリアも対象 (承認モードの解決と同じ)。
  // 押した時点で確定した group の全 leg から数える。 明細が未解決の group があると対象の
  // エリアを確定できないので行を出さない。
  const draftBlockApprovalModes = useMemo(
    () =>
      isBlockStateUnresolved
        ? undefined
        : spotApprovalModesOf(
            blockSubmitScope.flatMap((g) => g.rows).map((r) => r.areaCode),
          ),
    [isBlockStateUnresolved, spotApprovalModesOf, blockSubmitScope],
  )

  // 重複確認の対象は、各確認 modal が実際に遷移させる札と同じにする (主ボタンを押した時点で
  // 確定した、一覧に表示中の下書き。 AC-BSP-007 / AC-BSP-036、 #7758)。
  const spotSubmissionDuplicateTargets = useMemo(
    () =>
      submitScope === undefined
        ? []
        : draftSubmitTargets(submitScope.plan).map((target) => ({
            expectedUpdatedAt: target.expectedUpdatedAt,
            id: target.id,
            spotBidIds: target.spotBidIds,
          })),
    [submitScope],
  )
  const blockSubmissionDuplicateTargets = useMemo(
    () =>
      blockSubmitScope.map((group) => ({
        expectedUpdatedAt: group.updatedAt,
        id: group.id,
      })),
    [blockSubmitScope],
  )
  const spotSubmissionDuplicateCheck = useSpotSubmissionDuplicateCheck(
    modalKind === 'request-confirm' || modalKind === 'direct-submit-confirm',
    spotSubmissionDuplicateTargets,
    `${spotListUpdatedAt}:${screenUpdatedAt}`,
    isSpotListFetching || isScreenFetching,
  )
  const blockSubmissionDuplicateCheck = useBlockSubmissionDuplicateCheck(
    modalKind === 'block-submit-confirm' ||
      modalKind === 'block-direct-submit-confirm',
    blockSubmissionDuplicateTargets,
    `${blockListUpdatedAt}:${screenUpdatedAt}`,
    isBlockListFetching || isScreenFetching,
  )

  // ----- 調達必要量 (need) の実データ (= GetProcurementNeed、 spot 市場) -----
  // RPC は全 9 エリア分を取得し、 チャート (needByKoma) は選択中の単一エリアだけを使う
  // (AC-BSP-016a: 複数エリア合算はカバー率が実態と合わないため単一エリア限定)。
  // needByArea (= 全エリア) は一括作成が複数エリアを対象にできるよう別軸で消費する
  // (AC-BSP-005a: 対象エリアは複数選択)。取得エラー時は need line 非表示 + 注記、
  // cover/executed は描画継続 (= 設計書 bidding-position-need.md §7、ADR
  // bidding-position-need-bilateral-baseline.md 残論点参照)。
  const chartAreas = useMemo(
    () =>
      singleSelectedArea !== undefined
        ? new Set([singleSelectedArea])
        : new Set<AreaCode>(),
    [singleSelectedArea],
  )
  // caseId 付きで開いている間、 ケース対象日が確定するまでは対象日依存のこの
  // need query も取得を開始しない (AC-WFW-006a Decision 1)。
  // チャートの need は共通ツールバーで選んだ発注主体区分の計画から求める (AC-BSP-102)。
  // 区分の既定値が入る前に区分なしで取ると、 別の量の線が一瞬描かれるので取らない。
  const { needByKoma, needUnavailable: chartNeedUnavailable } =
    useProcurementNeedChartData(
      targetDate,
      chartAreas,
      ProcurementNeedMarket.SPOT,
      JEPX_AREA_CODES,
      {
        enabled:
          isTargetDateReady && pageIssuerType !== LicenseType.UNSPECIFIED,
        issuerType: pageIssuerType,
      },
    )
  // need が無い間 (取得前・区分の既定値が入る前・区分がチャート対象エリアに計画を持たない) は
  // need 由来の値 (need 線・残量・超過・札作成の数量の既定値) を出さない。 0 で埋めると
  // 「過不足なし」 と読まれる。 取得失敗は注記を出す既存の扱い (needUnavailable) に任せる。
  const chartNeedAbsent = needByKoma === undefined && !chartNeedUnavailable
  // 一括作成は区分なしの need のまま (一括作成モーダルで選んだ区分の need にするのは別 slice。
  // #10574 の分割計画への書き戻し)。 ツールバーの区分と別の query にして値を変えない。
  const { needByArea, needUnavailable: bulkNeedUnavailable } =
    useProcurementNeedChartData(
      targetDate,
      chartAreas,
      ProcurementNeedMarket.SPOT,
      JEPX_AREA_CODES,
      { enabled: isTargetDateReady },
    )

  // スポットは通常/ブロック両タブで同一チャートを共有する (spec spot-bidding.md AC-016)。
  // 選択中エリアの通常＋ブロックの cover/executed を合算した slot を組み、両タブへ供給する
  // (need は同一)。 単一エリア以外 (AC-BSP-016a) では空配列を返し、
  // `SpotPositionChartPanel` 側のガイド表示に委ねる。
  const sharedPositionSlots = useMemo(
    () =>
      singleSelectedArea === undefined
        ? []
        : buildIssuerPositionSlots({
            bids,
            blockRows: blockBidRows,
            area: singleSelectedArea,
            issuer: pageIssuerType,
            needByKoma,
            // 入札後カバー量を「入札済み」「全札（下書き含む）」の 2 本に分ける分類は締切通過を
            // 見る (AC-BSP-108)。 締切通過は分単位 tick (useNowByMinute 由来の
            // phase/isBeforeDeadline) で再評価し、 画面を開いたまま締切を跨いでも分類が古いまま
            // 残らないようにする。
            isPastDeadline: !isBeforeDeadline,
          }),
    [
      bids,
      blockBidRows,
      singleSelectedArea,
      pageIssuerType,
      needByKoma,
      isBeforeDeadline,
    ],
  )

  /*
   * コマクリックのハンドラ。 チャートはタブの外 (`SpotPositionChartPanel`) にあり、
   * 開くモーダルの state は `NormalBidTab` が持ったままなので、 タブ側から
   * `onRegisterSlotClick` で預かったものをここで保持して繋ぐ (issue #6596 論点3)。
   */
  const slotClickRef = useRef<((index: number) => void) | undefined>(undefined)

  // ブロック入札タブ表示中はコマクリックを無効化する (2026-09-02 Des 判断 / #6596 Q2)。
  // 実装に prefill 付きブロック追加の経路が無く、 通常入札モーダルが開くと
  // 「ブロックを見ているのに通常入札が作られる」 ことになるため。
  const chartSlotClick =
    tab === 'normalBid'
      ? (index: number): void => slotClickRef.current?.(index)
      : undefined

  const { mutateAsync: blockCreateAsync, isPending: isBlockCreating } =
    useCreateBiddingBlockApprovalRequest()
  const { mutateAsync: blockUpdateAsync, isPending: isBlockUpdating } =
    useUpdateBiddingBlockApprovalRequest()
  const { mutateAsync: blockDeleteAsync, isPending: isBlockDeleting } =
    useDeleteBiddingBlockApprovalRequest()
  const { mutateAsync: blockTransitionAsync, isPending: isBlockTransitioning } =
    useTransitionBiddingBlockApprovalState()
  const { mutateAsync: blockWithdrawAsync, isPending: isBlockWithdrawing } =
    useWithdrawBiddingBlockBidGroup()
  // 同一 tick の連打で同じ楽観ロック値の取下 RPC が複数飛ぶのを防ぐ (withdrawingRef と同型)。
  const blockWithdrawingRef = useRef(false)
  const [blockWriteError, setBlockWriteError] = useState<string | undefined>(
    undefined,
  )
  // 入力の初期値は開いた時の snapshot に固定する (AC-BSP-110a)。背景更新で
  // フォームを再 mount しない。保存可否は最新の screen、編集の照合値は入力と同じ版を使う。
  const [blockModalSource, setBlockModalSource] = useState<
    BiddingBlockApprovalRequest | undefined
  >(undefined)
  const editingBlockGroupId = blockModalSource?.id
  const [deletingBlockGroupId, setDeletingBlockGroupId] = useState<
    string | undefined
  >(undefined)
  const editingBlockGroup = blockApprovalRequests.find(
    (g) => g.id === editingBlockGroupId,
  )
  // AC-BSP-055: 申請取消の確認 modal が対象とする PENDING group の id (= undefined で
  // 非表示)。 取消単位は BlockBidGroup 単位 (通常入札の request 単位と対照、issue #4218)。
  // editingBlockGroupId / deletingBlockGroupId と同型に id だけを保持し、 group 本体は
  // 確定時に blockApprovalRequests (= 並列 Get 結果) から都度解決する (= entity を state
  // に固定すると、 modal を開いたまま invalidate/refetch が走った際に stale な
  // expectedUpdatedAt を握り続け、 正当な取消が楽観ロック競合 (Aborted) で弾かれ続ける。
  // 通常入札の cancelingRequestId / 承認・差戻しの両 hook も同じ不変条件)。
  const [cancelingBlockGroupId, setCancelingBlockGroupId] = useState<
    string | undefined
  >(undefined)
  // 取下 (AC-BSP-057) の対象 group の id。 cancelingBlockGroupId と同じく id だけを持ち、
  // 楽観ロック基準値は確定時に blockApprovalRequests から解決する。
  const [withdrawingBlockGroupId, setWithdrawingBlockGroupId] = useState<
    string | undefined
  >(undefined)
  const deletingBlockSummary = useMemo(
    () =>
      deletingBlockGroupId === undefined
        ? []
        : formatBlockBidGroupDeleteSummary(
            blockBidRows.filter((r) => r.groupId === deletingBlockGroupId),
            t,
          ),
    [blockBidRows, deletingBlockGroupId, t],
  )
  const withdrawingBlockSummary = useMemo(
    () =>
      withdrawingBlockGroupId === undefined
        ? undefined
        : formatBlockBidGroupWithdrawSummary(
            blockBidRows.filter((r) => r.groupId === withdrawingBlockGroupId),
            t,
          ),
    [blockBidRows, withdrawingBlockGroupId, t],
  )
  // 入札中の札の一括取下 (AC-BSP-116)。 通常入札・ブロック入札の両タブの ⋯ メニューが同じ状態を使う。
  // 対象は、 両タブの一覧と同じ共通ツールバーの範囲と、 両タブで共有する絞り込み (listFilter) で数える。
  const bulkWithdrawScope = useMemo(
    () => ({
      selectedAreas,
      unknownIssuerUnselectedAreas:
        rawSelectedAreas.size === 0 ? areaCodes : [],
      issuerScopeType,
    }),
    [selectedAreas, rawSelectedAreas, areaCodes, issuerScopeType],
  )
  const bulkWithdraw = useBulkWithdrawControl({
    bids,
    blockBidRows,
    spotRequests: spotApprovalRequests,
    blockRequests: blockApprovalRequests,
    spotExcludedWorkflowIds: spotBulkWithdrawExcludedWorkflowIds,
    blockExcludedWorkflowIds: blockBulkWithdrawExcludedWorkflowIds,
    scope: bulkWithdrawScope,
    listFilter,
    isBeforeDeadline,
    isReadonly,
    isSpotUnresolved: isSpotStateUnresolved,
    isBlockUnresolved: isBlockStateUnresolved,
    targetDate,
    holdNotices: withdrawResultNotices.holdForBulkWithdraw,
  })

  // 取消確認 modal が示す対象の leg (札) 件数 (= 承認確認 modal の legCount と同じ helper)。
  const cancelingBlockLegCount = useMemo(
    () =>
      countBlockGroupLegs(
        pendingBlockApprovalRequests.find(
          (g) => g.id === cancelingBlockGroupId,
        ),
      ),
    [cancelingBlockGroupId, pendingBlockApprovalRequests],
  )

  // ----- 承認 / 差戻し (AC-BSP-094 / AC-BSP-097 / AC-BSP-099) -----
  // 承認者としての判断 (確認 modal の集計・入札制限の再検証・確定時ガード・遷移) は
  // 申請者側の下書き編集フローと独立した関心なので、 通常入札 / ブロック入札とも専用の
  // hook に閉じている。 承認案件一覧画面が Phase 1 時点で動作保証対象外のため、
  // 本画面が承認/差戻し操作の唯一の経路 (spec spot-bidding.md:171)。
  const spotDecision = useSpotApprovalDecision({
    pendingRequests,
    bids,
    volumeTotalRows: activeVolumeTotalRows,
    isBlockVolumeUnresolved: isBlockStateUnresolved,
    limits: normalBidLimits,
    isLimitsError: isBidLimitsError,
    isStateUnresolved: isSpotStateUnresolved,
    isReadonly,
    phase,
    targetDate,
    onTransitionStart: () => {
      setWriteError(undefined)
    },
    onDecided: clearFocusIfMatches,
  })
  const blockDecision = useBlockApprovalDecision({
    pendingGroups: pendingBlockApprovalRequests,
    isStateUnresolved: isBlockStateUnresolved,
    isReadonly,
    phase,
    targetDate,
    onTransitionStart: () => {
      setBlockWriteError(undefined)
    },
    volumeTotalRows: activeVolumeTotalRows,
    isSpotVolumeUnresolved: isSpotStateUnresolved,
  })
  // ----- 取得失敗の集約 (= 部分描画の継続) -----
  // スポット入札画面は独立した複数 RPC の合成画面なので、 各 read query は
  // `throwOnError: false` で error boundary (= 全画面エラー) に飛ばさず、 失敗した
  // データ源だけをこのバナーで知らせて残りは描画を続ける (= 1 本の RPC 障害で受渡日の
  // 変更も他タブの参照もできなくなるのを避ける)。 書込 (mutation) は従来どおり
  // modal 内のエラー表示 / toast で扱う。
  const failedDataLabels = computeFailedDataLabels(
    {
      isSpotListError,
      isScreenError,
      isSpotScreenSourceFailed,
      isBlockListError,
      isBlockScreenSourceFailed,
      isCrossDateApprovalsError,
      needUnavailable: chartNeedUnavailable || bulkNeedUnavailable,
      isBidLimitsError,
      isTradableAreasError,
      isBusinessAreasError,
      isWfCaseError,
      isApprovalFlowError: isApprovalFlowScreenError,
    },
    {
      spotBids: t('page:biddingSpot.loadError.source.spotBids'),
      blockBids: t('page:biddingSpot.loadError.source.blockBids'),
      crossDateApprovals: t(
        'page:biddingSpot.loadError.source.crossDateApprovals',
      ),
      procurementNeed: t('page:biddingSpot.loadError.source.procurementNeed'),
      bidLimits: t('page:biddingSpot.loadError.source.bidLimits'),
      tradableAreas: t('page:biddingSpot.loadError.source.tradableAreas'),
      businessAreas: t('page:biddingSpot.loadError.source.businessAreas'),
      workflowCase: t('page:biddingSpot.loadError.source.workflowCase'),
      approvalFlow: t('page:biddingSpot.loadError.source.approvalFlow'),
    },
  )

  /** バナーの「再読み込み」= 画面が現在購読している query をまとめて再取得する。 */
  const handleReloadFailedQueries = (): void => {
    void queryClient.refetchQueries({ type: 'active' })
  }

  /** ブロック作成フォームを確定して Create RPC を呼ぶ。 */
  const handleBlockCreate = async (form: BlockBidSubmission): Promise<void> => {
    setBlockWriteError(undefined)
    // モーダルを開いた後に list / screen が不整合になった場合も、確定時に
    // 再検査して既存 DRAFT を見落とした重複 Create を防ぐ。
    if (isBlockStateUnresolved) {
      setBlockWriteError(
        translateBlockWriteError(new BlockDetailUnresolvedError(), t),
      )
      return
    }
    // 承認フロー設定が未解決の間は resolveCreateApprovalMode を呼べない
    // (2026-09-16 ユーザー決定 1-B)。 UI 側はブロック作成ボタンを非活性にして
    // 通常はここに到達しないが、 背景で取得が失敗に転じてから押下までの間隙を
    // 塞ぐ防御として中断する。
    if (isApprovalFlowUnavailable || approvalFlowScreenData === undefined) {
      setBlockWriteError(
        translateBlockWriteError(new ApprovalFlowUnresolvedError(), t),
      )
      return
    }
    const { groupType, primary, secondary } = formToCreateBlockInput(
      form,
      targetDateFilter,
    )
    try {
      await blockCreateAsync({
        targetDate: targetDateFilter,
        // 承認モードは対象エリア (primary + secondary の LINK/LOOP 相手側) の
        // 承認フロー設定から解決する (AC-BSP-015)。 全エリアが「承認なし」 または
        // 「自動」 (手作業で作成する札は自動でも承認なし相当。 #8297) で揃う時
        // だけ承認なしになり、 それ以外 (要承認を含む混在) は従来どおり
        // 要承認にとどまる (resolveCreateApprovalMode 参照)。
        approvalMode: resolveCreateApprovalMode({
          processes: approvalFlowScreenData.processes,
          businessProcess: BusinessProcess.SPOT_BID,
          areaCodes:
            secondary !== undefined
              ? [primary.areaCode, secondary.areaCode]
              : [primary.areaCode],
        }),
        // 発注主体はモーダルで選んだものをグループ全 leg へ適用する (issue #5366)。
        issuerType: form.issuerType,
        groupType,
        primary,
        secondary,
      })
      saveLastBidAreas(
        'spot',
        secondary !== undefined
          ? [primary.areaCode, secondary.areaCode]
          : [primary.areaCode],
      )
      showSuccessToast({
        title: t('page:biddingBlock.createModal.successMessage'),
      })
      setModalKind('none')
    } catch (err) {
      setBlockWriteError(translateBlockWriteError(err, t))
    }
  }

  /** 編集対象 group を確定して編集モーダルを開く。 */
  const handleBlockEdit = (groupId: string): void => {
    setBlockWriteError(undefined)
    const group = blockApprovalRequests.find((item) => item.id === groupId)
    if (group === undefined) return
    setBlockModalSource(group)
    setModalKind('block-create')
  }

  /** 編集フォームを確定して Update RPC を呼ぶ (= group 単位の全 leg 差し替え)。 */
  const handleBlockUpdate = async (form: BlockBidSubmission): Promise<void> => {
    const existing = editingBlockGroup
    if (existing === undefined || blockModalSource === undefined) {
      // 背景再取得で対象 group が消えていた (= 他ユーザーが削除) 場合は中断してエラー表示する。
      setBlockWriteError(t('page:biddingBlock.serverError.aborted'))
      return
    }
    setBlockWriteError(undefined)
    // 承認フロー設定が未解決の間は resolveCreateApprovalMode を呼べない
    // (2026-09-16 ユーザー決定 1-B、 handleBlockCreate と同じ理由)。
    if (isApprovalFlowUnavailable || approvalFlowScreenData === undefined) {
      setBlockWriteError(
        translateBlockWriteError(new ApprovalFlowUnresolvedError(), t),
      )
      return
    }
    try {
      const { primary, secondary } = formToUpdateBlockInput(
        form,
        targetDateFilter,
      )
      // 承認モードは Create 時だけでなく、 DRAFT の leg 構成が変わる Update の
      // たびに対象エリア群から再解決する (AC-BSP-015、 code review 指摘)。
      // 固定値 (existing.approvalMode) をそのまま使い回すと、 承認なしで作成
      // した DRAFT を要承認エリアへ編集しても承認なしのまま残り、 直接提出
      // ボタンで要承認エリアの leg まで承認を経ず提出できてしまう。
      const approvalMode = resolveCreateApprovalMode({
        processes: approvalFlowScreenData.processes,
        businessProcess: BusinessProcess.SPOT_BID,
        areaCodes:
          secondary !== undefined
            ? [primary.areaCode, secondary.areaCode]
            : [primary.areaCode],
      })
      await blockUpdateAsync({
        id: existing.id,
        approvalMode,
        issuerType: form.issuerType,
        primary,
        secondary,
        // 古い入力で他画面の更新を上書きしない (AC-BSP-124)。
        expectedUpdatedAt: blockModalSource.updatedAt,
      })
      showSuccessToast({
        title: t('page:biddingBlock.editModal.successMessage'),
      })
      setBlockModalSource(undefined)
      setModalKind('none')
    } catch (err) {
      setBlockWriteError(translateBlockWriteError(err, t))
    }
  }

  /** 編集/作成モーダルの submit を isEdit で Create/Update に振り分ける。 */
  const handleBlockModalSubmit = async (
    form: BlockBidSubmission,
  ): Promise<void> => {
    if (
      editingBlockGroupId === undefined &&
      memberBidDisabledReason !== undefined
    )
      return
    if (editingBlockGroupId !== undefined) {
      await handleBlockUpdate(form)
    } else {
      await handleBlockCreate(form)
    }
  }

  /** 編集モーダルのフッター削除ボタン: 編集モーダルを閉じて削除確認モーダルを開く。 */
  const handleRequestBlockDeleteFromEdit = (): void => {
    if (editingBlockGroupId === undefined) return
    setDeletingBlockGroupId(editingBlockGroupId)
    setModalKind('none')
  }

  /** 削除確認後に Delete RPC を呼ぶ (= group 単位の全 leg 一括削除)。 */
  const handleBlockDelete = async (groupId: string): Promise<void> => {
    const target = blockApprovalRequests.find((g) => g.id === groupId)
    if (target === undefined) {
      setBlockWriteError(t('page:biddingBlock.serverError.aborted'))
      return
    }
    setBlockWriteError(undefined)
    try {
      await blockDeleteAsync({
        id: target.id,
        expectedUpdatedAt: target.updatedAt,
      })
      showSuccessToast({
        title: t('page:biddingBlock.deleteModal.successMessage'),
      })
      setDeletingBlockGroupId(undefined)
      setBlockModalSource(undefined)
      setModalKind('none')
    } catch (err) {
      setBlockWriteError(translateBlockWriteError(err, t))
    }
  }

  // group 単位の取下 (AC-BSP-057)。 取消送信は backend で非同期に走るので、 成功しても
  // 即「取消」 にはならず取下処理中を経る (通常入札の handleConfirmWithdraw と同じ)。
  const handleConfirmBlockWithdraw = async (): Promise<void> => {
    const groupId = withdrawingBlockGroupId
    if (groupId === undefined) return
    // 確定済ワークフロー経由の取下を確定時にも再判定する (AC-BSP-058、
    // handleCancelBlockRequest と同じ fail-closed)。
    if (isReadonly) {
      showErrorToast({
        title: t('common:workflowLock.readonlyReason'),
      })
      setWithdrawingBlockGroupId(undefined)
      return
    }
    if (blockWithdrawingRef.current) return
    blockWithdrawingRef.current = true
    try {
      setBlockWriteError(undefined)
      const target = blockApprovalRequests.find((g) => g.id === groupId)
      if (target === undefined) {
        setBlockWriteError(t('page:biddingBlock.serverError.aborted'))
        return
      }
      try {
        await blockWithdrawAsync({
          id: target.id,
          expectedUpdatedAt: target.updatedAt,
        })
        setWithdrawingBlockGroupId(undefined)
        showSuccessToast({
          title: t('page:biddingSpot.withdrawConfirm.success'),
        })
      } catch (err) {
        setBlockWriteError(translateBlockWithdrawError(err, t))
      }
    } finally {
      blockWithdrawingRef.current = false
    }
  }

  /**
   * ブロック全削除 (issue #2091)。 押した時点で全 leg が一覧に表示中の下書きの group
   * (blockClearAllGroupIds) を group 単位で順次削除する (機能詳細 7、 #7758)。
   *
   * ブロック入札は「1 group = 1〜2 leg」で leg 単体の削除 API を持たない (= LINK / LOOP の
   * ペアは全 leg 一緒にしか消せない) ため、 group 単位の Delete RPC をループ適用する。
   * 非 DRAFT (PENDING/APPROVED/SUBMITTED) の group は backend が FailedPrecondition を
   * 返すので対象にしない (= 通常入札の handleClearAllBids と同じ DRAFT 限定方針)。
   */
  const handleBlockClearAll = async (): Promise<void> => {
    // 確認 modal に出した group が、 いまの下書きにすべて見つかり、 更新日時も開いた時点から
    // 動いていないこと。 modal を開いた後に group が更新・削除・提出された時は、 modal に出した
    // 件数と違う内容を消さないよう書き込まずに中断する (AC-BSP-124)。
    const targets = resolveBlockClearAllGroups(
      draftBlockGroups,
      blockClearAllUpdatedAt,
    )
    if (targets === undefined) {
      setBlockWriteError(t('page:biddingBlock.serverError.aborted'))
      return
    }
    setBlockWriteError(undefined)
    try {
      // 明細 Get が未解決の間は draftBlockGroups (list 由来) の中身を利用者が確認できて
      // いない。 全削除は不可逆なので、 handleBlockSubmitRequest と同じく fail-closed。
      if (isBlockStateUnresolved) {
        throw new BlockDetailUnresolvedError()
      }
      for (const group of targets) {
        // 楽観ロックは modal を開いた時点の更新日時で取る (modal に出した内容と同じ版を消す)。
        await blockDeleteAsync({
          id: group.id,
          expectedUpdatedAt: blockClearAllUpdatedAt.get(group.id),
        })
      }
      showSuccessToast({ title: t('page:biddingBlock.clearAll.success') })
      setModalKind('none')
    } catch (err) {
      // 途中で失敗した場合、 既に消えた group と残った group が混在する。 一覧を
      // invalidate して実状態を再取得し、 理由は modal 内にインライン表示する。
      void invalidateBlockApprovalRequestQueries(queryClient)
      setBlockWriteError(translateBlockWriteError(err, t))
    }
  }

  // 締切後の状態遷移 (承認申請 / JEPX 提出) を**確定時**にも塞ぐ。 主アクション
  // ボタンは `!isBeforeDeadline` で非活性にしているが、 締切前に開いた確認 modal を
  // 締切後に確定する経路が残る (= 本 PR が塞いだ「開いたまま締切を跨ぐ」経路と同型)。
  // AC-BSP-064 の「翌日受渡分の入札は作成・提出できません」は提出も対象。
  const isBlockedByDeadline = (): boolean => {
    if (isBeforeDeadline) return false
    showErrorToast({ title: t('page:biddingSpot.alert.gcPassedTitle') })
    return true
  }

  /**
   * 押した時点で一覧に表示中の下書きの group (blockSubmitScope) に、 REQUEST (承認申請) または
   * SUBMIT (直接提出) の遷移を順次適用する (AC-BSP-007)。 途中で失敗すると先の group だけ
   * 申請済みになる (一括の原子化は未対応)。 SUBMIT は対象 group 全件が承認なしの時だけ
   * renderBlockActionButton が出す経路で、 ここでモードは検査しない (backend の entity ガードが拒否する)。
   */
  const submitBlockDrafts = async (
    action: BiddingLifecycleAction.REQUEST | BiddingLifecycleAction.SUBMIT,
  ): Promise<void> => {
    if (isBlockedByDeadline()) return
    setBlockWriteError(undefined)
    try {
      // 明細 Get が未解決の間は draftBlockGroups (list 由来) に含まれる group の
      // 内容を利用者が確認できていない。 中身を確認できないまま承認申請の状態遷移を
      // 実行する事故を防ぐため fail-closed にする (= 通常入札の persistDraftBids と同じ理由)。
      if (isBlockStateUnresolved) {
        throw new BlockDetailUnresolvedError()
      }
      // modal を開いた後に group が更新・削除・提出された時は、 見せた内容と違う group を
      // 申請しないよう書き込まずに中断する (AC-BSP-124)。
      const expectedUpdatedAt = new Map(
        blockSubmitScope.map((g) => [g.id, g.updatedAt]),
      )
      const targets = resolveUnchangedDrafts(
        draftBlockGroups,
        expectedUpdatedAt,
      )
      if (targets === undefined) {
        setModalKind('none')
        void invalidateBlockApprovalRequestQueries(queryClient)
        showErrorToast({ title: t('page:biddingBlock.serverError.aborted') })
        return
      }
      for (const group of targets) {
        await blockTransitionAsync({
          id: group.id,
          action,
          expectedUpdatedAt: expectedUpdatedAt.get(group.id),
        })
      }
      showSuccessToast({
        title:
          action === BiddingLifecycleAction.REQUEST
            ? t('page:biddingBlock.approvalAction.requestSuccess')
            : t('page:biddingBlock.approvalAction.submitSuccess'),
      })
      setModalKind('none')
    } catch (err) {
      setModalKind('none')
      void invalidateBlockApprovalRequestQueries(queryClient)
      showErrorToast({ title: translateBlockWriteError(err, t) })
    }
  }

  const handleBlockSubmitRequest = (): Promise<void> =>
    submitBlockDrafts(BiddingLifecycleAction.REQUEST)

  const handleBlockSubmitDirect = (): Promise<void> =>
    submitBlockDrafts(BiddingLifecycleAction.SUBMIT)

  // PENDING → DRAFT (ブロック申請取消、 AC-BSP-055)。 取消単位は BlockBidGroup 単位
  // (通常入札の handleCancelRequest は request 単位、 spec 上の対照)。 対象は
  // BlockApprovalStatusBanner の「申請取消」から開いた PENDING group。 楽観ロック
  // 基準値は確定時に blockApprovalRequests (= 並列 Get 結果) から解決した最新の
  // updatedAt を使う (cancelingBlockGroupId は entity を固定しない id のみの保持 — modal を
  // 開いたまま invalidate/refetch が走っても stale な expectedUpdatedAt を握り続けない)。
  const handleCancelBlockRequest = async (): Promise<void> => {
    const targetId = cancelingBlockGroupId
    if (targetId === undefined) return
    // 確定済ワークフロー経由の状態変更を確定時にも再判定する (AC-BSP-058、
    // code review 指摘)。 導線の出し分け (banner の onCancelRequest) だけでは、
    // modal を開いたまま別ユーザーがケースを完了させた / ケース取得が失敗して
    // isReadonly に倒れた場合に CANCEL が飛んでしまう。 backend も case_id で
    // ケース状態を検証する (AC-BSP-058) が、 送る前に画面側でも止める (fail-closed)。
    if (isReadonly) {
      showErrorToast({
        title: t('common:workflowLock.readonlyReason'),
      })
      setCancelingBlockGroupId(undefined)
      return
    }
    // 明細 Get が未解決の group が残る間は、 その内容を利用者が確認できていない。
    // 中身を確認できないまま取消 (不可逆な状態遷移) を実行する事故を防ぐため
    // fail-closed にする (= handleBlockSubmitRequest と
    // 同じ理由。 対象 group 自体は Get 済みでも、 他 group の未解決を理由に一覧
    // 全体の整合性が取れていない可能性があるため揃える)。
    if (isBlockStateUnresolved) {
      showErrorToast({
        title: translateBlockWriteError(new BlockDetailUnresolvedError(), t),
      })
      setCancelingBlockGroupId(undefined)
      return
    }
    // 対象 group を確定時に pendingBlockApprovalRequests (= 並列 Get 済み・PENDING
    // 絞り込み済み) から解決する。 背景再取得で対象が PENDING でなくなっていた
    // (= 他ユーザーが承認/差戻し/取消済み等) 場合は中断する (= handleBlockUpdate /
    // handleBlockDelete の「他ユーザーが先に更新した」ガードと同じ理由)。
    const target = pendingBlockApprovalRequests.find((g) => g.id === targetId)
    if (target === undefined) {
      // 文言は「他のユーザーが先に更新しました」 (= handleBlockUpdate /
      // handleBlockDelete の同型ガードと同じ key)。 明細 Get は解決済み
      // (上の isBlockStateUnresolved を通過している) なので、 読み込み未完了を
      // 意味する detailUnresolved は実態と食い違う (AC-BSP-075、 code review 指摘)。
      showErrorToast({ title: t('page:biddingBlock.serverError.aborted') })
      setCancelingBlockGroupId(undefined)
      return
    }
    // 締切の再判定 (AC-BSP-055c)。 導線表示時の phase は targetDate 変更まで
    // 再計算されないため、 画面を開いたまま前日 10:00 を跨ぐと stale な 'open' の
    // まま確定できてしまう。 確定時に現在時刻で再判定して締切後は中断する
    // (通常入札の handleCancelRequest と同じガード。 backend の締切検証は
    // issue #3108 — 入るまでの UI 側ガード)。
    const phaseNow = calcSpotPhase(serverNow(), targetDate)
    if (!isSpotBeforeDeadline(phaseNow)) {
      showErrorToast({
        title: t('page:biddingBlock.approvalAction.cancelDeadlinePassed'),
      })
      setCancelingBlockGroupId(undefined)
      return
    }
    setBlockWriteError(undefined)
    try {
      await blockTransitionAsync({
        id: target.id,
        action: BiddingLifecycleAction.CANCEL,
        expectedUpdatedAt: target.updatedAt,
      })
      showSuccessToast({
        title: t('page:biddingBlock.approvalAction.cancelSuccess'),
      })
      setCancelingBlockGroupId(undefined)
    } catch (err) {
      // 楽観ロック競合 (Aborted) 等で失敗した場合、 一覧・明細を invalidate して
      // 実状態を再取得する (= handleBlockSubmitRequest の
      // catch と対称。 失敗後に画面が最新化されないまま古い状態で放置されるのを防ぐ)。
      void invalidateBlockApprovalRequestQueries(queryClient)
      showErrorToast({ title: translateBlockWriteError(err, t) })
    }
  }

  // REJECTED → DRAFT (ブロック差戻し後再編集、 AC-BSP-056)。 遷移単位は BlockBidGroup
  // 単位 (AC-BSP-059: グループ内の各レグは同じ承認状態を投影するため、 レグ単位の再編集は
  // 取り得ない)。 BlockApprovalStatusBanner の「再編集して再申請」から確認 modal を挟まず
  // 直接呼ぶ (= 下書きに戻すだけの可逆操作。 通常入札の handleReopenRequest と同じ判断)。
  // 差戻し理由・日時は backend が保持するため (block_bid_group.go Reopen)、 下書きに
  // 戻した後も前回差戻しコンテキストをバナーに表示できる。 楽観ロック基準値は Get 済み
  // full entity の updatedAt。
  //
  // 成功後はブロック入札タブに留まる (= 通常入札の handleReopenRequest は
  // setTab('normalBid') + 札一覧スクロールをするが、 ブロックは対象一覧がバナー直下に
  // あり、 かつ差戻しは group 単位で対象が自明なため絞り込み・スクロールは不要)。
  // 一覧・明細の再取得は useTransitionBiddingBlockApprovalState の invalidate に委譲。
  //
  // 明細 Get 未解決の fail-closed ガード (handleCancelBlockRequest / handleBlockSubmitRequest
  // 等) は張らない: REOPEN は下書きに戻すだけで可逆であり、 バナーに出ている group は
  // 既に Get 解決済み (= expectedUpdatedAt が正しい) 一方で、 他 group の明細 Get 失敗を
  // 理由に塞ぐと差戻しからの唯一の前進経路 (AC-BSP-056) を止めてしまう。
  const handleBlockReopenRequest = async (
    group: BiddingBlockApprovalRequest,
  ): Promise<void> => {
    // 確認 modal を挟まない = 連打が直接 RPC に届くため、 遷移中は無視する
    // (2 回目は REJECTED でなくなり FailedPrecondition になる)。
    if (isBlockTransitioning) return
    // 締切の再判定 (AC-BSP-056c)。 導線の出し分けに使う phase は分単位 tick
    // (useNowByMinute) 由来なので、 締切を跨いだ直後の 1 分間はまだ 'open' のまま
    // ボタンが出ている。 押下時に現在時刻で再判定して締切後は送らない (backend も
    // 同じ検証を持つが、 汎用の FailedPrecondition 文言ではなく理由を明示するため)。
    const phaseNow = calcSpotPhase(serverNow(), targetDate)
    if (!isSpotBeforeDeadline(phaseNow)) {
      showErrorToast({
        title: t('page:biddingBlock.approvalAction.reopenDeadlinePassed'),
      })
      return
    }
    setBlockWriteError(undefined)
    try {
      await blockTransitionAsync({
        id: group.id,
        action: BiddingLifecycleAction.REOPEN,
        expectedUpdatedAt: group.updatedAt,
      })
    } catch (err) {
      // 楽観ロック競合 (Aborted) 等での失敗時は一覧・明細を invalidate して実状態を
      // 再取得する (= handleCancelBlockRequest の catch と対称)。
      void invalidateBlockApprovalRequestQueries(queryClient)
      showErrorToast({ title: translateBlockWriteError(err, t) })
      return
    }
    showSuccessToast({
      title: t('page:biddingBlock.approvalAction.reopenSuccess'),
    })
  }

  // 送信中 → DRAFT (ブロックの「下書きに戻す」、 AC-BSP-117 / AC-BSP-129)。 送り直しを止めて同じ
  // グループを下書きに戻す。 遷移単位は BlockBidGroup (LINK / LOOP の 2 本をまとめて戻す)。
  // design の判断で確認 modal は挟まない (内容は失われず、 下書きから出し直せる)。 拒否
  // (入札締切・送信の最中・楽観ロック) は理由ごとの文言を出し、 一覧・明細を取り直す。
  const handleBlockReturnToDraftRequest = async (
    groupId: string,
  ): Promise<void> => {
    // 確認 modal を挟まない = 連打が直接 RPC に届くため、 遷移中は無視する。
    if (isBlockTransitioning) return
    const group = blockApprovalRequests.find((r) => r.id === groupId)
    if (group === undefined) return
    setBlockWriteError(undefined)
    try {
      await blockTransitionAsync({
        id: group.id,
        action: BiddingLifecycleAction.RETURN_TO_DRAFT,
        expectedUpdatedAt: group.updatedAt,
      })
    } catch (err) {
      void invalidateBlockApprovalRequestQueries(queryClient)
      showErrorToast({ title: translateBlockWriteError(err, t) })
      return
    }
    showSuccessToast({ title: t('page:biddingBlock.returnToDraft.success') })
  }

  // 追加系 (個別作成 / コマ枠作成) と一括系 (一括作成 / CSV 取込 / 下書き復元) の書込
  // (issue #3511 Phase 2)。 受渡日内の DRAFT 申請セット群 (draftRequests) を「1 つの
  // 下書き」 として扱い、 残す既存札 (keep) と追加する新規札 (added) から各申請セットの
  // 次状態を組み立てて永続化する。
  //
  // - 新規札の追加先は最新の DRAFT (PdM 論点1、 AC-079)。 DRAFT が 1 件も無ければ
  //   Create で新規申請セット (DRAFT) を起票する。
  // - keep で落ちた札は元の申請セットから除かれる (= 一括作成 replace は「画面に出ている
  //   全 DRAFT 札」 が置換対象。 先頭 DRAFT だけを置換すると別 DRAFT の同一コマ・同一方向の
  //   札が消えずに残り階段数超過を生む、 PR #5293 の残課題)。
  // - 札が 0 件になった申請セットは Delete RPC で消す (空配列の Update は proto の
  //   spot_bids min_items=1 で InvalidArgument になるため。 handleConfirmDelete の
  //   最後の 1 札削除と同じ判断)。
  // - 内容が変わらない申請セットには RPC を飛ばさない。
  // - 既存の非 DRAFT request (PENDING/APPROVED/SUBMITTED 等) には書き込まない
  //   (= 独立した新しい承認案件として扱う、 AC-082)。
  //
  // 一覧 / 明細 Get が未解決の間は Create/Update/Delete どれにも進まない
  // (= fail-closed)。 未解決分に本来の DRAFT が含まれるかもしれず「Create すべきか
  // (DRAFT 不在)」を確定できないのはもちろん、 明細 Get が失敗した request は
  // draftRequests の解決 (filter) から静かに脱落するため、 Update 分岐も「別の
  // request へすり替わって書き込む」誤操作になりうる (= REOPEN 中の request の
  // 明細 Get が失敗すると、 別の既存 DRAFT へ誤って書き込む退行、 AC-056)。
  // 呼び出し元の catch で detailUnresolved 文言を表示する。
  //
  // 複数の申請セットへ跨る書込は順次実行するため、 途中で失敗すると一部だけ反映された
  // 状態になりうる (= handleSubmitDirect の順次 SUBMIT と同じ割り切り。 1 RPC に束ねる
  // には BFF 側の一括 API が要る)。 呼び出し元がエラー表示し、 mutation hook の
  // invalidate で再取得された実状態を一覧に反映する。
  const persistDraftBids = async (params: {
    /** 既存の DRAFT 札のうち残すものの述語 (未指定 = 全て残す)。 */
    keep?: (bid: BiddingSpotBid) => boolean
    /** 最新の DRAFT へ追加する新規札 (未指定 = 追加なし)。 */
    added?: BiddingSpotBidInput[]
  }): Promise<void> => {
    const { keep, added = [] } = params
    if (isSpotStateUnresolved) {
      throw new SpotDetailUnresolvedError()
    }
    // 承認フロー設定が未解決の間は書き込まない (2026-09-16 ユーザー決定 1-B)。
    // 下の Create/Update 分岐が resolveCreateApprovalMode を呼ぶため、 呼ぶ前に
    // 中断する (= 読めていない設定を要承認と推測して確定させない)。 UI 側は
    // 個別作成・一括作成の入札追加ボタンを非活性にして通常はここに到達しない。
    if (isApprovalFlowUnavailable || approvalFlowScreenData === undefined) {
      throw new ApprovalFlowUnresolvedError()
    }
    // 1 つの申請セットに絞り込み表示していて、 その DRAFT が画面に出ていない間は下書きへ
    // 書き込まない。 draftRequests が空なのは DRAFT を隠している (または対象 DRAFT が
    // 消えた) からで、 このまま進むと下の Create 分岐が「既存 DRAFT を無視した重複申請
    // セット」 を起票する (draftRequests の不変条件を自分で破る)。 下書き復元
    // (keep: () => false) は「全置換」 が「新規作成」 に化けて札が二重になる。
    // 表示専用の絞り込みなので、 解除するまで書込は中断する。
    if (isFocusedDraftUnavailable) {
      throw new SpotFocusedRequestUnavailableError()
    }
    if (draftRequest === undefined) {
      if (added.length === 0) return
      // 承認モードは追加する札のエリア群の承認フロー設定から解決する
      // (AC-BSP-015)。 全エリアが「承認なし」 または「自動」 (手作業で作成する
      // 札は自動でも承認なし相当。 #8297) で揃う時だけ承認なしになり、 それ以外
      // (要承認を含む混在) は従来どおり要承認にとどまる
      // (resolveCreateApprovalMode 参照)。
      const approvalMode = resolveCreateApprovalMode({
        processes: approvalFlowScreenData.processes,
        businessProcess: BusinessProcess.SPOT_BID,
        areaCodes: added.map((b) => b.areaCode),
      })
      await createAsync(
        create(CreateBiddingSpotApprovalRequestRequestSchema, {
          targetDate: targetDateFilter,
          approvalMode,
          spotBids: added,
        }),
      )
      return
    }
    for (const req of draftRequests) {
      const kept = keep === undefined ? req.spotBids : req.spotBids.filter(keep)
      const isAppendTarget = req.id === draftRequest.id
      // 削除も追加も無い申請セットは触らない (= 無変化の全置換 Update を投げない)。
      if (
        kept.length === req.spotBids.length &&
        (!isAppendTarget || added.length === 0)
      ) {
        continue
      }
      const nextBids = [
        ...kept.map(spotBidToInput),
        ...(isAppendTarget ? added : []),
      ]
      if (nextBids.length === 0) {
        await deleteApprovalAsync({
          id: req.id,
          expectedUpdatedAt: req.updatedAt,
        })
        // AC-056: 絞り込み対象そのものを消したので絞り込みも解除する
        // (handleConfirmDelete の最後の 1 札削除経路と同じ理由)。
        clearFocusIfMatches(req.id)
      } else {
        // 承認モードは Create 時だけでなく、 DRAFT の札構成が変わる Update の
        // たびに対象エリア群 (= 更新後に残る全札のエリア) から再解決する
        // (AC-BSP-015、 code review 指摘)。 固定値 (req.approvalMode) をそのまま
        // 使い回すと、 承認なし・自動 (= isApprovalOptionalMode) で作成した DRAFT に
        // 要承認エリアの札を追加 / 要承認エリアの札だけを残す編集をしても承認なしの
        // まま残り、 直接提出ボタンで要承認エリアの札まで承認を経ず提出できてしまう。
        const approvalMode = resolveCreateApprovalMode({
          processes: approvalFlowScreenData.processes,
          businessProcess: BusinessProcess.SPOT_BID,
          areaCodes: nextBids.map((b) => b.areaCode),
        })
        await updateAsync(
          create(UpdateBiddingSpotApprovalRequestRequestSchema, {
            id: req.id,
            approvalMode,
            spotBids: nextBids,
            expectedUpdatedAt: req.updatedAt,
          }),
        )
      }
    }
  }

  // 行操作 (編集 / 削除) の書込先を、 その行が属する申請セットで解決する (issue #3511)。
  //
  // 追加系 (persistDraftBids) が「最新 DRAFT または新規 Create」 を解決するのに対し、
  // 行操作は必ず「その札を持っている申請セット」 が対象になる。 AC-BSP-055 (申請取消)
  // / AC-BSP-056 (差戻し後再編集) で同一受渡日に DRAFT が複数並存しうるため、
  // 先頭 DRAFT 固定のままだと 2 件目以降の DRAFT の札は編集が別セットへ飛び、
  // 削除は「変化のない全置換 Update」 が成功して札が残る silent no-op になる。
  //
  // 行アクションの表示条件 (SpotBidTable: 行の approvalState === DRAFT) はそのまま
  // 維持し、 表示された全 DRAFT 札が実際に操作できる状態へ揃える。
  //
  // DRAFT に限定するのは「書込先は必ず DRAFT 申請セット」 (AC-066 / AC-082b) の不変条件を
  // 書込側でも保つため (= 旧実装は draftRequest が構造上 DRAFT だったので自動的に成立して
  // いた)。 非 DRAFT の札に対しては undefined を返し、 呼び出し側が中断する。
  const resolveRequestForBid = (
    bid: SpotBidRow,
  ): BiddingSpotApprovalRequest | undefined =>
    spotApprovalRequests.find(
      (r) =>
        r.id === bid.approvalRequestId &&
        r.approvalState === BiddingApprovalState.DRAFT,
    )

  // 解決済みの申請セットに対する全置換 Update (= 行操作系の書込経路、 issue #3511)。
  // persistDraftBids と違い Create へフォールバックしない (= 対象セット以外へ書き込まない)。
  // 空配列は proto の spot_bids min_items=1 で弾かれるため、 0 件になる場合は
  // 呼び出し側が Delete RPC に振り分ける。
  const replaceRequestBids = async (
    target: BiddingSpotApprovalRequest,
    nextBids: BiddingSpotBidInput[],
  ): Promise<void> => {
    // 明細 Get が未解決の間は書き込まない (persistDraftBids と同じ fail-closed)。
    if (isSpotStateUnresolved) {
      throw new SpotDetailUnresolvedError()
    }
    // 承認フロー設定が未解決の間は resolveCreateApprovalMode を呼べない
    // (persistDraftBids / handleBlockUpdate と同じ理由、2026-09-16 ユーザー決定 1-B)。
    if (isApprovalFlowUnavailable || approvalFlowScreenData === undefined) {
      throw new ApprovalFlowUnresolvedError()
    }
    // 承認モードは Create/Update (persistDraftBids) だけでなく、 行操作 (編集・削除、
    // issue #3511) で対象セットのエリア構成が変わる場合にも、 更新後に残る全札の
    // エリア群から再解決する (AC-BSP-015、 code review 指摘と同型の抜け)。
    // target.approvalMode をそのまま使い回すと、 承認なしで作成した申請セットの
    // 札を IndividualBidModal の編集でエリア変更しても承認なしのまま残り、
    // 直接提出ボタンで要承認エリアの札まで承認を経ず提出できてしまう。
    const approvalMode = resolveCreateApprovalMode({
      processes: approvalFlowScreenData.processes,
      businessProcess: BusinessProcess.SPOT_BID,
      areaCodes: nextBids.map((b) => b.areaCode),
    })
    await updateAsync(
      create(UpdateBiddingSpotApprovalRequestRequestSchema, {
        id: target.id,
        approvalMode,
        spotBids: nextBids,
        // 楽観ロック基準値は解決した request のもの。
        expectedUpdatedAt: target.updatedAt,
      }),
    )
  }

  const handleSubmitBid = async (
    form: IndividualBidSubmission,
  ): Promise<void> => {
    setWriteError(undefined)
    // 編集は「その札が属する申請セット」 が母集合 (issue #3511)。 新規追加は従来どおり
    // 最新 DRAFT (無ければ Create) が母集合 (AC-079 / Des ①「追加先 = 最新の DRAFT」)。
    const editingRequest =
      editingBid !== undefined ? resolveRequestForBid(editingBid) : undefined
    const current = editingRequest?.spotBids ?? []
    // 編集対象の札が背景再取得で消えていた (= 他ユーザーが削除) 場合、 全置換 Update が
    // 黙って成功し編集が捨てられる。 それを防ぐため、 ここで中断してエラー表示する。
    if (
      editingBid !== undefined &&
      !current.some((b) => b.id === editingBid.id)
    ) {
      setWriteError(t('page:biddingSpot.serverError.aborted'))
      return
    }
    try {
      if (editingBid !== undefined && editingRequest !== undefined) {
        await replaceRequestBids(
          editingRequest,
          current.map((b) =>
            b.id === editingBid.id
              ? // 価格種別 (AC-BSP-004a) は編集フォーム自身が持つ ([[bidRowToForm]] が
                // 編集対象の札の値で初期化する)。 起票由来はモーダルが決めた値を送る。
                formToBidInput(form, form.origin)
              : spotBidToInput(b),
          ),
        )
      } else {
        // 再入札 (AC-BSP-081) は取消済みの札を編集対象にしない (新規 BidId)。
        // 価格種別 (指値 / 成行) はフォーム自身が持つ (AC-BSP-004a) ので、 再入札元の
        // 値はプリフィル (bidRowToForm) 経由で引き継がれ、 ここで別途渡さない。
        // 新規作成・複製・再入札はモーダルが手動起票で返す (AC-BSP-103)。
        await persistDraftBids({
          added: [formToBidInput(form, form.origin)],
        })
        saveLastBidAreas('spot', [form.areaCode])
      }
      setIndividualModalSource({ mode: 'create' })
      setModalKind('none')
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      // このモーダル (`individual-create`) は**新規追加と編集の両方**が開く
      // (行の編集ボタンは `mode: 'edit'`、 追加ボタン・複製・再入札は
      // `mode: 'create'` / `'duplicate'` / `'rebid'`)。 `editingBid` の有無がそのまま両者の区別なので、
      // それで文言を出し分ける。 なお編集開始後に元の札が消えていたケースは上の
      // membership チェックで中断するため、 ここには到達しない。
      showSuccessToast({
        title:
          editingBid !== undefined
            ? t('page:biddingSpot.bidEdit.success')
            : t('page:biddingSpot.bidCreate.success'),
      })
    } catch (err) {
      setWriteError(translateSpotWriteError(err, t))
    }
  }

  // コマ枠クリックの作成を最新の DRAFT request に append して永続化する
  // (無ければ persistDraftBids が Create で新規起票する、 AC-079)。
  const handleCreateBidFromSlot = async (
    values: SpotBidCreateSubmission,
  ): Promise<void> => {
    await persistDraftBids({ added: [spotCreateFormToBidInput(values)] })
    saveLastBidAreas('spot', [values.areaCode])
  }

  // 札 1 枚の取下 (AC-BSP-057)。 取消送信は backend 側で非同期に走るので、 成功
  // しても札が即「取消」 になるとは限らない (= 「取下処理中」 のことがある)。
  // トーストの文言もそれを前提にしている。
  const handleConfirmWithdraw = async (): Promise<void> => {
    if (withdrawingBid === undefined) return
    // 同一 tick の連打防止 (deletingRef と同型)。
    if (withdrawingRef.current) return
    withdrawingRef.current = true
    try {
      setWriteError(undefined)
      // 対象札が属する申請セットの updated_at が楽観ロックの基準。 背景再取得で
      // 申請セットが消えていた場合は、 基準値が作れないので中断する
      // (handleConfirmDelete の membership チェックと同型)。
      const owner = spotApprovalRequests.find(
        (r) => r.id === withdrawingBid.approvalRequestId,
      )
      if (owner === undefined) {
        setWriteError(t('page:biddingSpot.serverError.aborted'))
        return
      }
      try {
        await withdrawAsync({
          spotBidId: withdrawingBid.id,
          expectedUpdatedAt: owner.updatedAt,
        })
        setWithdrawingBid(undefined)
        showSuccessToast({
          title: t('page:biddingSpot.withdrawConfirm.success'),
        })
      } catch (err) {
        setWriteError(translateSpotWriteError(err, t))
      }
    } finally {
      withdrawingRef.current = false
    }
  }

  // 札 1 件の削除 (issue #3511)。 母集合は「その札が属する申請セット」 の札で、
  // 先頭 DRAFT 固定ではない (= 2 件目以降の DRAFT の札も確実に消える)。
  const handleConfirmDelete = async (): Promise<void> => {
    if (deletingBid === undefined) return
    // 同一 tick の連打防止。 isUpdating / isDeleting (React Query の isPending) は
    // 次の render でしか立たないので、 2 回目のクリックが 1 回目と同じ closure を
    // 見る間に同じ expectedUpdatedAt の RPC が 2 発飛ぶ (SpotBidCreateModal の
    // savingRef と同型)。
    if (deletingRef.current) return
    deletingRef.current = true
    try {
      setWriteError(undefined)
      const target = resolveRequestForBid(deletingBid)
      // 対象の申請セットを解決できない / 解決できてもその札を持っていない場合は、
      // 全置換 Update を投げても内容が変わらず「成功したのに札が残る」 silent no-op に
      // なる。 handleSubmitBid の membership チェックと同型に、 中断してエラー表示する。
      if (
        target === undefined ||
        !target.spotBids.some((b) => b.id === deletingBid.id)
      ) {
        setWriteError(t('page:biddingSpot.serverError.aborted'))
        return
      }
      try {
        const next = target.spotBids
          .filter((b) => b.id !== deletingBid.id)
          .map(spotBidToInput)
        if (next.length === 0) {
          // 最後の 1 札の削除 = 申請セットごと削除する。 空配列の Update は proto の
          // spot_bids min_items=1 で InvalidArgument になるため Delete RPC を使う
          // (= handleClearAllBids と同じ判断。 次の札追加で persistDraftBids が Create し直す)。
          if (isSpotStateUnresolved) {
            throw new SpotDetailUnresolvedError()
          }
          await deleteApprovalAsync({
            id: target.id,
            expectedUpdatedAt: target.updatedAt,
          })
          // AC-056: 絞り込み対象そのものを消したので絞り込みも解除する。 残すと
          // 存在しない request に絞られたまま一覧が 0 件になり、 一方 draftRequests は
          // 受渡日内の全 DRAFT へ戻るので、 ヘッダの承認申請が画面に
          // 出ていない申請セットを対象にする (handleSubmitDrafts と同型の解除)。
          clearFocusIfMatches(target.id)
        } else {
          await replaceRequestBids(target, next)
        }
        setDeletingBid(undefined)
        // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
        // 最後の 1 札 (申請セットごと Delete) と 2 札目以降 (残りで全置換 Update) の
        // どちらの経路も、 ユーザーから見れば「札を 1 件消した」 なので同じ文言。
        showSuccessToast({ title: t('page:biddingSpot.bidDelete.success') })
      } catch (err) {
        setWriteError(translateSpotWriteError(err, t))
      }
    } finally {
      deletingRef.current = false
    }
  }

  // 一括作成 (AC-005 / AC-005a、案B)。 エリア別・コマ別の need × カバー率% で生成した札を
  // 永続化する。 add = 既存 DRAFT に追加 (既存入札量を差し引いた不足分のみ。 差引の母集合は
  // 下の bulkAddExistingVolumes) / replace = 既存 DRAFT を生成札で置換する。 いずれも
  // persistDraftBids で書き込み、 置換と判定 (階段数・上限数量) の母集合は受渡日内の全 DRAFT
  // 申請セットの札 (draftBids / issue #3511 Phase 2)。 画面に出ている全 DRAFT 札を対象に
  // しないと、 別の申請セットの同一コマ・同一方向の札が残って階段数超過を生む。
  const draftExistingBids = useMemo<ExistingBidVolume[]>(
    () =>
      draftBids.map((b) => ({
        areaCode: b.areaCode,
        timeCode: b.timeCode,
        direction: b.direction,
        volumeMwh: Number(b.volumeMwh),
      })),
    [draftBids],
  )

  // 上限数量(コマ計) (AC-BSP-061 / AC-BSP-080) の一括作成インライン判定に使う、 既存の
  // 有効札 (入札中・承認申請中・承認済、 selectRevalidationBids) と、 有効なブロック入札の
  // 当該コマ数量 (blockVolumeTotalRows)。 draftExistingBids (下書き) とは別軸なので、
  // 一括作成の追加/置換モードに関わらず常に合算対象。
  const existingActiveBidVolumes = useMemo<ExistingBidVolume[]>(
    () =>
      activeVolumeTotalRows.map((b) => ({
        areaCode: b.areaCode,
        timeCode: b.timeCode,
        direction: b.direction,
        volumeMwh: Number(b.volumeMwh),
      })),
    [activeVolumeTotalRows],
  )

  // 一括作成の「追加」 で差し引く既存入札量 (AC-BSP-005a、 selectBulkAddExistingVolumes)。
  const bulkAddExistingVolumes = useMemo(
    () =>
      selectBulkAddExistingVolumes(
        bids,
        screenData?.blockApprovalRequests ?? [],
        !isBeforeDeadline,
      ),
    [bids, screenData, isBeforeDeadline],
  )

  // 一括作成の「対象エリア」 の候補と、 数量の決め方ごとの選択可否 (AC-BSP-005a:
  // 複数選択)。 page のエリア選択 (単一、 AC-BSP-016a) とは独立させる。 母集合
  // (`areaCodes`) と可否を 1 件にまとめて渡すのは、 別集合で渡すと母集合の外の
  // エリアが checkbox にも注記にも出ないまま生成対象から silent に落ちるため
  // (`models/bulkCreate.ts` の [[BulkAreaOption]])。
  //
  // - 目標カバー率モード: need を取得できた ∩ 取引可能。 need Map に無いエリアは
  //   buildBulkSpotBids が黙って skip する (= 札が silent に欠落) ため選択させない
  // - 数量指定モード (AC-BSP-005c): need を参照しないので need のゲートだけ外す。
  //   取引可能エリア (AC-BSP-005b の広域マスタ登録軸。 実体は発注主体区分ごとに
  //   受渡契約コードを解決できるエリア ∩ 送信先の受渡契約コード、
  //   `docs/adr/tradable-area-definition.md`) のゲートは外さない。
  //   `ListTradableAreas` を取得できていない間 (`tradableAreasData === undefined`)
  //   は `disabledAreas` が空になり全 9 エリアが通ってしまうので、 その間は 1 件も
  //   選べない状態にする。 数量指定モードはゲートが 1 つしかないぶん、 それを検証
  //   できない状態で候補を広げると受渡契約コードを解決できないエリアへ 48 件の
  //   DRAFT を作れてしまう (mock-app `GenerateBulkBidCandidates()` も fixed モードで
  //   `activeAreas.Contains(SingleArea)` を必須にしている)
  //
  // 数量指定側をカバー率側と同じ候補へ寄せても防御にならない。 backend は要求した
  // エリアごとに必ず series を返し (`GetProcurementNeed`)、 page は全 9 エリアを
  // 要求するので、 need さえ取得できていれば `needByArea` は need 0 のエリアも含む
  // 9 件になる。 `disabledAreas` が空の状態と重なるとカバー率側の候補自体が全 9
  // エリアになり、 防ぎたかった状態をそのまま返す (= `ListTradableAreas` だけが
  // 遅延・失敗している通常の組み合わせで no-op)。
  const bulkAreaOptions = useMemo<BulkAreaOption[]>(
    () =>
      JEPX_AREA_CODES.map((code) => {
        const outsideBusinessAreas = !areaCodes.includes(code)
        return {
          code,
          outsideBusinessAreas,
          selectableInCoverageMode:
            !outsideBusinessAreas &&
            (needByArea?.has(code) ?? false) &&
            !disabledAreas.has(code),
          selectableInFixedMode:
            !outsideBusinessAreas &&
            tradableAreasData !== undefined &&
            !disabledAreas.has(code),
        }
      }),
    [areaCodes, needByArea, disabledAreas, tradableAreasData],
  )

  // `previewBids` は **モーダルが Step2 で表示した生成札そのもの**。 ここで
  // `buildBulkSpotBids` を呼び直すと「確認した行」と「書き込む行」が別々の生成呼び出しに
  // なり、`docs/principles.md` §1「注文・入札の内容と確認画面の表示が 100% 一致」を
  // 「同じ props を両側へ配線している」convention でしか満たせない (レビュー指摘)。
  const handleBulkCreate = async (
    params: BulkCreateParams,
    previewBids: readonly BulkGeneratedBid[],
  ): Promise<void> => {
    setWriteError(undefined)
    const generated = previewBids.map((bid) =>
      // モーダルで選んだ発注主体 / 排出係数紐付けの判別コメント / 自由記述メモを
      // 全生成札へ同一適用 (AC-BSP-086、 発注主体は issue #5366)。
      // 起票由来は数量入力モードで決まる (目標カバー率モードの札だけが自動起票
      // = 上限数量(1札) の適用対象外、 AC-BSP-103 / issue #8165)。
      bulkGeneratedToBidInput(
        bid,
        params.issuerType,
        BULK_ORIGIN_BY_VOLUME_MODE[params.volumeMode],
        {
          memoJepx: params.memoJepx,
          memoFree: params.memoFree,
        },
      ),
    )
    if (generated.length === 0) return
    // replace: bulk 対象範囲 (方向 × コマ範囲 × 生成札のあるエリア) に一致する既存 DRAFT 札
    // のみを生成札で差し替え、 対象外 (別エリア / 範囲外コマ / 逆方向) の既存札は温存する
    // (= mock-app `ApplyBulkBid()` の IsReplaced と同一述語。全捨てすると対象外の下書きが
    // 無警告で消える)。 エリアは選択エリア (params.areaCodes) でなく生成札のエリア集合で
    // 判定する: 選択したが need 0 / 逆符号で候補が生成されないエリアの既存札まで消さない
    // (mock も candidates.Select(c => c.Area) で判定)。
    // add: 既存 DRAFT 札 + 生成札 (差引は buildBulkSpotBids 内で完了済み)。
    const slotFrom = Math.min(params.slotFrom, params.slotTo)
    const slotTo = Math.max(params.slotFrom, params.slotTo)
    const generatedAreas = new Set(generated.map((g) => g.areaCode))
    const isReplaced = (b: BiddingSpotBid): boolean =>
      b.direction === params.side &&
      b.timeCode >= slotFrom &&
      b.timeCode <= slotTo &&
      generatedAreas.has(b.areaCode)
    try {
      await persistDraftBids({
        keep: params.mode === 'replace' ? (b) => !isReplaced(b) : undefined,
        added: generated,
      })
      saveLastBidAreas('spot', params.areaCodes)
      showSuccessToast({
        title: t('page:biddingSpot.bulkCreate.success', {
          count: generated.length,
        }),
      })
      setModalKind('none')
    } catch (err) {
      setWriteError(translateSpotWriteError(err, t))
    }
  }

  // 入札全削除 (機能詳細7)。 削除するのは、 押した時点で入札一覧に表示中の下書きの札だけ
  // (clearAllTargets。 表示していない札は消さない。 #7758 / PdM 判断 2026-09-25)。
  // 非 DRAFT request の札は対象外 (AC-066/AC-082)。
  //
  // 申請セットは札を複数持つので、 表示中の札が一部だけの申請セットは表示外の札を残して
  // 更新し、 札が 0 件になる申請セットは Delete RPC で消す (空配列 Update は proto の
  // spot_bids min_items=1 で弾かれる。 次の札追加で persistDraftBids が Create し直す)。
  // 手順の組み立ては [[planClearAll]]。 絞り込み中 (AC-056 REOPEN) は draftRequests が
  // その 1 セットに限られる。
  const handleClearAllBids = async (): Promise<void> => {
    const steps = planClearAll(
      draftRequests,
      new Set(clearAllTargets.map((b) => b.id)),
    )
    // 確認 modal に出した札が、 いまの下書きの申請セットにすべて見つかること。 modal を開いた後に
    // 申請セットが更新された (札の ID は更新で振り直される) 時は、 件数と違う札を消さないよう
    // 書き込まずに中断する (AC-BSP-124)。
    if (countClearedBids(steps) !== clearAllTargets.length) {
      setWriteError(t('page:biddingSpot.serverError.aborted'))
      return
    }
    if (steps.length === 0) return
    setWriteError(undefined)
    try {
      // 明細 Get が未解決の間は draftRequests が DRAFT の全量を指しているか
      // 確定できない (= 明細 Get が失敗した request は filter から静かに脱落する。
      // persistDraftBids と同じ理由で fail-closed にする。 全削除は不可逆操作のため、
      // 誤った申請セットを削除する / 消し残す事故を防ぐ)。
      if (isSpotStateUnresolved) {
        throw new SpotDetailUnresolvedError()
      }
      // 表示外の札を残す更新は承認モードを承認フロー設定から解決し直す (replaceRequestBids)。
      // 設定が未解決でも先に並んだ申請セットの削除が確定しないよう、 書き込みの前に検査する。
      if (
        steps.some((step) => step.kind === 'keep') &&
        (isApprovalFlowUnavailable || approvalFlowScreenData === undefined)
      ) {
        throw new ApprovalFlowUnresolvedError()
      }
      for (const step of steps) {
        if (step.kind === 'delete') {
          await deleteApprovalAsync({
            id: step.request.id,
            expectedUpdatedAt: step.request.updatedAt,
          })
          // AC-056: 絞り込み対象を消したら絞り込みも解除する (handleConfirmDelete の
          // 最後の 1 札削除経路と同じ理由)。
          clearFocusIfMatches(step.request.id)
        } else {
          await replaceRequestBids(
            step.request,
            step.remaining.map(spotBidToInput),
          )
        }
      }
      showSuccessToast({
        title: t('page:biddingSpot.clearAll.success'),
      })
      setModalKind('none')
    } catch (err) {
      setWriteError(translateSpotWriteError(err, t))
    }
  }

  // DRAFT → PENDING (承認申請) / DRAFT → SUBMITTED (直接提出)。 承認申請確認 modal の「申請する」
  // と直接提出確認 modal の「提出する」から呼ぶ。 対象は主ボタンを押した時点で確定した計画
  // (submitScope.plan = 一覧に表示中の下書きの札、 AC-BSP-007、 #7758) で、 申請セットごとの
  // target をそのまま 1 つの RPC に載せる (action は backend が承認モードから導き、 どれか 1 つでも
  // 拒否されたら何も変わらない。 AC-BSP-015)。
  const handleSubmitDrafts = async (): Promise<void> => {
    if (isBlockedByDeadline()) return
    if (submitScope === undefined || submitScope.plan.entries.length === 0) {
      return
    }
    setWriteError(undefined)
    try {
      // 明細 Get が未解決の間は draftRequests が DRAFT の全量を指しているか
      // 確定できない (= persistDraftBids と同じ理由。 不可逆な状態遷移のため fail-closed)。
      if (isSpotStateUnresolved) {
        throw new SpotDetailUnresolvedError()
      }
      // modal を開いた後に他のユーザーが申請セットを更新した時は、 見せた札と違う札を
      // 申請しないよう書き込まずに中断する (AC-BSP-124)。
      if (!isDraftSubmitPlanCurrent(submitScope.plan, draftRequests)) {
        closeModal()
        void invalidateSpotApprovalRequestQueries(queryClient)
        showErrorToast({ title: t('page:biddingSpot.serverError.aborted') })
        return
      }
      const targets = draftSubmitTargets(submitScope.plan)
      await transitionDraftsAsync({ targets })
      for (const target of targets) {
        // AC-056: 再編集対象を承認申請・提出したら絞り込みも解除する。
        clearFocusIfMatches(target.id)
      }
      showSuccessToast({
        title:
          draftKind === 'direct'
            ? t('page:biddingSpot.approvalBanner.submitSuccess')
            : t('page:biddingSpot.approvalBanner.requestSuccess'),
      })
      closeModal()
    } catch (err) {
      showErrorToast({ title: translateSpotDraftSubmitError(err, t) })
    }
  }

  // 送信中 → DRAFT (通常入札の「下書きに戻す」、 AC-BSP-117 / AC-BSP-129)。 送り直しを止めて
  // 同じ申請セットを下書きに戻す。 遷移単位は申請セット (押した札の申請セット全体が戻る)。
  // ブロックと同じく確認 modal は挟まない (内容は失われず、 下書きから出し直せる)。 拒否
  // (入札締切・送信の最中・楽観ロック) は理由ごとの文言を出し、 一覧・明細を取り直す。
  const handleReturnToDraftRequest = async (row: SpotBidRow): Promise<void> => {
    // 確認 modal を挟まない = 連打が直接 RPC に届くため、 遷移中は無視する。
    if (isTransitioning) return
    const request = spotApprovalRequests.find(
      (r) => r.id === row.approvalRequestId,
    )
    if (request === undefined) return
    setWriteError(undefined)
    try {
      await transitionAsync({
        id: request.id,
        action: BiddingLifecycleAction.RETURN_TO_DRAFT,
        expectedUpdatedAt: request.updatedAt,
      })
    } catch (err) {
      void invalidateSpotApprovalRequestQueries(queryClient)
      showErrorToast({ title: translateSpotWriteError(err, t) })
      return
    }
    showSuccessToast({ title: t('page:biddingSpot.returnToDraft.success') })
  }

  // PENDING → DRAFT (申請取消)。 申請取消確認 modal の「申請取消」から呼ぶ。
  // 対象は取消確認を開いた PENDING request (AC-055、 AC-082 で複数並存しうるため
  // request 単位で取消)。
  // 成功時の一覧再取得は useTransitionBiddingSpotApprovalState の invalidate に委譲。
  //
  // 同一 organization のメンバーなら誰でも他人の申請を取り消せる。 これは spec どおり
  // (AC-BSP-055「本 AC は本人検証を行わない」、 本人限定の AC-055b / 056b は
  // spot-bidding.md Discussion の決定 B (issue #4087) で削除済み)。 操作者
  // (`operated_by`) は BFF → backend まで配線済みで、 申請取消・再編集の操作ログに
  // 記録される (AC-BSP-055 / 056)。 申請 (REQUEST) には記録しないので `requested_by`
  // は空のまま。
  const handleCancelRequest = async (): Promise<void> => {
    const targetId = cancelingRequestId
    if (targetId === undefined) return
    // 締切の再判定 (AC-BSP-055c)。導線表示時の phase は targetDate 変更まで
    // 再計算されないため、画面を開いたまま前日 10:00 を跨ぐと stale な 'open' の
    // まま確定できてしまう。確定時に現在時刻で再判定して締切後は中断する
    // (backend の締切検証は issue #3108 — 入るまでの UI 側ガード)。
    const phaseNow = calcSpotPhase(serverNow(), targetDate)
    if (!isSpotBeforeDeadline(phaseNow)) {
      showErrorToast({
        title: t('page:biddingSpot.approvalBanner.cancelDeadlinePassed'),
      })
      setCancelingRequestId(undefined)
      return
    }
    // 対象を確定時に pendingRequests から解決する。 背景再取得で対象が PENDING で
    // なくなっていた (= 他ユーザーが承認/差戻し/取消済み) 場合は中断する
    // (= ブロック側 handleCancelBlockRequest と同じガード)。
    const target = pendingRequests.find((r) => r.id === targetId)
    if (target === undefined) {
      showErrorToast({ title: t('page:biddingSpot.serverError.aborted') })
      setCancelingRequestId(undefined)
      return
    }
    setWriteError(undefined)
    try {
      await transitionAsync({
        id: target.id,
        action: BiddingLifecycleAction.CANCEL,
        expectedUpdatedAt: target.updatedAt,
      })
      showSuccessToast({
        title: t('page:biddingSpot.approvalBanner.cancelSuccess'),
      })
      setCancelingRequestId(undefined)
    } catch (err) {
      showErrorToast({ title: translateSpotWriteError(err, t) })
    }
  }

  // REJECTED → DRAFT (差戻し後再編集、 AC-056)。 差戻しバナーの「再編集して再申請」 から
  // 直接呼ぶ (= 確認 modal は挟まない。 下書きに戻すだけの可逆操作のため、 design
  // `spot.html:10787` の判断に従う)。 対象は押された REJECTED request (AC-082 で複数
  // 並存しうるため request 単位)。 差戻し理由・日時は backend で保持されるため、 再申請時に
  // 前回差戻しコンテキストを表示できる (spec 367-368)。
  // 成功後は入札一覧を対象申請セットの札だけに絞り込んでスクロールする (= 複数の差戻しが
  // 並存する時に直す対象を札そのもので示す)。 一覧再取得は
  // useTransitionBiddingSpotApprovalState の invalidate に委譲。
  const handleReopenRequest = async (
    target: BiddingSpotApprovalRequest,
  ): Promise<void> => {
    // 確認 modal を挟まない = 連打が直接 RPC に届くため、 遷移中は無視する
    // (2 回目は REJECTED でなくなり FailedPrecondition になる)。
    if (isTransitioning) return
    // 締切の再判定 (AC-BSP-056c)。 導線の出し分けに使う phase は分単位 tick
    // (useNowByMinute) 由来なので、 締切を跨いだ直後の 1 分間はまだ 'open' のまま
    // ボタンが出ている。 押下時に現在時刻で再判定して締切後は送らない (backend も
    // 同じ検証を持つが、 汎用の FailedPrecondition 文言ではなく理由を明示するため)。
    // ブロック入札の handleBlockReopenRequest と同じガード。
    const phaseNow = calcSpotPhase(serverNow(), targetDate)
    if (!isSpotBeforeDeadline(phaseNow)) {
      showErrorToast({
        title: t('page:biddingSpot.approvalBanner.reopenDeadlinePassed'),
      })
      return
    }
    setWriteError(undefined)
    try {
      await transitionAsync({
        id: target.id,
        action: BiddingLifecycleAction.REOPEN,
        expectedUpdatedAt: target.updatedAt,
      })
    } catch (err) {
      showErrorToast({ title: translateSpotWriteError(err, t) })
      return
    }
    showSuccessToast({
      title: t('page:biddingSpot.approvalBanner.reopenSuccess'),
    })
    // 承認状態バナーは通常入札タブ内にあるので、押下時点で既に normalBid のはず
    // (#6449)。 それでも setTab を残すのは、他の導線が増えた時に一覧へ着地させる
    // 保険として (= no-op で害が無い)。
    setTab('normalBid')
    if (!canFocusRequest && !reopensIntoListedDraft(target)) return
    setFocusedRequest({ id: target.id, origin: 'reopen' })
    // スクロールは直接呼ばず one-shot フラグに委譲する (= 一覧の絞り込み反映後に
    // 1 回だけ走らせるため)。
    setPendingBidListScroll(true)
  }

  // AC-BSP-094: 承認前に対象の札そのものを確認する導線 (= 承認状態バナーの
  // 「対象の札を確認」)。 入札一覧をこの申請セットの札だけに絞り込んでスクロールする。
  // RPC は伴わない純粋な表示操作なので締切・isReadonly のガードは掛けない。
  //
  // 確認 modal のサマリ (件数・売買数量・違反明細) だけでは個々の札を確認できず、
  // かつ AC-BSP-079 / AC-BSP-082 が同一受渡日での申請セット並存を許すため、
  // バナーだけでは「どの札の承認案件か」 が判別できない (2026-08-26 Des 判断
  // 「承認者が札一覧を確認できることはマスト要件」。 差戻し後再編集 AC-056 と同じ解)。
  const handleReviewRequest = (target: BiddingSpotApprovalRequest): void => {
    setTab('normalBid')
    setFocusedRequest({ id: target.id, origin: 'approvalReview' })
    setPendingBidListScroll(true)
  }

  const closeModal = (): void => {
    setWriteError(undefined)
    setBlockWriteError(undefined)
    setIndividualModalSource({ mode: 'create' })
    setBlockModalSource(undefined)
    setCancelingRequestId(undefined)
    setCancelingBlockGroupId(undefined)
    setModalKind('none')
  }

  if (isLoading && data === undefined) {
    return <RegionLoading minHeight="200px" isLoading />
  }

  // 承認申請・提出の主ボタンが、 絞り込みを解除しても押せない状態か (入札締切後・承認フロー設定を
  // 読めていない・ワークフローが読み取り専用)。 「絞り込みを解除して」 の案内はこの状態では
  // 出さない (AC-BSP-007。 理由が 2 つ重なる時は絞り込みの解除では解消しない側だけを出す)。
  const isDraftSubmitBlocked =
    isReadonly || !isBeforeDeadline || isApprovalFlowUnavailable

  // ========== 承認状態別メインアクションボタン ==========
  // AC-082 で同一受渡日に複数の承認案件が並存しうるため、 優先順位で出し分ける
  // (renderBlockActionButton と同型のパターン、 AC-082c):
  // 1. DRAFT request があれば (または request が 1 件も無ければ) 「承認申請」 → 一覧に表示中の
  //    下書きの札だけを承認申請する (AC-BSP-007)。 引数 visibleDraftBids は NormalBidTab が
  //    渡す表示中の下書きで、 ボタンの件数も、 押した時に確定する対象もこの札。
  //    表示中の下書きが 0 件なら非活性 (他に下書きがある時は理由を出す)。
  //    ただし全 target の遷移後の承認モード ([[planDraftSubmit]]) が「承認なし」 または
  //    「自動」 で揃っている場合は「提出」 → 承認を経ず提出する (AC-BSP-015)。
  // 2. DRAFT 無し PENDING があれば 主ボタンは無効。「承認申請中」表示のみ。
  // 3. DRAFT / PENDING が無く承認済があれば、 提出の操作口は出さず、 送信の進み具合で
  //    非活性の「JEPX 送信未完了」 / 「JEPX 送信済」 ボタンと押せない理由を出す
  //    (design `spot.html` の承認済。 resolveApprovedSendKind)。 最終承認は同じ呼び出しで
  //    JEPX 送信まで進むので (AC-BSP-094 / spot-bidding.md §ビジネスルール)、 承認済は承認軸
  //    SUBMITTED + 送信軸で表れ、 利用者が操作する機会は無い。 承認軸 APPROVED が残るのは
  //    切替前に作られた札だけ。
  // 4. それ以外 (REJECTED / 承認を経ない SUBMITTED / FAILED / CANCELLED のみ) はボタン非表示。
  const spotApprovedSendKind = resolveApprovedSendKind(spotBannerRequests)
  // 押せない理由の文末 (「承認申請できません」 / 「提出できません」) は、 承認済の依頼の札の
  // エリアの承認フロー設定の現在値で決める (design `spot.html` の `__spotSubmitReason`)。
  // 承認の後に設定が変わった受渡日では、 札に保存した承認モードと食い違う。
  const approvedActionVerbOf = (
    areaCodes: readonly AreaCode[],
  ): ApprovedActionVerb =>
    resolveApprovedActionVerb(
      spotApprovalModesOf(areaCodes)?.map((group) => group.mode),
    )
  const approvedReasonKeys = {
    request: {
      sent: 'page:biddingSpot.approvalAction.approvedSentRequestReason',
      unsent: 'page:biddingSpot.approvalAction.approvedUnsentRequestReason',
    },
    submit: {
      sent: 'page:biddingSpot.approvalAction.approvedSentReason',
      unsent: 'page:biddingSpot.approvalAction.approvedUnsentReason',
    },
  } as const
  const renderApprovedSendButton = (
    kind: ApprovedSendKind,
    verb: ApprovedActionVerb,
  ): React.ReactElement => (
    <ButtonTooltip content={t(approvedReasonKeys[verb][kind])}>
      <PrimaryButton disabled size="md">
        {t(
          kind === 'sent'
            ? 'page:biddingSpot.approvalAction.approvedSent'
            : 'page:biddingSpot.approvalAction.approvedUnsent',
        )}
      </PrimaryButton>
    </ButtonTooltip>
  )
  const renderMainActionButton = (
    visibleDraftBids: readonly SpotBidRow[],
  ): React.ReactElement | null => {
    if (draftRequests.length > 0 || spotApprovalRequests.length === 0) {
      // 表示中の下書きがある時のみボタンを活性化する。
      // workflow-back-bar-readonly AC-15: ワークフローケース完了済み/削除済みでも disabled。
      const hasVisibleDraft = visibleDraftBids.length > 0
      // 設定を読めていない間・札が見つからない間 (一覧と下書きが 1 描画だけ食い違う間) は
      // undefined で、 ボタンを塞ぐ (設定が未解決の間は元から塞ぐ)。
      const plan =
        resolveSpotDraftMode === undefined
          ? undefined
          : planDraftSubmit(
              draftRequests,
              new Set(visibleDraftBids.map((b) => b.id)),
              resolveSpotDraftMode,
            )
      // 対象が 0 件 (request 自体が 1 件も無い場合を含む) は承認申請ボタンのまま
      // (何もない状態で提出ボタンを出さない)。
      const isDirectSubmit =
        plan !== undefined && draftSubmitKind(plan) === 'direct'
      // 他に下書きがあるのに表示中の下書きが 0 件の時の押せない理由 (AC-BSP-007)。
      // 絞り込みを解除しても押せない状態 (isDraftSubmitBlocked) では出さない。 開いた入口の
      // 不可 (readonlyReason) は画面内の操作で解消しないので先に単独で出す。
      const noVisibleDraftReason =
        draftBids.length > 0 && !hasVisibleDraft && !isDraftSubmitBlocked
          ? t('page:biddingSpot.approvalAction.noVisibleDraft')
          : undefined
      return (
        <ButtonTooltip
          content={readonlyReason ?? noVisibleDraftReason}
          focusable
        >
          <PrimaryButton
            // 締切後は承認申請 / 直接提出も塞ぐ (AC-BSP-064「作成・提出できません」。
            // 申請・提出してもJEPX へ届かない札を流さない)。 承認フロー設定が
            // 未解決の間も塞ぐ (2026-09-16 ユーザー決定 1-B)。
            disabled={
              !hasVisibleDraft || isDraftSubmitBlocked || plan === undefined
            }
            onClick={() => {
              if (plan === undefined) return
              setSubmitScope({
                plan,
                bids: [...visibleDraftBids],
                // 絞り込みで表示していない下書きの札の数 (確認 modal の件数の下に 1 行で出す)。
                hiddenCount: draftBids.length - visibleDraftBids.length,
              })
              setModalKind(
                isDirectSubmit ? 'direct-submit-confirm' : 'request-confirm',
              )
            }}
            // 見出し帯の高さ (LIST_HEADER_HEIGHT) は中身 40px 前提なので size を明示する
            // (同じ帯の「入札追加」も size="md")。 未指定で DS 既定に任せると、 既定が
            // 40px でない時に帯を突き破って左の見出しと下線がズレる (2026-08-31 指摘)。
            size="md"
          >
            {t(
              isDirectSubmit
                ? 'page:biddingSpot.approvalAction.submitWithCount'
                : 'page:biddingSpot.approvalAction.requestWithCount',
              { count: visibleDraftBids.length },
            )}
          </PrimaryButton>
        </ButtonTooltip>
      )
    }

    if (countedPendingRequests.length > 0) {
      return (
        <SecondaryButton disabled size="md">
          {t('page:biddingSpot.approvalAction.pending')}
        </SecondaryButton>
      )
    }

    if (spotApprovedSendKind !== undefined) {
      return renderApprovedSendButton(
        spotApprovedSendKind,
        approvedActionVerbOf(
          selectApprovedRequests(spotBannerRequests).flatMap((request) =>
            request.spotBids.map((bid) => bid.areaCode),
          ),
        ),
      )
    }

    // REJECTED / 承認を経ない SUBMITTED / FAILED / CANCELLED はボタン非表示
    return null
  }

  // ========== ブロック入札 承認状態別メインアクションボタン ==========
  // ブロック入札は group (承認依頼) 単位で状態を持つため、 通常入札の単一 approvalState と
  // 異なり複数 group が異なる状態を取り得る。 優先順位:
  // 1. DRAFT が 1 件でもあれば 承認申請 ボタン (= 未申請分を先に片付けさせる、 旧実装と同じく
  //    draftBlockCount===0 でも他が 0 件なら disabled 表示のまま出す)。
  //    対象は一覧に表示中の下書きの group (AC-BSP-007)。 引数 visibleDraftRows は BlockBidTab が
  //    渡す表示中の下書きの group の全 leg で、 ボタンの件数も、 押した時に確定する対象も
  //    この group。 0 件なら非活性 (他に下書きがある時は理由を出す)。
  //    ただし対象の全 DRAFT group の承認モードが「承認なし」 または「自動」 で揃って
  //    いる場合は「提出」 → 承認を経ず対象の group へ順次 SUBMIT に切り替える
  //    (AC-BSP-015)。 明細 Get が未解決の間は表示中かを判定できないので、 一覧 (list) の
  //    下書きの group を対象にして承認申請フローにとどめる (実行は fail-closed で止まる)。
  // 2. DRAFT 0 件で PENDING が 1 件でもあれば 承認申請中 の無効ボタン。
  // 3. DRAFT/PENDING が 0 件で承認済があれば、 提出の操作口は出さず、 送信の進み具合で
  //    非活性の「JEPX 送信未完了」 / 「JEPX 送信済」 ボタンと押せない理由を出す
  //    (design `spot.html` の承認済。 resolveApprovedSendKind)。 承認は同じ呼び出しで
  //    JEPX 送信まで進むので (AC-BSP-094 / spot-bidding.md §ビジネスルール)、 承認済は承認軸
  //    SUBMITTED + 送信軸で表れ、 利用者が操作する機会は無い。 承認軸 APPROVED が残るのは
  //    切替前に作られた札だけ。
  // ボタン高は同じ帯に並ぶ「ブロック作成」(BlockBidTab の TertiaryButton
  // size="md") と揃える。 8080 も `.btn { height: 40px }` が基本で、 提出は
  // `btn-primary` = 40px (2026-09-01 デザイナー指摘「左のボタンと同じ高さに」)。
  // 通常入札側の renderMainActionButton は既に md で揃えてある。
  const renderBlockActionButton = (
    visibleDraftRows: readonly BlockBidRow[],
  ): React.ReactElement | null => {
    if (
      draftBlockCount > 0 ||
      (pendingBlockCount === 0 && blockApprovedSendKind === undefined)
    ) {
      // 対象の group の集合は 1 つ。 ボタンの件数・承認フローの行・実行時の照合の基準は
      // すべてこの集合から導く。
      const targetGroupIds = [
        ...new Set(
          isBlockStateUnresolved
            ? draftBlockGroups.map((g) => g.id)
            : visibleDraftRows.map((r) => r.groupId),
        ),
      ]
      const targetGroups: BlockSubmitTarget[] = targetGroupIds.map((id) => ({
        id,
        updatedAt: draftBlockGroups.find((g) => g.id === id)?.updatedAt,
        rows: visibleDraftRows.filter((r) => r.groupId === id),
      }))
      const hasVisibleDraft = targetGroups.length > 0
      const isDirectSubmit =
        !isBlockStateUnresolved &&
        isDirectSubmitMode(
          draftBlockApprovalRequests
            .filter((g) => targetGroupIds.includes(g.id))
            .map((g) => g.approvalMode),
        )
      // 通常入札と同じ押せない理由 (readonlyReason を先に単独で出す。 絞り込みを解除しても
      // 押せない状態では出さない)。
      const noVisibleDraftReason =
        draftBlockCount > 0 && !hasVisibleDraft && !isDraftSubmitBlocked
          ? t('page:biddingBlock.approvalAction.noVisibleDraft')
          : undefined
      return (
        <ButtonTooltip
          content={readonlyReason ?? noVisibleDraftReason}
          focusable
        >
          <PrimaryButton
            // 承認フロー設定が未解決の間も塞ぐ (2026-09-16 ユーザー決定 1-B)。
            disabled={!hasVisibleDraft || isDraftSubmitBlocked}
            onClick={() => {
              setBlockSubmitScope(targetGroups)
              setBlockSubmitHiddenCount(
                isBlockStateUnresolved
                  ? 0
                  : draftBlockCount - targetGroups.length,
              )
              setModalKind(
                isDirectSubmit
                  ? 'block-direct-submit-confirm'
                  : 'block-submit-confirm',
              )
            }}
            size="md"
          >
            {/* 提出できる下書きが 0 件の時は件数を付けない (design/bidding/spot.html の主ボタン)。 */}
            {draftBlockCount === 0
              ? t('page:biddingBlock.submitButton.labelEmpty')
              : t(
                  isDirectSubmit
                    ? 'page:biddingBlock.approvalAction.submitWithCount'
                    : 'page:biddingBlock.submitButton.label',
                  { count: targetGroups.length },
                )}
          </PrimaryButton>
        </ButtonTooltip>
      )
    }

    if (pendingBlockCount > 0) {
      return (
        <SecondaryButton disabled size="md">
          {t('page:biddingBlock.approvalAction.pending')}
        </SecondaryButton>
      )
    }

    // ここへ来るのは承認済がある時だけ (上の条件が、 承認済も PENDING も無ければ先頭で返す)。
    return blockApprovedSendKind === undefined
      ? null
      : renderApprovedSendButton(
          blockApprovedSendKind,
          approvedActionVerbOf(
            selectApprovedRequests(blockBannerRequests).flatMap((group) =>
              [group.primary?.areaCode, group.secondary?.areaCode].filter(
                (area): area is AreaCode => area !== undefined,
              ),
            ),
          ),
        )
  }

  // ステータス帯 ([[SpotStatusHub]]) を出すか。 中身が 1 つも無い時に器だけ残ると、
  // 親 column flex の `gap` を 1 つぶん (12px) 消費してしまうので、 5 本の表示条件を
  // ここで合流させる。 承認待ちバナーだけは自前で `null` を返す作りなので、
  // 同じ判定を `crossDatePendingKeys` から共有する (issue #6671)。
  // 統合 warning に何を並べるか。 **component を関数として呼ばない** —
  // `CrossDatePendingApprovalBody` は `useTranslation` を持つので、 関数呼び出しに
  // すると hooks が親の系列に混ざる。 表示可否は純関数の述語で判定する。
  const hasCrossDatePending =
    crossDatePendingKeys(allApprovalRequests, targetDate, now).length > 0
  const showTradableAreasWarning =
    tradableAreasData !== undefined && tradableAreas.length === 0
  const hasWarningItem = hasCrossDatePending || showTradableAreasWarning

  // ステータス帯 (DS Alert) に残るのは統合 warning (行ごとの導線を持つ箇条書き) だけ。
  // 取得失敗・締切後・発注主体不明・未取込は Compact でページ最上部の `notices` に出す。
  const hasStatusBanner = hasWarningItem
  const hasMemberBidWarning =
    memberBidDisabledReason !== undefined && memberBidDisabledReason !== ''
  const hasPageNotices =
    failedDataLabels.length > 0 ||
    hasMemberBidWarning ||
    !isBeforeDeadline ||
    unknownIssuerDeliveryContractCodes.length > 0 ||
    hasUnimportedBids

  return (
    <WorkflowPageLayout
      breadcrumbItems={[
        { label: t('page:biddingSpot.breadcrumb.bidding') },
        { label: t('page:biddingSpot.breadcrumb.page'), bold: true },
      ]}
      // ページ最上部の Compact 通知 (alert-placement.md §2 の順: 取得失敗 → 締切 → 要対応)。
      // 取得失敗は画面の数字そのものが欠けるので最初に置く。
      notices={
        hasPageNotices ? (
          <>
            {/* 取得失敗: 失敗したデータ源だけを名指しし、 画面は描画を継続する。
                「再読み込み」 は右端の Tertiary sm (alert-placement.md §4)。 */}
            {failedDataLabels.length > 0 && (
              <CompactAlert
                actions={
                  <TertiaryButton onClick={handleReloadFailedQueries} size="sm">
                    {t('page:biddingSpot.loadError.reload')}
                  </TertiaryButton>
                }
                description={t('page:biddingSpot.loadError.body', {
                  sources: failedDataLabels.join(
                    t('page:biddingSpot.loadError.sourceSeparator'),
                  ),
                })}
                role="alert"
                status="error"
                title={t('page:biddingSpot.loadError.title')}
              />
            )}
            {/* JEPX 会員情報が未登録で入札できない (AC-IJM-013)。業務が止まる通知 */}
            {hasMemberBidWarning && (
              <JepxMemberBidWarning reason={memberBidDisabledReason} />
            )}
            {/* 入札締切後 (AC-BSP-064)。 どのタブでも「今ヘッダーに出ている受渡日の締切が
                過ぎている」 という page-level の状態を示す (issue #5349)。 */}
            {!isBeforeDeadline && (
              <CompactAlert
                description={t('page:biddingSpot.alert.gcPassedBody')}
                status="info"
                title={t('page:biddingSpot.alert.gcPassedTitle')}
              />
            )}
            <UnknownIssuerBidBanner
              deliveryContractCodes={unknownIssuerDeliveryContractCodes}
            />
            <UnimportedBidBanner
              compact
              hasUnimportedBids={hasUnimportedBids}
            />
          </>
        ) : null
      }
    >
      {/* design `spot.html:739` 「全 page scroll に変更」 を踏襲: 内容が自然に伸び、
          このコンテンツ領域 (SideMenuLayoutContentLayout が viewport 高に bound) を
          縦スクロールさせる。 design が最下部に sticky で固定していた札一覧の合算値
          フッターは spec AC-BSP-003 で「表示しない」 に反転済み (issue #7573) のため、
          この領域に viewport 下端へ貼り付く要素は無い。 */}
      <Flex flexDirection="column" gap={3} overflowY="auto" height="100%">
        {/* AC-BSP-131: 受渡日・発注主体・エリアをページタブより上の 1 本のバーにまとめる。
            空状態でも日付と区分は残し、切り替えて解消できるようにする。 */}
        <SpotPageHeader
          areaFilter={
            !showsAreaEmptyPanel && !hasNoAreaCandidate && !isResultTab ? (
              <AreaFilterBar
                areaCodes={JEPX_AREA_CODES}
                availabilityOf={filterAreaAvailabilityOf}
                missingMasterOf={filterMissingMasterOf}
                onChange={handleAreaFilterChange}
                selectedAreas={rawSelectedAreas}
              />
            ) : undefined
          }
          isDateLocked={isDateLocked}
          isTargetDateConfirmed={lockedTargetDate !== undefined}
          issuerSelector={
            // 約定結果・履歴は発注主体区分と共通エリアで絞らない (AC-BSP-131)。
            isResultTab ? undefined : (
              <PageIssuerSelector
                onChange={setPageIssuerType}
                options={pageIssuerOptions}
                selected={pageIssuerSelected}
              />
            )
          }
          onSelectToday={() => setManualTargetDate(todayMidnight())}
          onSelectTomorrow={() => setManualTargetDate(tomorrowMidnight())}
          onTargetDateChange={setManualTargetDate}
          phase={phase}
          targetDate={targetDate}
        />
        {/* 自動入札が発注しなかったコマの警告バナー (AC-BSP-120、 design `_mountAutoBidSkipBanner`)。
            共通ツールバーの直下 (ステータス帯より上) に置き、 どのタブでも出す。 受渡日・発注主体・エリアの
            選択では絞らない。 この高さ拘束された
            column flex でも押し潰されないよう、 部品が `flexShrink={0}` を持つ。 空なら部品は何も描画しない。 */}
        <AutoBidNotOrderedBanner
          market={AutoBidNotOrderedMarket.SPOT}
          notices={autoBidNotices}
        />
        {/* タブより上のステータス帯 (design `spot.html` の `#spot-status-hub`)。
            複数の alert を素の `gap={3}` で積むと、 複数出た時に上部だけで縦を
            使い切り、 下の入札一覧までスクロールしないと届かない (issue #6671)。
            器ごとの出し分けは `hasStatusBanner` が持つ。 */}
        {hasStatusBanner && (
          <SpotStatusHub>
            {/* 他の受渡日の承認待ち (#5538 / AC-BSP-095)。 受渡日を切り替えなくても
              気づけるよう、 ページ最上部の通知 (`notices`。 alert-placement.md §1・§2:
              業務が止まる → 対応が要る) の下、 本文の先頭 = 選択中の受渡日のバナーより
              上に置く (Des 裁定 2026-08-18、 10/27 に alert-placement.md を正として更新)。
              選択中の受渡日ぶんは通常入札タブ内の
              ApprovalStatusBanner / ブロック入札タブ内の BlockApprovalStatusBanner が
              出すので、 こちらは他の日だけを扱う。 タブ外に残すのは、 データ源が受渡日で
              絞らない spot list (= 全受渡日ぶん) で、 選択中受渡日のタブ内容と紐づかない
              横断情報だから。 ただし横断しているのは受渡日であって入札種別ではない:
              数えているのは通常入札の承認申請だけで、 ブロック入札
              (useListBiddingBlockApprovalRequests) は targetDate で絞って取得している
              ため他の受渡日ぶんを持たない。 種別横断にするには block list も全受渡日ぶん
              取得して合流させる必要があり、 本 PR のスコープ外。 */}
            {/* [統合 2026-08-31] 同じ status の警告は 1 枚にまとめ、 中を箇条書きにする
              (デザイナー指示「alert が多すぎる。 同じ色でマージできない? alert 内で
              リスト表示で整理するのはどう?」)。 2 件は「他の受渡日の承認待ち」 と
              「取引可能エリアが無い」 で行き先が違うため、 DS `Alert` の `actions`
              スロット (1 つしか無い) には載せられない。 **各項目の行内に導線を置く**。

              title は付けない。 「確認が必要な項目が N 件あります」 のような見出しは
              件数以上の情報を持たず、 1 行増やして本文を下げるだけになる。
              `design/CLAUDE.md`「1 行で完結する alert は本文を title で書く」 の裏返しで、
              複数項目を持つ alert は本文 (リスト) 側に情報を置く。 */}
            {hasWarningItem && (
              <Alert flexShrink={0} status="warning">
                <Box
                  as="ul"
                  flex={1}
                  margin={0}
                  listStyleType="disc"
                  paddingInlineStart={5}
                >
                  {hasCrossDatePending && (
                    <Box as="li">
                      <CrossDatePendingApprovalBody
                        allRequests={allApprovalRequests}
                        currentTargetDate={targetDate}
                        isDateLocked={isDateLocked}
                        now={now}
                        onSelectDate={setManualTargetDate}
                      />
                    </Box>
                  )}
                  {showTradableAreasWarning && (
                    <Box as="li" marginTop={hasCrossDatePending ? 3 : 0}>
                      <TradableAreasBannerBody />
                    </Box>
                  )}
                </Box>
              </Alert>
            )}
          </SpotStatusHub>
        )}

        {/* 入札中の札の一括取下の結果 (AC-BSP-116。 design `spot.html` の
            `#spot-bulk-withdraw-reject-alert`)。 通常入札 + ブロック入札の合算の、 画面全体の結果なので、
            手数料単価の取得失敗の Alert と同じ帯 (タブバーより上) に 1 つだけ置く。 どのタブから実行しても、
            タブを切り替えても同じ位置に出る。 結果が無い時は何も描画しない。 */}
        <BulkWithdrawResultBanner
          onDismiss={bulkWithdraw.dismissOutcome}
          onReload={bulkWithdraw.reloadLists}
          outcome={bulkWithdraw.outcome}
        />

        {/* 入札 CSV の取込完了の案内 (AC-SPOT-024)。 UI 辞書 alert-placement: 「知っておけばよい」
            ものなので、 締切・状態の Alert (上のステータス帯) より下に置く。 高さ拘束された
            column flex なので `flexShrink={0}` (BidCsvImportDoneAlert が持つ)。 */}
        {csvImportNotice !== undefined && (
          <BidCsvImportDoneAlert
            isDateLocked={isDateLocked}
            notice={csvImportNotice}
            onDismiss={dismissCsvImportNotice}
            onSelectDate={setManualTargetDate}
            selectedDate={targetDate}
          />
        )}

        {/* 承認状態別メインアクションボタンは design `spot.html` 同様、 通常入札タブの
            入札一覧ヘッダ action group 内 (NormalBidTab headerActionSlot) に配置する
            (= 旧 右上ヘッダ配置を撤去)。 */}

        {/* 入札後ポジションチャート。 **タブバーの上に常設**する
            (design `bidding/spot.html:1021-1027` / 2026-09-02 Des 判断 / issue #6596 論点3)。
            通常入札とブロック入札の合算が 1 つのポジションであることを構造で示すため、
            タブの中ではなく外に置く。 経緯と根拠は部品側の docstring に集約。

            約定結果・オペレーション履歴タブ以外の全タブで出す (#6596 Q1)。 この 2 タブは共通
            ツールバーのエリアを使わないので出さない (AC-BSP-131、 design #10970)。 それ以外のタブでは
            「どのタブを見ていても同じポジション」 という採用理由と整合する。

            スクロールしても画面上部に固定しない (2026-10-08 デザイナー判断。 旧: sticky)。
            下との余白はこの Flex の `gap={3}` に委ねる (marginBottom は不要)。

            業務エリアに JEPX 対象が 1 つも無いときはチャートを出さない (Des 判断 #7502
            「東京などを暗黙に選択した一覧・チャート・エリア別合計は表示しません」)。
            候補が空だと選択中エリアも空になり、 単一エリア未選択のガイドだけの
            チャート枠が残ってしまう。 */}
        {!hidesBidLists && !isResultTab && (
          <>
            {/* `hasBids` は **チャートが描いている slot から判定する**
              (2026-09-07 レビュー指摘)。 以前は `bids` / `blockBidRows`
              (= 全エリアの札) の件数で見ていたが、 チャートは
              `sharedPositionSlots` = **選択中エリアだけ**を描くので、
              他エリアに札があると札ゼロのエリアでも「入札後ポジション」と
              出ていた。 */}
            <SpotPositionChartPanel
              area={singleSelectedArea}
              hasBids={sharedPositionSlots.some(
                (slot) => slot.buyMw !== 0 || slot.sellMw !== 0,
              )}
              kind={tab === 'blockBid' ? 'block' : 'normal'}
              needAbsent={chartNeedAbsent}
              needUnavailable={chartNeedUnavailable}
              onSlotClick={chartSlotClick}
              slots={sharedPositionSlots}
            />
          </>
        )}

        <TabsRoot
          lazyMount
          onValueChange={(details: { value: string }) => {
            const next = SPOT_TABS.find((d) => d.key === details.value)
            if (next !== undefined) setTab(next.key)
          }}
          value={tab}
        >
          {/* paddingBottom 1px は選択タブの下線を削らせないための実寸補正
              (理由は WorkflowCases FilterBar の同指定のコメント参照)。 */}
          <TabsList overflowX="auto" overflowY="hidden" paddingBottom="1px">
            {SPOT_TABS.map((def) => {
              const isPhase2 = def.phase === 2
              const label = t(def.labelKey)
              const inner = (
                <HStack as="span" gap={1}>
                  {def.icon === 'gear' && (
                    <FaGearRegular
                      boxSize={iconSize.labeledButton}
                      aria-hidden="true"
                    />
                  )}
                  <Box as="span">{label}</Box>
                </HStack>
              )
              const tabElement = isPhase2 ? (
                <Tooltip
                  key={def.key}
                  content={t('page:biddingSpot.placeholder.phase2Body')}
                  placement="top"
                  showArrow
                >
                  <TabsTrigger
                    fontSize={tabTriggerFontSize.token}
                    aria-disabled
                    disabled
                    value={def.key}
                  >
                    {inner}
                  </TabsTrigger>
                </Tooltip>
              ) : (
                <TabsTrigger
                  key={def.key}
                  fontSize={tabTriggerFontSize.token}
                  value={def.key}
                >
                  {inner}
                </TabsTrigger>
              )
              if (def.trailingDivider === true) {
                return (
                  <Fragment key={def.key}>
                    {tabElement}
                    <Box
                      as="span"
                      alignSelf="center"
                      display="inline-block"
                      width="1px"
                      height="20px"
                      aria-hidden="true"
                      backgroundColor="Border.lowEmphasis"
                      marginX={1}
                    />
                  </Fragment>
                )
              }
              return tabElement
            })}
          </TabsList>
          {/* 候補 0 件が確定したら共通ツールバーのエリア欄を消して空状態パネルを出す
              (Des 判断 #7502「空のエリアラベルだけを残さず、 選択対象がないことを上記案内で
              説明します」)。 radio が 1 つも無い帯を残すと、 なぜ選べないのかも次に何を
              すればよいのかも読めない。 パネルを出さない状態 (区分 0 件) でも、
              業務エリアに JEPX 対象が 1 つも無ければ帯だけを出さない。

              パネルは**タブ枠の直下に 1 枚だけ**置き、 タブごとには置かない。 DS
              `TabsContent` は非選択でも DOM に残り `hidden` を持たない
              (`data-state="closed"` + CSS のみ) ので、 タブごとに同じ案内を置くと支援技術
              には同じ見出しとリンクが同時に複数見える。

              約定結果・オペレーション履歴タブは区分を表示せず区分で絞らない (AC-BSP-131) ので、
              選んだ区分で判定した案内を出すと、 どの区分の話か読めず、 区分を切り替えて解消もできない。
              このタブでは、 中身を出さない時 (業務エリアに JEPX 対象が無い) だけ、 組織全体で判定した
              区分名の無い案内を出す (何も出さないと空白のタブになり、 理由が読めないため)。 */}
          {(isResultTab
            ? hasNoAreaCandidate && orgWideAreaEmptyState.showsAreaEmptyPanel
            : showsAreaEmptyPanel) && (
            <Box marginTop={3}>
              <BusinessAreaEmptyState
                isBroadAreaMasterEmpty={
                  isResultTab
                    ? orgWideAreaEmptyState.isBroadAreaMasterEmpty
                    : isBroadAreaMasterEmpty
                }
                issuerLabel={
                  isResultTab || scopeIssuer === undefined
                    ? undefined
                    : issuerTypeLabel(scopeIssuer.issuerType)
                }
                missingMasters={missingMastersInScope(
                  isResultTab ? undefined : scopeIssuer,
                  tradableAreasData?.registrationIssues ?? [],
                  JEPX_AREA_CODES,
                  tradableAreasData?.registeredIssuerTypes ?? [],
                )}
                orderableAreaCodes={
                  isResultTab
                    ? orgWideAreaEmptyState.orderableAreas
                    : orderableJepxAreas
                }
                unknownIssuerBidCount={spotUnknownIssuerBidCount({
                  tab,
                  bids,
                  blockRows: blockBidRows,
                  businessAreas: areaCodes,
                })}
              />
            </Box>
          )}
          {/* エリア依存コンテンツは候補 0 件が確定したら出さない (Des 判断 #7502
              「東京などを暗黙に選択した一覧・チャート・エリア別合計は表示しません」
              「『0件の入札』『0 MW』 等の実データがゼロであるような表現もしません」
              「候補がない操作には同じ理由と個人設定への導線を示します」)。 候補が空だと
              選択中エリアも空になり、 ここで止めないと 0 件の一覧と、 候補が無いのに
              押せる作成導線が出る。

              オペレーション履歴・約定結果タブは共通ツールバーのエリアを受け取らない (全エリアの
              一覧) が、 これも出さない。 `conventions.md`「業務エリアフィルタの原則」 は
              一覧・選択 UI から業務エリア外を隠すことを求めており、 業務エリアに JEPX 対象が
              1 つも無ければ残る行が無いため。

              承認状態バナーとまとめて承認は、 組織全体の申請セットが対象でエリア依存ではない
              ので出したままにする。 AC-BSP-105 が空状態に差し替えるのは入札一覧とチャートで、
              バナーまで隠すと、 既定の発注主体区分で選べるエリアが 0 件の承認者が、 区分を
              切り替えるまで承認・差戻しの入口 (AC-BSP-094) に辿り着けない。 */}
          <TabsContent paddingX={0} value="normalBid">
            <VStack
              align="stretch"
              gap={3}
              minHeight={hidesAreaContent ? undefined : '600px'}
            >
              {/* 承認状態バナー (= PENDING / APPROVED / REJECTED / SUBMITTED で表示。
                  AC-082: 複数 request が並存する場合は非 DRAFT の各 request を縦積み表示、 暫定実装)。
                  design `spot.html` の `#spot-status-hub` (= `#spot-approval-status` の
                  実体置き場) は `#tab-create` 直下なので、 ここも通常入札タブ内に置く
                  (= ブロック入札の BlockApprovalStatusBanner と同じ扱い)。
                  タブ外に置くと ブロック入札タブで 通常入札 / ブロック入札 の
                  「承認申請中（承認待ち）」バナーが同一文言のまま 2 つ縦積みになり、
                  どちらの承認案件か判別できなくなる (#6449)。 */}
              {/* まとめて承認の入口と直近の結果 (AC-BSP-118)。 承認状態バナーの上に置く。 */}
              {/* 案件ごとの事前チェック・承認者の識別・締切等の可否 policy は単件の hook
                      (spotDecision) と同じものを案件ごとに当て、 承認可否の最終判定は backend が行う。
                      開閉などの state はこの component の中に閉じ込める (ページを描き直さない)。 */}
              {/* 完了済みワークフローから開いた読み取り専用 (isReadonly) では入口ごと出さない。
                  承認はできないので「まとめて承認できます」 と案内しても事実に反し、
                  disabled のボタンだけが残る (alert-placement §3)。 */}
              {!isReadonly && (
                <SpotBulkApproval
                  bids={bids}
                  decision={spotDecision}
                  isBlockVolumeUnresolved={isBlockStateUnresolved}
                  onDecided={clearFocusIfMatches}
                  pendingRequests={countedPendingRequests}
                  targetDate={targetDate}
                  volumeTotalRows={activeVolumeTotalRows}
                />
              )}
              <NeedsReviewBanner count={needsReviewCount} />
              <ApprovalStatusBanner
                // 受渡日ごとに作り直す。 banner は畳んだ分の展開状態を内部 state で
                // 持つが、 それは**その受渡日の案件集合に対する状態**なので日をまたいで
                // 持ち越す意味が無い (2026-08-25 codex 指摘)。
                //
                // 現状は受渡日を変えると list → 明細 Get の再取得中に
                // `spotApprovalRequests` が一旦空になり、 banner が null を返して
                // unmount されるため state は結果的にリセットされる。 つまりこの key は
                // **今日ただちに直る不具合があるわけではない**。 その暗黙の再マウントに
                // 依存しているのが危うく (例: `placeholderData: keepPreviousData` を
                // 足した瞬間に展開したままになる)、 意図を明示して固定するために置く。
                key={targetDate.toISOString()}
                approvalRequests={spotBannerRequests}
                // 導線の出し分け (以降の on* prop は chakra props-order lint により
                // アルファベット順に並ぶため、 条件をまとめてここに書く):
                //
                // - 入札締切後 (AC-BSP-067): 承認待ち・差戻しのバナーは
                //   resolveApprovalBannerHeading が先に除くので、 下の導線は締切後には
                //   描画されない (承認 / 差戻し / 申請取消 / 再編集 / 対象の札を確認)。
                // - 申請取消 (AC-BSP-055) / 再編集 (AC-BSP-056): 完了済みワークフロー
                //   (isReadonly、ケース状態未取得の fail-closed 含む) では出さない。
                //   `!isBeforeDeadline` (AC-BSP-055c / AC-BSP-056c) は保険で、 締切後の
                //   バナーは上のとおり先に除かれる。 締切後の CANCEL は spec 上 Forbidden で、
                //   backend 側の締切検証は issue #3108 (全 action 一括)。 REOPEN も締切後は
                //   再申請 → JEPX 提出まで到達できない (backend も締切超過で拒否する)。
                //   ブロック入札タブの出し分けと同条件。
                // - 承認 / 差戻し (AC-BSP-094): 完了済みワークフロー (AC-BSP-058) /
                //   明細未解決 の間は decisionDisabledReason で disabled + tooltip にする。
                //   入札締切 (AC-BSP-099) の成分が効くのは、 まとめて承認と、 モーダルを
                //   開いたまま締切を跨いだ時の確定時の再判定だけ (バナーは締切後に出ない)。
                //   承認者でない人・申請者本人には無効化せず注意を出す (AC-BSP-098)。
                //   可否 policy は useSpotApprovalDecision に 1 か所で持ち、 確定時にも
                //   サーバー由来の現在時刻で再判定する。 承認申請中のまま締切を迎えた入札は
                //   「失効」 へ遷移する。 承認案件一覧画面が Phase 1 時点で動作保証対象外の
                //   ため、 本画面が承認/差戻しの唯一の経路 (spec spot-bidding.md:171)。
                // - 対象の札を確認: RPC を伴わない表示操作。 承認待ちのバナーにあるので、
                //   バナーと一緒に締切後は出ない。
                // - 対象の札を確認: 絞り込んでも一覧が出ない間 (canFocusRequest が false) は
                //   出さない。 押しても絞り込む以外の効果が無いため。 再編集は下書きに戻す効果が
                //   あるので出し、 絞り込みだけを止める (handleReopenRequest)。
                decisionDisabledReason={spotDecision.decisionDisabledReason}
                onApproveRequest={spotDecision.openApprove}
                onCancelRequest={
                  isReadonly || !isBeforeDeadline
                    ? undefined
                    : (request) => {
                        setCancelingRequestId(request.id)
                      }
                }
                onRejectRequest={spotDecision.openReject}
                onReopenRequest={
                  isReadonly || !isBeforeDeadline
                    ? undefined
                    : (request) => {
                        void handleReopenRequest(request)
                      }
                }
                onReviewRequest={
                  canFocusRequest ? handleReviewRequest : undefined
                }
                phase={phase}
                targetDate={targetDate}
              />
              {!hidesBidLists && (
                <NormalBidTab
                  approvalFlowUnavailable={isApprovalFlowUnavailable}
                  areaCodes={areaCodes}
                  bidListRef={bidListRef}
                  bids={bids}
                  bulkWithdraw={bulkWithdraw.menu}
                  chartArea={singleSelectedArea}
                  disabledAreas={disabledAreas}
                  focusedApprovalRequestId={focusedRequestId}
                  focusedApprovalRequestNote={
                    focusedRequest === undefined
                      ? undefined
                      : t(FOCUSED_REQUEST_NOTE_KEY[focusedRequest.origin])
                  }
                  hasDraftRequest={draftRequests.length > 0}
                  headerActionSlot={renderMainActionButton}
                  isFocusedDraftUnavailable={isFocusedDraftUnavailable}
                  isReadonly={isReadonly}
                  isSidebarOpen={isSidebarOpen}
                  issuerScopeType={issuerScopeType}
                  listFilter={listFilter}
                  memberBidDisabledReason={memberBidDisabledReason}
                  needAbsent={chartNeedAbsent}
                  needByKoma={needByKoma}
                  onClearAreaFilter={() => handleAreaFilterChange(new Set())}
                  onClearFocusedApprovalRequest={
                    handleClearFocusedApprovalRequest
                  }
                  onCreateBid={handleCreateBidFromSlot}
                  onDeleteBid={(row) => setDeletingBid(row)}
                  // 取消済みの札からの再入札 (AC-BSP-081)。 取消札には触れず、
                  // その内容を初期値にした個別作成モーダルを**新規作成**として
                  // 開くだけ (= 自動連結しない)。 保存すると新しい BidId の札が
                  // 最新 DRAFT に追加される。
                  onDuplicateBid={
                    canDuplicateBid ? handleDuplicateBid : undefined
                  }
                  onEditBid={(row) => {
                    openIndividualBidModal({ mode: 'edit', bid: row })
                  }}
                  onListFilterChange={setListFilter}
                  // 行クリックはどの札でも開く (#5356 Des 裁定 2026-08-18
                  // 「全行開く・下書き以外は表示専用」)。 表示専用かどうかは
                  // モーダル側へ `readOnly` で渡す。
                  onOpenBid={(row) => {
                    openIndividualBidModal({ mode: 'edit', bid: row })
                  }}
                  // 行クリックはどの札でも開く (#5356 Des 裁定 2026-08-18
                  // 「全行開く・下書き以外は表示専用」)。 表示専用かどうかは
                  // モーダル側へ `readOnly` で渡す。
                  onOpenBidCreateModal={() => {
                    openIndividualBidModal({ mode: 'create' })
                  }}
                  onOpenBulkCreateModal={() => setModalKind('bulk-create')}
                  onOpenClearAllModal={(targets, excludedCount) => {
                    setClearAllTargets(targets)
                    setClearAllExcludedCount(excludedCount)
                    setModalKind('clear-all')
                  }}
                  onRebidBid={(row) => {
                    setWriteError(undefined)
                    openIndividualBidModal({ mode: 'rebid', source: row })
                  }}
                  onRegisterSlotClick={(fn) => {
                    slotClickRef.current = fn
                  }}
                  onReturnToDraft={(row) => {
                    void handleReturnToDraftRequest(row)
                  }}
                  onTabListFiltersChange={patchNormalTabListFilters}
                  onToggleSidebar={handleToggleSidebar}
                  onWithdrawBid={(row) => {
                    setWriteError(undefined)
                    setWithdrawingBid(row)
                  }}
                  pageIssuerType={pageIssuerType}
                  phase={phase}
                  rawSelectedAreas={rawSelectedAreas}
                  selectedAreas={selectedAreas}
                  tabListFilters={{
                    inversionOnly: listFilters.inversionOnly,
                    limitOnly: listFilters.limitOnly,
                  }}
                  targetDate={targetDate}
                  unknownIssuerUnselectedAreas={
                    rawSelectedAreas.size === 0 ? areaCodes : []
                  }
                />
              )}
            </VStack>
          </TabsContent>
          {/* トランシェ構成 タブ: P3 で対応 (トランシェ機能未確定のため一旦非表示) */}
          {/*
          <TabsContent paddingX={0} value="tranche">
            <Box>
              <Phase2PlaceholderTab />
            </Box>
          </TabsContent>
          */}
          <TabsContent paddingX={0} value="blockBid">
            <VStack
              align="stretch"
              gap={3}
              minHeight={hidesAreaContent ? undefined : '600px'}
            >
              {/* 承認状態バナー (PENDING → 申請取消 AC-BSP-055 / REJECTED → 再編集
                  AC-BSP-056)。 design `spot.html` `#block-approval-status` の
                  `#tab-block` 直下配置に対応 (= 通常入札の ApprovalStatusBanner が
                  `#tab-create` 内なのと同じ扱い。 #6449 で揃えた)。
                  完了済みワークフロー (isReadonly、 AC-BSP-058) と 入札締切後
                  (AC-BSP-055c / AC-BSP-056c、 phase pre/open 以外) では
                  どちらの導線も出さない。
                  渡すのは並列 Get 済みの full entity (blockApprovalRequests) で、
                  一覧 (list) 由来の item は使わない (= 楽観ロック基準値 updatedAt を
                  正しく解決できる group だけをバナーに出す fail-closed)。 */}
              {/* 通常入札タブと同じく、 読み取り専用ではまとめて承認の入口を出さない。 */}
              {!isReadonly && (
                <BlockBulkApproval
                  decision={blockDecision}
                  isSpotVolumeUnresolved={isSpotStateUnresolved}
                  pendingGroups={countedPendingBlockApprovalRequests}
                  targetDate={targetDate}
                  volumeTotalRows={activeVolumeTotalRows}
                />
              )}
              <NeedsReviewBanner count={needsReviewCount} />
              <BlockApprovalStatusBanner
                decisionDisabledReason={blockDecision.decisionDisabledReason}
                // 導線の出し分けは通常入札の ApprovalStatusBanner と同条件
                // (on* prop は props-order lint でアルファベット順に並ぶため、
                // 条件はここにまとめる):
                //   - 入札締切後 (AC-BSP-067): 承認待ち・差戻しのバナーは
                //     resolveApprovalBannerHeading が先に除くので、 導線は締切後には
                //     描画されない
                //   - 申請取消 (AC-BSP-055) / 再編集 (AC-BSP-056): 完了済みワークフロー
                //     (AC-BSP-058) では出さない。 `!isBeforeDeadline` は保険
                //   - 承認 / 差戻し (AC-BSP-094): 完了済みワークフロー (AC-BSP-058) /
                //     明細未解決 の間は decisionDisabledReason で disabled + tooltip。
                //     入札締切 (AC-BSP-099) の成分が効くのは、 まとめて承認
                //     (useBlockBulkApproval) と、 モーダルを開いたまま締切を跨いだ時の
                //     確定時の再判定だけ (= 通常入札と同条件、 policy は
                //     useBlockApprovalDecision に 1 か所)
                groups={blockBannerRequests}
                onApproveRequest={blockDecision.openApprove}
                onCancelRequest={
                  isReadonly || !isBeforeDeadline
                    ? undefined
                    : (group) => {
                        setCancelingBlockGroupId(group.id)
                      }
                }
                onRejectRequest={blockDecision.openReject}
                onReopenRequest={
                  isReadonly || !isBeforeDeadline
                    ? undefined
                    : (group) => {
                        void handleBlockReopenRequest(group)
                      }
                }
                phase={phase}
                targetDate={targetDate}
              />
              {!hidesBidLists && (
                <BlockBidTab
                  blockBidRows={blockBidRows}
                  bulkWithdraw={bulkWithdraw.menu}
                  createDisabledReason={
                    // 承認フロー設定 (AC-BSP-015、 2026-09-16 ユーザー決定 1-B) を
                    // 明細未解決より先に判定する: どちらも「ブロック作成」 を
                    // 塞ぐが、 前者は resolveCreateApprovalMode を呼べないという
                    // より根本的な理由なので、 両方未解決でもこちらの文言を出す。
                    isApprovalFlowUnavailable
                      ? t(
                          'page:biddingBlock.serverError.approvalFlowUnavailable',
                        )
                      : isBlockStateUnresolved
                        ? t('page:biddingBlock.serverError.detailUnresolved')
                        : undefined
                  }
                  hasDraftGroup={draftBlockCount > 0}
                  headerActionSlot={renderBlockActionButton}
                  isReadonly={isReadonly || isBlockStateUnresolved}
                  isSidebarOpen={isSidebarOpen}
                  issuerScopeType={issuerScopeType}
                  listFilter={listFilter}
                  memberBidDisabledReason={memberBidDisabledReason}
                  onClearAreaFilter={() => handleAreaFilterChange(new Set())}
                  onDeleteGroup={(groupId) => {
                    setBlockWriteError(undefined)
                    setDeletingBlockGroupId(groupId)
                  }}
                  onEditGroup={handleBlockEdit}
                  onListFilterChange={setListFilter}
                  onOpenBlockCreateModal={() => {
                    setBlockWriteError(undefined)
                    setBlockModalSource(undefined)
                    setModalKind('block-create')
                  }}
                  // 行クリックはどの group でも開く (AC-BSP-001a「全行開く・下書き
                  // 以外は表示専用」)。 表示専用かどうかは modal 側へ `readOnly` で
                  // 渡す (= handleBlockEdit を onEditGroup と共用する通常入札と同形)。
                  onOpenClearAllModal={(targets) => {
                    const targetGroupIds = new Set(
                      targets.map((r) => r.groupId),
                    )
                    setBlockWriteError(undefined)
                    setBlockClearAllTargets(targets)
                    setBlockClearAllHiddenCount(
                      draftBlockCount - targetGroupIds.size,
                    )
                    setBlockClearAllUpdatedAt(
                      new Map(
                        blockApprovalRequests
                          .filter((r) => targetGroupIds.has(r.id))
                          .map((r) => [r.id, r.updatedAt]),
                      ),
                    )
                    setModalKind('block-clear-all')
                  }}
                  onOpenGroup={handleBlockEdit}
                  onReturnGroupToDraft={(groupId) => {
                    void handleBlockReturnToDraftRequest(groupId)
                  }}
                  onTabListFiltersChange={patchBlockTabListFilters}
                  onToggleSidebar={handleToggleSidebar}
                  onWithdrawGroup={(groupId) => {
                    setBlockWriteError(undefined)
                    setWithdrawingBlockGroupId(groupId)
                  }}
                  phase={phase}
                  rawSelectedAreas={rawSelectedAreas}
                  selectedAreas={selectedAreas}
                  tabListFilters={{
                    blockKind: listFilters.blockKind,
                    blockSubmit: listFilters.blockSubmit,
                  }}
                  targetDate={targetDate}
                  unknownIssuerUnselectedAreas={
                    rawSelectedAreas.size === 0 ? areaCodes : []
                  }
                />
              )}
            </VStack>
          </TabsContent>
          {/* 発注主体区分の空状態では隠さない (AC-BSP-131)。 業務エリアに
              JEPX 対象が無いときだけ出さない。 */}
          {!hasNoAreaCandidate && (
            <>
              <TabsContent paddingX={0} value="executionResult">
                <VStack align="stretch" gap={3} minHeight="400px">
                  <ExecutionResultTab
                    areaCodes={areaCodes}
                    bids={bids}
                    blockBidRows={blockBidRows}
                    isIncomplete={
                      isSpotBidsIncomplete || isBlockStateUnresolved
                    }
                    phase={phase}
                  />
                </VStack>
              </TabsContent>
              <TabsContent paddingX={0} value="operationHistory">
                <VStack align="stretch" gap={3} minHeight="400px">
                  <OperationHistoryTab
                    areaCodes={areaCodes}
                    deliveryDate={targetDateFilter}
                  />
                </VStack>
              </TabsContent>
            </>
          )}
        </TabsRoot>

        <IndividualBidModal
          areaCodes={areaCodes}
          defaultAreaCode={singleSelectedArea}
          defaultIssuerType={pageIssuerType}
          deliveryDate={targetDate}
          disabledAreas={disabledAreas}
          errorMessage={writeError}
          isBeforeDeadline={isBeforeDeadline}
          // 「下書きへ戻す手順」 の案内 (AC-BSP-066) を出せるのは締切前だけ。
          // 申請取消 / 再編集 / 取下 のいずれも入札締切前のみ実行可能なため。
          isOpen={
            modalKind === 'individual-create' &&
            (individualModalSource.mode === 'edit' ||
              memberBidDisabledReason === undefined)
          }
          isSubmitting={isCreating || isUpdating}
          // 下書き (DRAFT) 以外は編集できない (AC-066 / AC-082b: 編集できるのは
          // DRAFT request の札のみ)。 ワークフロー側が read-only の時も同様。
          isWorkflowReadonly={isReadonly}
          // 案内 (AC-BSP-066) にはワークフロー軸も渡す。 確定済ケース経由の入札は
          // 申請取消 / 再編集 / 取下 を backend が拒否し (AC-BSP-058) 画面も導線を
          // 出さないため、 案内だけ出すと実行できない手順を案内することになる。
          onClose={closeModal}
          onSubmit={(bid) => {
            if (
              individualModalSource.mode !== 'edit' &&
              memberBidDisabledReason !== undefined
            )
              return
            void handleSubmitBid(bid)
          }}
          // 売買逆転の判定相手。 一覧の警告アイコンと同じ母集合を渡す
          // (逆転はエリア × コマ単位の判定なので、 一覧側の「選択エリアで絞る」は
          // 結果に影響しない)。
          // 表示専用の条件 (spec AC-BSP-001a):
          //   1. 下書き以外の承認状態 (申請中・承認済・差戻し・提出済 等)
          //   2. **入札締切後** (GC 済 = phase が reserved/open 以外)。 締切後は下書きでも
          //      編集させない。 spec の元の判断「締切後に編集フォームを開かせない」 を
          //      「表示専用で開く」 へ改訂した際も、 **編集不可は維持**している
          //   3. ワークフロー側が read-only (完了済ケース等)
          readOnly={
            editingBid !== undefined &&
            (isReadonly ||
              !isBeforeDeadline ||
              editingBid.approvalState !== BiddingApprovalState.DRAFT)
          }
          siblingBids={inversionPoolBids}
          source={individualModalSource}
        />
        <WithdrawBidConfirmModal
          deadline={spotDeadlineAt(targetDate)}
          errorMessage={writeError}
          isSubmitting={isWithdrawing}
          now={now}
          onClose={() => {
            setWriteError(undefined)
            setWithdrawingBid(undefined)
          }}
          onConfirm={() => {
            void handleConfirmWithdraw()
          }}
          summaryLines={
            withdrawingBid !== undefined
              ? [formatSpotBidWithdrawSummary(withdrawingBid, t)]
              : undefined
          }
        />
        <DeleteBidConfirmModal
          errorMessage={writeError}
          isOpen={deletingBid !== undefined}
          isSubmitting={isUpdating || isDeleting}
          // 最後の 1 札の削除は Update ではなく Delete RPC を使うので、 isUpdating
          // だけだとその間ボタンが有効なままになる (= 同じ expectedUpdatedAt の
          // Delete が連打で複数飛ぶ)。
          onClose={() => {
            setWriteError(undefined)
            setDeletingBid(undefined)
          }}
          onConfirm={() => {
            void handleConfirmDelete()
          }}
          summary={
            deletingBid !== undefined
              ? formatSpotBidDeleteSummary(deletingBid, t)
              : undefined
          }
        />
        <BulkCreateModal
          areaOptions={bulkAreaOptions}
          defaultAreaCode={singleSelectedArea}
          defaultIssuerType={pageIssuerType}
          deliveryDate={targetDate}
          errorMessage={writeError}
          existingActiveBids={existingActiveBidVolumes}
          existingAddVolumes={bulkAddExistingVolumes}
          existingAddVolumesUnavailable={
            isSpotStateUnresolved || isBlockStateUnresolved
          }
          existingDraftBids={draftExistingBids}
          isOpen={
            modalKind === 'bulk-create' && memberBidDisabledReason === undefined
          }
          isSubmitting={isCreating || isUpdating}
          needByArea={needByArea}
          needUnavailable={bulkNeedUnavailable}
          onClose={closeModal}
          onSubmit={(params, bids) => {
            if (memberBidDisabledReason !== undefined) return
            void handleBulkCreate(params, bids)
          }}
        />
        <ClearAllBidsConfirmModal
          count={clearAllTargets.length}
          errorMessage={writeError}
          excludedNote={
            clearAllExcludedCount > 0
              ? t('page:biddingSpot.clearAll.excluded', {
                  count: clearAllExcludedCount,
                })
              : undefined
          }
          isOpen={modalKind === 'clear-all'}
          // 表示外の札を残す申請セットは Update RPC で更新する (isUpdating)。
          isSubmitting={isDeleting || isUpdating}
          onClose={closeModal}
          onDelete={() => {
            void handleClearAllBids()
          }}
        />
        {/* 承認申請確認 modal (DRAFT → PENDING)。
            承認ボタンクリック → この modal → 「申請する」で承認申請。
            申請前に、 ボタンを押した時点で一覧に表示中の下書きの件数 / 売買サマリ /
            エリア別内訳を確認させる (AC-082 / AC-BSP-007)。 申請セットごとの承認モードが
            混ざる時 (AC-BSP-015) もこの modal で、 題・本文・確定ボタン・内訳・違反の文言・
            「JEPX への送信」 を混在向けにする (design `spot.html` の #8804、 AC-BSP-076)。
            入札制限違反 (AC-008) / JEPX 階段数超過 (AC-021) がある間は同一 modal 内に
            違反明細をインライン表示して「申請する」を非活性にする (別ダイアログは
            開かず、入札一覧で該当行を修正すれば再活性化する)。 売買逆転チェック結果
            (AC-BSP-007) は警告のみでブロックしない (承認確認 modal と同じ宛先違いの
            同型の節、 useSpotApprovalDecision.warningSections 参照)。 表示中は入札締切
            までの残り時間を出す (AC-BSP-012)。
            数量は MW (既定単位、 AC-BSP-053 / issue #6266 の PdM 確定)。 以下の申請取消 /
            承認 / 提出の各 modal も同じ。
            承認申請・提出の確認 (本 modal と直接提出 / JEPX 提出、 ブロックも同じ) は、
            承認フロー・ JEPX への送信・取下の期限を説明文でなく確認項目の行で出す
            (#9586、 2026-10-05 デザイナー判断。 リード文は冒頭の 1 文だけ)。 */}
        <SubmitConfirmModal
          areaBreakdown={draftAreaBreakdown.map((row) => ({
            areaLabel: row.areaLabel,
            count: row.count,
            buyQuantity: row.buyMw,
            sellQuantity: row.sellMw,
          }))}
          bidCount={draftSubmitSummary.count}
          body={
            draftSplit === undefined
              ? t('page:biddingCommon.submitConfirm.requestDescription')
              : t('page:biddingSpot.approvalAction.mixedBody')
          }
          confirmItems={{
            market: 'spot',
            approvalModes: draftApprovalModes,
            send: draftSendTiming,
            reservedOpenAt: confirmReservedOpenAt,
          }}
          confirmLabel={
            draftSplit === undefined
              ? t('page:biddingSpot.approvalAction.requestConfirm')
              : t('page:biddingSpot.approvalAction.mixedConfirm', {
                  requestCount: draftSplit.request.count,
                  submitCount: draftSplit.submit.count,
                })
          }
          deadlineDisplay={
            draftDeadlineRemainingSeconds !== undefined
              ? formatSpotDeadlineDisplay(
                  draftDeadlineRemainingSeconds,
                  spotDeadlineAt(targetDate),
                )
              : undefined
          }
          duplicateCheck={spotSubmissionDuplicateCheck}
          excludedByFilterCount={submitScope?.hiddenCount}
          hasViolation={hasDraftViolation}
          isOpen={modalKind === 'request-confirm'}
          isSubmitting={isTransitioningDrafts}
          note={
            draftSplit === undefined ? undefined : (
              <DraftSubmitSplitNote split={draftSplit} />
            )
          }
          noteTitle={
            draftSplit === undefined
              ? undefined
              : t('page:biddingSpot.approvalAction.mixedSplitTitle')
          }
          onClose={closeModal}
          onConfirm={() => {
            void handleSubmitDrafts()
          }}
          title={
            draftSplit === undefined
              ? t('page:biddingSpot.approvalAction.requestModalTitle')
              : t('page:biddingSpot.approvalAction.mixedModalTitle')
          }
          totalBuyQuantity={draftSubmitSummary.buyMw}
          totalSellQuantity={draftSubmitSummary.sellMw}
          violationSections={draftViolationSections}
          warningSections={draftWarningSections}
        />
        {/* 直接提出確認 modal (DRAFT → SUBMITTED、 承認なしモード、 AC-BSP-015)。
            全 target が「承認なし」 / 「自動」 で揃っている時のみ主ボタンからこの modal を開く
            (renderMainActionButton)。 集計 / 違反チェックは承認申請確認 modal
            (request-confirm) と同じ母集合 (submitTargets) を使う。 */}
        <SubmitConfirmModal
          bidCount={draftSubmitSummary.count}
          confirmItems={{
            market: 'spot',
            approvalModes: draftApprovalModes,
            send: draftSendTiming,
            reservedOpenAt: confirmReservedOpenAt,
          }}
          duplicateCheck={spotSubmissionDuplicateCheck}
          excludedByFilterCount={submitScope?.hiddenCount}
          hasViolation={hasDraftViolation}
          isOpen={modalKind === 'direct-submit-confirm'}
          isSubmitting={isTransitioningDrafts}
          onClose={closeModal}
          onConfirm={() => {
            void handleSubmitDrafts()
          }}
          title={t('page:biddingSpot.approvalAction.directSubmitModalTitle')}
          totalBuyQuantity={draftSubmitSummary.buyMw}
          totalSellQuantity={draftSubmitSummary.sellMw}
          violationSections={draftViolationSections}
        />
        {/* 申請取消確認 modal (PENDING → DRAFT、 AC-055)。 対象 request の件数 /
            売買サマリを提示し、「申請取消」で CANCEL 遷移。 SubmitConfirmModal を
            title/body/confirmLabel 差し替えで流用 (request-confirm と同型)。 */}
        <SubmitConfirmModal
          bidCount={cancelingSummary.count}
          body={t('page:biddingSpot.approvalAction.cancelModalBody')}
          confirmLabel={t('page:biddingSpot.approvalAction.cancelConfirm')}
          hasViolation={false}
          isOpen={cancelingRequestId !== undefined}
          isSubmitting={isTransitioning}
          onClose={() => {
            setCancelingRequestId(undefined)
          }}
          onConfirm={() => {
            void handleCancelRequest()
          }}
          title={t('page:biddingSpot.approvalAction.cancelModalTitle')}
          totalBuyQuantity={cancelingSummary.buyMw}
          totalSellQuantity={cancelingSummary.sellMw}
        />
        {/* 承認確認 modal (PENDING → APPROVED、 AC-BSP-094 / AC-BSP-097)。 対象 request の
            件数 / 売買サマリ / エリア別内訳を提示し、「承認する」で APPROVE 遷移。
            SubmitConfirmModal を title/body/confirmLabel 差し替えで流用 (request-confirm と
            同型)。 入札制限を再検証して違反があれば承認をブロックする (AC-BSP-097。
            承認者は札を直せないので、 救済は「差戻し」 で申請者に返す)。 売買逆転は警告
            のみでブロックしない (spec §2)。 個々の札の確認はバナーの「対象の札を確認」→
            入札一覧で行う。 */}
        <SubmitConfirmModal
          areaBreakdown={spotDecision.areaBreakdown.map((row) => ({
            areaLabel: row.areaLabel,
            count: row.count,
            buyQuantity: row.buyMw,
            sellQuantity: row.sellMw,
          }))}
          bidCount={spotDecision.summary.count}
          body={t('page:biddingSpot.approvalAction.approveModalBody')}
          confirmLabel={t('page:biddingSpot.approvalAction.approveConfirm')}
          hasViolation={spotDecision.hasViolation}
          isOpen={spotDecision.approvingRequestId !== undefined}
          isSubmitting={spotDecision.isTransitioning}
          onClose={spotDecision.closeApprove}
          onConfirm={() => {
            void spotDecision.approve()
          }}
          title={t('page:biddingSpot.approvalAction.approveModalTitle')}
          totalBuyQuantity={spotDecision.summary.buyMw}
          totalSellQuantity={spotDecision.summary.sellMw}
          violationSections={spotDecision.violationSections}
          warningMessages={spotDecision.approveWarningMessages}
          warningSections={spotDecision.warningSections}
        />
        {/* 差戻し確認 dialog (PENDING → REJECTED、 AC-BSP-094)。 差戻し理由は必須
            (AC-BSP-063 / backend の非空検証)。 承認案件一覧画面と同じ共通部品。 */}
        <RejectDialog
          description={t('page:biddingSpot.approvalAction.rejectDescription')}
          isLoading={spotDecision.isTransitioning}
          isOpen={spotDecision.rejectingRequestId !== undefined}
          onClose={spotDecision.closeReject}
          onConfirm={(reason) => {
            void spotDecision.reject(reason)
          }}
          title={t('page:biddingSpot.approvalAction.rejectModalTitle')}
          warningMessages={spotDecision.rejectWarningMessages}
        />
        {/* ブロック作成/編集 modal (= block-create modal → Create/Update RPC、 issue #2090)。
            editingBlockGroupId が設定されていれば編集モード。 */}
        <BlockBidCreateModal
          areaCodes={areaCodes}
          defaultAreaCode={singleSelectedArea}
          defaultIssuerType={pageIssuerType}
          deliveryDate={targetDate}
          disabledAreas={disabledAreas}
          errorMessage={blockWriteError}
          initialGroup={blockModalSource}
          isDeleting={isBlockDeleting}
          isOpen={
            modalKind === 'block-create' &&
            (editingBlockGroupId !== undefined ||
              memberBidDisabledReason === undefined)
          }
          isSubmitting={isBlockCreating || isBlockUpdating}
          onClose={closeModal}
          onDelete={
            editingBlockGroupId !== undefined
              ? handleRequestBlockDeleteFromEdit
              : undefined
          }
          onSubmit={handleBlockModalSubmit}
          // 表示専用の条件 (spec AC-BSP-001a、 IndividualBidModal の readOnly と同形):
          //   1. 下書き以外の承認状態 (申請中・承認済・差戻し・提出済 等)、 または入札締切まで
          //      提出せず失効にした札 (承認ステータスは下書きに戻る、 AC-BSP-099)
          //   2. 入札締切後 (GC 済 = phase が reserved/open 以外)。 締切後は下書きでも編集させない
          //   3. ワークフロー側が read-only (完了済ケース等)
          //   4. 明細 (screen/list) が未解決 (= isBlockStateUnresolved)。 行クリックは
          //      list 由来の項目からも開けるが、 明細を確認できていない group に
          //      Update RPC を通すと事故になる (handleBlockCreate の同種ガードと同じ
          //      理由。 codex-review 指摘: fail-closed にする)。
          readOnly={
            blockModalSource !== undefined &&
            (editingBlockGroup === undefined ||
              isReadonly ||
              isBlockStateUnresolved ||
              !isBeforeDeadline ||
              !isNonExpiredDraftBlockGroup(editingBlockGroup))
          }
        />

        {/* ブロック全削除 確認 modal (= design `spot.html` `#block-clear-modal`、 issue #2091)。
            削除対象は DRAFT group のみなので件数も DRAFT group 数を出す。 通常入札の
            全削除 modal を文言差し替えで共用する (= design も同一パターン)。 */}
        <ClearAllBidsConfirmModal
          bodyKey="page:biddingBlock.clearAll.body"
          count={blockClearAllGroupIds.size}
          csvNote={t('page:biddingBlock.clearAll.csvNote')}
          errorMessage={blockWriteError}
          excludedNote={
            blockClearAllHiddenCount > 0
              ? t('page:biddingSpot.clearAll.excluded', {
                  count: blockClearAllHiddenCount,
                })
              : undefined
          }
          isOpen={modalKind === 'block-clear-all'}
          isSubmitting={isBlockDeleting}
          onClose={() => {
            setBlockWriteError(undefined)
            setModalKind('none')
          }}
          onDelete={() => {
            void handleBlockClearAll()
          }}
          title={t('page:biddingBlock.clearAll.title')}
        />

        {/* ブロック提出確認 modal (= 表示中の下書きの group → REQUEST 一括遷移)。 */}
        <SubmitConfirmModal
          bidCount={blockSubmitScope.length}
          body={t('page:biddingCommon.submitConfirm.requestDescription')}
          confirmItems={{
            market: 'spot',
            approvalModes: draftBlockApprovalModes,
            send: 'afterApproval',
            reservedOpenAt: confirmReservedOpenAt,
          }}
          confirmLabel={t('page:biddingBlock.approvalAction.requestConfirm')}
          duplicateCheck={blockSubmissionDuplicateCheck}
          excludedByFilterCount={blockSubmitHiddenCount}
          hasViolation={false}
          isOpen={modalKind === 'block-submit-confirm'}
          isSubmitting={isBlockTransitioning}
          onClose={closeModal}
          onConfirm={() => {
            void handleBlockSubmitRequest()
          }}
          title={t('page:biddingBlock.approvalAction.requestModalTitle')}
        />

        {/* ブロック直接提出確認 modal (= 表示中の下書きの group → SUBMIT 一括遷移、 承認なしモード、
            AC-BSP-015)。 対象の group の承認モードが「承認なし」 / 「自動」 で揃っている時のみ
            主ボタンからこの modal を開く (renderBlockActionButton)。 */}
        <SubmitConfirmModal
          bidCount={blockSubmitScope.length}
          confirmItems={{
            market: 'spot',
            approvalModes: draftBlockApprovalModes,
            send: 'onSubmit',
            reservedOpenAt: confirmReservedOpenAt,
          }}
          duplicateCheck={blockSubmissionDuplicateCheck}
          excludedByFilterCount={blockSubmitHiddenCount}
          hasViolation={false}
          isOpen={modalKind === 'block-direct-submit-confirm'}
          isSubmitting={isBlockTransitioning}
          onClose={closeModal}
          onConfirm={() => {
            void handleBlockSubmitDirect()
          }}
          title={t('page:biddingBlock.approvalAction.directSubmitModalTitle')}
        />

        {/* ブロック申請取消確認 modal (PENDING → DRAFT、 AC-BSP-055)。 対象 group の
            サマリを提示し、「申請取消」で CANCEL 遷移。 SubmitConfirmModal を
            title/body/confirmLabel 差し替えで流用 (通常入札の cancelingRequestId 用
            modal と同型)。 件数行は既定の「札件数」を使わず countLabel で単位を
            明示する: 取消単位は BlockBidGroup 1 件だが、 LINK/LOOP は leg (札) を
            2 本持つため「札件数 1 件」では対象を誤認する (code review 指摘)。 */}
        <SubmitConfirmModal
          bidCount={cancelingBlockLegCount}
          body={t('page:biddingBlock.approvalAction.cancelModalBody')}
          confirmLabel={t('page:biddingBlock.approvalAction.cancelConfirm')}
          countLabel={t(
            'page:biddingBlock.approvalAction.cancelTargetSummary',
            {
              count: cancelingBlockLegCount,
            },
          )}
          hasViolation={false}
          isOpen={cancelingBlockGroupId !== undefined}
          isSubmitting={isBlockTransitioning}
          onClose={() => {
            setCancelingBlockGroupId(undefined)
          }}
          onConfirm={() => {
            void handleCancelBlockRequest()
          }}
          title={t('page:biddingBlock.approvalAction.cancelModalTitle')}
        />

        {/* ブロック承認確認 modal (PENDING → APPROVED、 AC-BSP-094 / AC-BSP-097)。 件数行は
            取消 modal と同じく leg 数で単位を明示する (LINK/LOOP は 1 グループ 2 札)。
            入札制限を再検証して違反があれば承認をブロックし、 違反の内容を同一 modal 内に
            出す (承認者は札を直せないので、 救済は「差戻し」 で申請者に返す)。 */}
        <SubmitConfirmModal
          bidCount={blockDecision.legCount}
          body={t('page:biddingBlock.approvalAction.approveModalBody')}
          confirmLabel={t('page:biddingBlock.approvalAction.approveConfirm')}
          countLabel={t(
            'page:biddingBlock.approvalAction.approveTargetSummary',
            {
              count: blockDecision.legCount,
            },
          )}
          hasViolation={blockDecision.hasViolation}
          isOpen={blockDecision.approvingGroupId !== undefined}
          isSubmitting={blockDecision.isTransitioning}
          onClose={blockDecision.closeApprove}
          onConfirm={() => {
            void blockDecision.approve()
          }}
          title={t('page:biddingBlock.approvalAction.approveModalTitle')}
          violationSections={blockDecision.violationSections}
          warningMessages={blockDecision.approveWarningMessages}
        />

        {/* ブロック差戻し確認 dialog (PENDING → REJECTED、 AC-BSP-094)。 通常入札と同じ
            共通部品で、 差戻し理由は必須。 */}
        <RejectDialog
          description={t('page:biddingBlock.approvalAction.rejectDescription')}
          isLoading={blockDecision.isTransitioning}
          isOpen={blockDecision.rejectingGroupId !== undefined}
          onClose={blockDecision.closeReject}
          onConfirm={(reason) => {
            void blockDecision.reject(reason)
          }}
          title={t('page:biddingSpot.approvalAction.rejectModalTitle')}
          warningMessages={blockDecision.rejectWarningMessages}
        />

        {/* ブロック削除確認 modal (= group 単位、 一覧アクション / 編集 modal 双方から開く)。 */}
        <BlockDeleteConfirmModal
          errorMessage={blockWriteError}
          isOpen={deletingBlockGroupId !== undefined}
          isSubmitting={isBlockDeleting}
          onClose={() => {
            setBlockWriteError(undefined)
            setDeletingBlockGroupId(undefined)
          }}
          onConfirm={() => {
            if (deletingBlockGroupId !== undefined) {
              void handleBlockDelete(deletingBlockGroupId)
            }
          }}
          summaryLines={deletingBlockSummary}
        />

        {/* 入札中の札の一括取下の確認 (AC-BSP-116)。 通常入札・ブロック入札どちらのタブの ⋯ メニューからでも
            同じ modal で、 件数は通常入札 + ブロック入札の合算 (内訳を出す)。 */}
        <BulkWithdrawConfirmModal
          blockCount={bulkWithdraw.dialog.blockCount}
          deadline={spotDeadlineAt(targetDate)}
          isOpen={bulkWithdraw.dialog.isOpen}
          isSubmitting={bulkWithdraw.dialog.isSubmitting}
          now={now}
          onClose={bulkWithdraw.dialog.onClose}
          onConfirm={bulkWithdraw.dialog.onConfirm}
          spotCount={bulkWithdraw.dialog.spotCount}
        />

        {/* ブロック取下確認 modal (AC-BSP-057)。 通常入札と同じ部品で、 サマリだけ group の leg を並べる。 */}
        <WithdrawBidConfirmModal
          deadline={spotDeadlineAt(targetDate)}
          errorMessage={blockWriteError}
          isSubmitting={isBlockWithdrawing}
          now={now}
          onClose={() => {
            setBlockWriteError(undefined)
            setWithdrawingBlockGroupId(undefined)
          }}
          onConfirm={() => {
            void handleConfirmBlockWithdraw()
          }}
          summaryLines={withdrawingBlockSummary}
        />
      </Flex>
    </WorkflowPageLayout>
  )
}
